import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, SlidersHorizontal, RefreshCw, Trash2, GripVertical,
  FileSpreadsheet, Play, FileText, FileDown, Ban, FilterX,
  ChevronDown, ChevronRight, ChevronLeft, ChevronUp,
  Plus, Pencil, Save, Baby, X, Search, Check, Link2,
  GripHorizontal, List, LayoutGrid, Database, User,
  ArrowDownUp, Clock, Bug, Wrench, CheckCircle, XCircle, ExternalLink, Lock, ShieldAlert,
  Upload, Crop, Pipette, Eraser, Palette, Blend, ListFilter,
  AlertTriangle, Info, UserCheck, Mail, LogOut, Loader2
} from 'lucide-react';
import '../../STUI.css';
import StuiRichText, { StuiRichTextViewer } from '../../components/common/StuiRichText';
import ColorHarmonyModal from '../../components/common/colorpicker/ColorHarmonyModal';
import ColorPickerModal, { ColorPickerField } from '../../components/common/colorpicker/ColorPickerModal';
import LogoUploadModal from '../../components/common/imageupload/LogoUploadModal';
import PictureUploadModal from '../../components/common/imageupload/PictureUploadModal';
import StuiFrame from '../../components/common/StuiFrame';
import StuiDateTime from '../../components/common/StuiDateTime';
import { DynamicModuleTab, NotificationsTab } from './STUIGuideTabs';
import { formatDateTime } from '../../utils/dateTime';
import { usePreferences } from '../../context/PreferencesContext';
import { previewStyle } from '../../theme/themeRuntime';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import { buildSearchQuery, applyColumnFilters } from '../../utils/listSearch';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnFilterMenu from '../../components/common/columnfilter/ColumnFilterMenu';
import { API_BASE_URL, databasesApi } from '../../services/api';
import { activeDbSlug } from '../../services/activeDatabase';
import wmLogo from '../../assets/logos/WM-Logo-CB-White-Header.png';
import StuiSelect from '../../components/common/StuiSelect';
import SearchSelect from '../../components/common/SearchSelect';

/* ─────────────────────────────────────────────────────────────
   STUI Style Guide — Standard Template User Interface
   Demo elements use stui-* classes from STUI.css (imported above).
   App live classes (module-container, btn, etc.)
   live in App.css. STUI.css is isolated to this page only.
─────────────────────────────────────────────────────────────── */

/* ── CSS location registry ─────────────────────────────────── */
const CSS_LOCATIONS = {
  buttons:              { file: 'STUI.css', selector: '.stui-btn' },
  actionMenu:           { file: 'STUI.css', selector: '.stui-action-menu-btn' },
  select:               { file: 'STUI.css', selector: '.stui-select' },
  dataGrid:             { file: 'STUI.css', selector: '.stui-data-grid-container' },
  sectionLayout:        { file: 'STUI.css', selector: '.stui-section-card' },
  detailSectionHeader:  { file: 'STUI.css', selector: '.stui-detail-section-header' },
  pageHeader:           { file: 'STUI.css', selector: '.stui-page-header' },
  baseReset:            { file: 'STUI.css', selector: '*, *::before, *::after' },
  baseBody:             { file: 'STUI.css', selector: 'body' },
  baseFormControls:     { file: 'STUI.css', selector: 'input, select, textarea, button' },
  moduleHeader:         { file: 'STUI.css', selector: '.stui-module-header' },
  columnFilter:         { file: 'STUI.css', selector: '.stui-filter-dropdown' },
  stepFamily:           { file: 'STUI.css', selector: '.stui-step-panel' },
  accessColors:         { file: 'STUI.css', selector: '.stui-access-grant' },
  badge:                { file: 'STUI.css', selector: '.stui-badge-accepted' },
  tile:                 { file: 'STUI.css', selector: '.stui-tile' },
  statRow:              { file: 'STUI.css', selector: '.stui-stat-row' },
  label:                { file: 'STUI.css', selector: '.stui-label' },
  checkbox:             { file: 'STUI.css', selector: '.stui-checkbox' },
  subLabel:             { file: 'STUI.css', selector: '.stui-sublabel' },
  pill:                 { file: 'STUI.css', selector: '.stui-pill' },
  toggle:               { file: 'STUI.css', selector: '.stui-toggle-group' },
  settingRow:           { file: 'STUI.css', selector: '.stui-row-setting' },
  listLayout:           { file: 'STUI.css', selector: '.stui-list-view' },
  formFields:           { file: 'STUI.css', selector: '.stui-field-group' },
  searchSelect:         { file: 'STUI.css', selector: '.stui-searchselect-trigger' },
  relatedTables:        { file: 'STUI.css', selector: '.stui-related-section' },
  datasheetExtras:      { file: 'STUI.css', selector: '.stui-data-grid.delete-mode' },
  pagination:           { file: 'STUI.css', selector: '.stui-pagination' },
  fkSelect:             { file: 'STUI.css', selector: '.stui-fk-select-overlay' },
  composerSelect:       { file: 'STUI.css', selector: '.stui-composer-select' },
  moduleHeaderCrud:     { file: 'STUI.css', selector: '.stui-module-header-crud' },
  moduleStates:         { file: 'STUI.css', selector: '.stui-module-loading' },
  booleanDisplay:       { file: 'STUI.css', selector: '.stui-boolean-display' },
  breadcrumb:           { file: 'STUI.css', selector: '.stui-breadcrumb' },
  sortControls:         { file: 'STUI.css', selector: '.stui-btn-sort-state' },
  columnWidths:         { file: 'STUI.css', selector: '.stui-col-1' },
  spinning:             { file: 'STUI.css', selector: '.stui-spinning' },
  orgImages:            { file: 'STUI.css', selector: '.stui-org-img-table' },
  lightbox:             { file: 'STUI.css', selector: '.stui-lightbox-overlay' },
  cssSourceViewer:      { file: 'STUI.css', selector: '.stui-cssview' },
  iconBrowser:          { file: 'STUI.css', selector: '.stui-icon-grid' },
  checkboxInput:        { file: 'STUI.css', selector: '.stui-checkbox-input' },
  checkboxList:         { file: 'STUI.css', selector: '.stui-checkbox-list' },
  radioInput:           { file: 'STUI.css', selector: '.stui-radio-input' },
  radioCard:            { file: 'STUI.css', selector: '.stui-radio-card' },
  innerTable:           { file: 'STUI.css', selector: '.stui-inner-table' },
  codeBlock:            { file: 'STUI.css', selector: '.stui-code-block' },
  popover:              { file: 'STUI.css', selector: '.stui-popover' },
  statusDot:            { file: 'STUI.css', selector: '.stui-status-dot' },
  statusText:           { file: 'STUI.css', selector: '.stui-status-text' },
  segmented:            { file: 'STUI.css', selector: '.stui-segmented' },
  layoutUtilities:      { file: 'STUI.css', selector: '.stui-row-between' },
  colorPicker:          { file: 'STUI.css', selector: '.stui-cp-modal' },
  colorHarmony:         { file: 'STUI.css', selector: '.stui-ch-modal' },
  frame:                { file: 'STUI.css', selector: '.stui-frame' },
  dateTime:             { file: 'STUI.css', selector: '.stui-datetime' },
  searchBuilder:        { file: 'STUI.css', selector: '.stui-search-builder-row' },
  reportViewer:         { file: 'STUI.css', selector: '.stui-step-panel-no-overflow' },
  reportPreview:        { file: 'STUI.css', selector: '.stui-rpt-preview-container' },
  authGate:             { file: 'STUI.css', selector: '.stui-auth-gate-card' },
};

/* ── Icon map for button configurator ─────────────────────── */
const BUTTON_ICONS = { Download, Play, RefreshCw, Plus, Trash2, X };

/* ── CSS location index ────────────────────────── */

/* Sections name the SELECTOR they document; the line number is derived from STUI.css at load.
   A stored line number is wrong the moment anyone edits the file above it, so nothing here is
   hand-maintained. CssChip shows the selector instead when a lookup finds nothing — a visible
   miss beats a confident wrong number. */

const CssIndexContext = React.createContext(null);

const buildCssIndex = (content) => {
  const lines = content.split('\n').map((l) => l.replace(/\r$/, ''));
  // Comment bodies are blanked for scanning only, so a selector named in prose cannot be
  // indexed ahead of its real rule; `lines` keeps the original text for the viewer.
  const scan = content
    .replace(/\/\*[\s\S]*?\*\//g, (m) => m.replace(/[^\n]/g, ' '))
    .split('\n');
  const index = new Map();
  scan.forEach((raw, i) => {
    const brace = raw.indexOf('{');
    if (brace < 0) return;
    let head = raw.slice(0, brace).trim();
    if (!head || head.startsWith('@')) return;
    // A selector list may span lines; walk back over the preceding comma-terminated ones.
    let start = i;
    while (start > 0 && scan[start - 1].trimEnd().endsWith(',')) {
      head = `${scan[start - 1].trim()} ${head}`;
      start -= 1;
    }
    head.split(',').forEach((sel) => {
      const key = sel.trim().replace(/\s+/g, ' ');
      if (key && !index.has(key)) index.set(key, start + 1);
    });
  });
  return { index, lines };
};

const resolveCssLine = (index, selector) => {
  if (!index || !selector) return null;
  const key = selector.trim().replace(/\s+/g, ' ');
  if (index.has(key)) return index.get(key);
  // Groups written one selector per line (`*, *::before, *::after`) resolve on the first.
  const first = key.split(',')[0].trim();
  if (index.has(first)) return index.get(first);
  // Otherwise the first rule whose selector list contains this one.
  for (const [k, line] of index) if (k.includes(key)) return line;
  return null;
};

/* ── Shared sub-components ─────────────────────────────────── */

// Used By, Text Inventory and the CSS chip's source view read the frontend source on disk, which
// exists only on dev VMs; elsewhere they say so instead of loading forever or linking nowhere.
const isDevVm = () => !!window.__config__?.IS_DEV_VM;
const DEV_VM_ONLY_MSG = 'Unavailable here: built from local source files that exist only on dev VMs.';

const CssChip = ({ cssFile, cssSelector, onClick }) => {
  const css = useContext(CssIndexContext);
  if (!cssFile || !cssSelector) return null;
  // Off a dev VM the CSS source cannot be opened, so the chip is a label, not a link
  if (!isDevVm()) return <span className="stui-css-chip static">{cssFile} → {cssSelector}</span>;
  const line = resolveCssLine(css?.index, cssSelector);
  return (
    <button className="stui-css-chip" onClick={() => onClick?.(line)}>
      {line ? `${cssFile} : L${line} ↗` : `${cssFile} → ${cssSelector}`}
    </button>
  );
};

/* A window of source around the rule rather than all 7k lines - enough context to read the
   rule without putting thousands of flex rows in the DOM on every open. */
const CSSVIEW_SPAN = 120;

const CssSourceModal = ({ file, selector, line, lines, onClose }) => {
  const targetRef = useRef(null);

  useEffect(() => { targetRef.current?.scrollIntoView({ block: 'center' }); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const first = Math.max(1, line - CSSVIEW_SPAN);
  const last  = Math.min(lines.length, line + CSSVIEW_SPAN);

  return (
    <div className="stui-modal-overlay" onClick={onClose}>
      <div className="stui-modal stui-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="stui-modal-header">
          <h3 className="stui-modal-title">{file} : L{line} → {selector}</h3>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="stui-cssview">
          {lines.slice(first - 1, last).map((text, i) => {
            const n = first + i;
            const isTarget = n === line;
            return (
              <div
                key={n}
                ref={isTarget ? targetRef : null}
                className={`stui-cssview-row${isTarget ? ' target' : ''}`}
              >
                <span className="stui-cssview-num">{n}</span>
                <span className="stui-cssview-code">{text || ' '}</span>
              </div>
            );
          })}
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, description, cssFile, cssSelector, onChipClick, noBodyScroll, children }) => (
  <div className="stui-ssg-section">
    <div className="stui-ssg-section-header">
      <div className="stui-detail-section-header">{title}</div>
      <CssChip cssFile={cssFile} cssSelector={cssSelector} onClick={(line) => onChipClick?.(cssFile, cssSelector, line)} />
    </div>
    {description && <p className="stui-ssg-section-desc">{description}</p>}
    <div className="stui-ssg-section-body">
      {children}
    </div>
  </div>
);

const Subsection = ({ label }) => (
  <div className="stui-detail-section-header stui-ssg-subsection">{label}</div>
);

const Example = ({ label, children }) => (
  <div className="stui-example-wrapper">
    <div className="stui-example-cell">{children}</div>
    {label && <span className="stui-example-label">{label}</span>}
  </div>
);

const CSSRef = ({ file, selector }) => (
  <code className="stui-ssg-cssref">
    {file} → {selector}
  </code>
);

const RefTable = ({ headers, rows }) => (
  <table className="stui-ssg-reftable">
    <thead>
      <tr>
        {headers.map(h => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, i) => (
        <tr key={i}>
          {row.map((cell, j) => (
            <td key={j}>{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const UsedBy = ({ cssClass, usageMap, pageSlugMap, navigate }) => {
  if (!isDevVm()) return <span className="stui-text-italic">{DEV_VM_ONLY_MSG}</span>;
  const pages = usageMap ? (usageMap[cssClass] || []) : null;
  if (pages === null) return <span className="stui-text-italic">Loading…</span>;
  if (pages.length === 0) return <span className="stui-text-italic">No pages reference .{cssClass}</span>;
  return (
    <div className="stui-ssg-usedby">
      {pages.map(page => {
        const info = pageSlugMap?.[page];
        const slug = info?.slug;
        // A db-scoped page needs a known database; never guess one
        const dbSlug = info?.dbScoped ? activeDbSlug() : null;
        const fullPath = slug && (!info.dbScoped || dbSlug)
          ? '/' + (info.dbScoped ? dbSlug + '/' : '') + slug
          : null;
        const why = !slug ? 'No App.js route renders this page' : !fullPath ? 'Select a database first' : undefined;
        return (
          <button key={page} onClick={() => fullPath && navigate(fullPath)} title={why} className={`stui-ssg-usedby-btn ${fullPath ? 'linked' : 'unlinked'}`}>
            {page}
          </button>
        );
      })}
    </div>
  );
};

const TextInventory = ({ items, emptyMsg }) => {
  if (!isDevVm()) return <span className="stui-text-italic">{DEV_VM_ONLY_MSG}</span>;
  if (!items) return <span className="stui-text-italic">Loading…</span>;
  const unique = [...new Set(items)].filter(t => t && t.trim().length > 0);
  if (!unique.length) return <span className="stui-text-italic">{emptyMsg || 'No text found'}</span>;
  return (
    <div className="stui-ssg-textinv">
      {unique.map((text, i) => (
        <span key={i} className="stui-ssg-textinv-item">
          "{text}"
        </span>
      ))}
    </div>
  );
};

const ConfigOutput = ({ text }) => (
  <div className="stui-config-output">{text}</div>
);

/* ── Configurators ─────────────────────────────────────────── */

const SegmentedPreview = () => {
  const [two, setTwo]     = useState('CIM');
  const [three, setThree] = useState('B');
  return (
    <div className="stui-col-8" style={{ maxWidth: 420 }}>
      <div className="stui-field-group" style={{ marginBottom: 'var(--spacing-md)' }}>
        <label>SCADA System</label>
        <div className="stui-segmented">
          {[['CIM', 'Cimplicity'], ['IGN', 'Ignition']].map(([v, l]) => (
            <button key={v} className={`stui-segmented-btn${two === v ? ' selected' : ''}`} onClick={() => setTwo(v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="stui-field-group">
        <label>Three options, one disabled</label>
        <div className="stui-segmented">
          {[['A', 'Database'], ['B', 'CSV Upload'], ['C', 'Manual']].map(([v, l]) => (
            <button key={v} className={`stui-segmented-btn${three === v ? ' selected' : ''}`} onClick={() => setThree(v)} disabled={v === 'C'}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

const PopoverPreview = () => {
  const [open, setOpen] = useState(true);
  return (
    <div className="stui-col-8" style={{ minHeight: 190 }}>
      <div className="stui-popover-anchor" style={{ display: 'inline-block', marginLeft: 200 }}>
        <button className="stui-btn stui-btn-secondary" onClick={() => setOpen(o => !o)}>Filter by date</button>
        {open && (
          <div className="stui-popover">
            <div className="stui-field-group"><label>From</label><input type="date" className="stui-field-input" /></div>
            <div className="stui-field-group"><label>To</label><input type="date" className="stui-field-input" /></div>
            <div className="stui-popover-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setOpen(false)}>Clear</button>
              <button className="stui-btn stui-btn-primary" onClick={() => setOpen(false)}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ButtonConfigurator = () => {
  const [variant, setVariant] = useState('primary');
  const [icon, setIcon] = useState('none');
  const [label, setLabel] = useState('Button');
  const [disabled, setDisabled] = useState(false);
  const [iconOnly, setIconOnly] = useState(false);
  const IconComp = BUTTON_ICONS[icon] || null;
  const showIconOnly = iconOnly && IconComp;
  const output = [`stui-btn-${variant}`, showIconOnly ? 'stui-btn-icon' : null, icon !== 'none' ? `${icon} icon` : 'no icon', showIconOnly ? 'no label' : `label "${label || 'Button'}"`, disabled ? 'disabled' : 'enabled'].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {[
          { key: 'Variant', items: ['primary', 'secondary', 'danger', 'danger-subtle'], val: variant, set: setVariant, name: 'btn-v' },
          { key: 'Icon',    items: ['none', 'Download', 'Play', 'RefreshCw', 'Plus', 'Trash2', 'X'], val: icon, set: setIcon, name: 'btn-i' },
        ].map(({ key, items, val, set, name }) => (
          <div key={key} className="stui-row-10-wrap">
            <span className="stui-cfg-key">{key}</span>
            {items.map(v => (
              <label key={v} className="stui-cfg-label">
                <input type="radio" className="stui-radio-input" name={name} checked={val === v} onChange={() => set(v)} /> {v}
              </label>
            ))}
          </div>
        ))}
        <div className="stui-row-10">
          <span className="stui-cfg-key">Label</span>
          <input value={label} onChange={e => setLabel(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key">State</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)} /> Disabled
          </label>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={iconOnly} onChange={e => setIconOnly(e.target.checked)} /> Icon only
          </label>
        </div>
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'center' }}>
        <button className={`stui-btn stui-btn-${variant}${showIconOnly ? ' stui-btn-icon' : ''}`} disabled={disabled} title={showIconOnly ? (label || 'Button') : undefined}>
          {IconComp && <IconComp size={showIconOnly ? 16 : 14} />}{!showIconOnly && (label || 'Button')}
        </button>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ActionMenuConfigurator = () => {
  const [variant, setVariant] = useState('generic');
  const [open, setOpen] = useState(false);
  const [generic, setGeneric] = useState({ refresh: true, download: true, modifyView: true, delete: false });
  const [report, setReport] = useState({ pdf: true, excel: true, csv: true, separateFiles: false, cancelExport: false, clearFilters: false });
  const toggle = (setter, key) => setter(prev => ({ ...prev, [key]: !prev[key] }));
  const genericItems = ['Refresh', 'Export Excel', 'Modify View'].filter((_, i) => [generic.refresh, generic.download, generic.modifyView][i]);
  const reportItems = [report.pdf && 'Download PDF', report.excel && 'Export Excel', report.csv && 'Export CSV', report.separateFiles && 'Separate Files toggle', report.cancelExport && 'Cancel Export (danger)', report.clearFilters && 'Clear Filters & Sort'].filter(Boolean);
  const output = variant === 'generic'
    ? ['stui-action-menu', 'generic', genericItems.length ? 'items: ' + genericItems.join(', ') : 'no items', generic.delete ? 'danger: Delete' : 'no danger item'].join(' · ')
    : ['stui-action-menu', 'report', reportItems.length ? 'items: ' + reportItems.join(', ') : 'no items'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Variant</span>
        {['generic', 'report'].map(v => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" className="stui-radio-input" name="am-v" checked={variant === v} onChange={() => setVariant(v)} /> {v}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {variant === 'generic'
          ? [['refresh','Refresh'],['download','Export Excel'],['modifyView','Modify View'],['delete','Delete (danger)']].map(([k,l]) => (
              <label key={k} className="stui-cfg-label">
                <input type="checkbox" checked={generic[k]} onChange={() => toggle(setGeneric, k)} /> {l}
              </label>
            ))
          : [['pdf','PDF'],['excel','Excel'],['csv','CSV'],['separateFiles','Separate Files'],['cancelExport','Cancel Export'],['clearFilters','Clear Filters & Sort']].map(([k,l]) => (
              <label key={k} className="stui-cfg-label">
                <input type="checkbox" checked={report[k]} onChange={() => toggle(setReport, k)} /> {l}
              </label>
            ))
        }
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }} className="stui-example-cell">
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {open && (
            <div className="stui-action-menu-dropdown">
              {variant === 'generic' ? (
                <>
                  {generic.refresh    && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><RefreshCw size={16} /> Refresh</button>}
                  {generic.download   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><Download size={16} /> Export Excel</button>}
                  {generic.modifyView && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><SlidersHorizontal size={16} /> Modify View</button>}
                  {generic.delete && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Trash2 size={16} /> Delete</button></>}
                </>
              ) : (
                <>
                  {report.separateFiles && <label className="stui-action-menu-item" style={{ cursor:'pointer' }}><input type="checkbox" style={{ marginRight:4 }} /><span>Separate Files</span></label>}
                  {(report.pdf || report.excel || report.csv) && <div className="stui-action-menu-divider" />}
                  {report.pdf   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileText size={16} /> Download PDF</button>}
                  {report.excel && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileSpreadsheet size={16} /> Export Excel</button>}
                  {report.csv   && <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileDown size={16} /> Export CSV</button>}
                  {report.cancelExport && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Ban size={16} /> Cancel Export</button></>}
                  {report.clearFilters && <><div className="stui-action-menu-divider" /><button className="stui-action-menu-item" onClick={() => setOpen(false)}><FilterX size={16} /> Clear Filters &amp; Sort</button></>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SelectConfigurator = () => {
  const [disabled, setDisabled] = useState(false);
  const [placeholder, setPlaceholder] = useState('-- None --');
  const [optionList, setOptionList] = useState('Panel, I/O Type, Drawing');
  const options = optionList.split(',').map(o => o.trim()).filter(Boolean);
  const output = ['stui-select', `placeholder "${placeholder}"`, `${options.length} option${options.length !== 1 ? 's' : ''}`, disabled ? 'disabled' : 'enabled'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">Placeholder</span>
          <input value={placeholder} onChange={e => setPlaceholder(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">Options</span>
          <input value={optionList} onChange={e => setOptionList(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} placeholder="comma-separated" />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-lg">State</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)} /> Disabled
          </label>
        </div>
      </div>
      <div className="stui-example-cell">
        <StuiSelect className="stui-select" disabled={disabled} defaultValue="">
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </StuiSelect>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SectionPanelConfigurator = () => {
  const [number, setNumber] = useState(1);
  const [title, setTitle] = useState('Report Configuration');
  const [hasDesc, setHasDesc] = useState(true);
  const [desc, setDesc] = useState('Select filter values to narrow report results.');
  const [collapsed, setCollapsed] = useState(false);
  const [hasBtn, setHasBtn] = useState(false);
  const output = ['stui-step-panel', `number ${number}`, `title "${title}"`, hasDesc ? `desc "${desc.slice(0,30)}…"` : 'no description', collapsed ? 'collapsed' : 'expanded', hasBtn ? 'Run Report button' : 'no action button'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {[
          ['Number', <input type="number" min="1" max="9" value={number} onChange={e => setNumber(+e.target.value)} className="stui-cfg-input-sm" />],
          ['Title',  <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} />],
        ].map(([k, ctrl]) => (
          <div key={k} className="stui-row-10">
            <span className="stui-cfg-key-md">{k}</span>
            {ctrl}
          </div>
        ))}
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Desc</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: 'var(--color-gray-600)', marginRight: 8 }}>
            <input type="checkbox" checked={hasDesc} onChange={e => setHasDesc(e.target.checked)} /> show
          </label>
          {hasDesc && <input value={desc} onChange={e => setDesc(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />}
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span className="stui-cfg-key-md">Options</span>
          {[['collapsed','Collapsed',collapsed,setCollapsed],['hasBtn','Action Button',hasBtn,setHasBtn]].map(([k,l,v,s]) => (
            <label key={k} className="stui-cfg-label">
              <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} /> {l}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="stui-step-panel">
          <div className="stui-step-header">
            <span className="stui-step-number">{number}</span>
            <h3>{title || 'Panel Title'}</h3>
            {hasBtn && <button className="stui-btn stui-btn-primary stui-ml-auto-mr"><Play size={14} /> Run Report</button>}
            <span style={{ marginLeft: hasBtn ? 0 : 'auto', color: 'var(--color-gray-500)' }}>
              {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {!collapsed && hasDesc && <p className="stui-step-desc">{desc}</p>}
          {!collapsed && <div style={{ padding: '8px 0', color: 'var(--color-gray-500)', fontSize: 11, fontStyle: 'italic' }}>…body content…</div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const DataGridConfigurator = () => {
  const [clickable, setClickable]     = useState(true);
  const [noResize, setNoResize]       = useState(true);
  const [colCount, setColCount]       = useState(3);
  const [rowCount, setRowCount]       = useState(4);
  const [withFilter, setWithFilter]   = useState(false);
  // "With filter" renders the REAL FilterableDataGrid — its header menus are the Column Filter.
  const [filters, setFilters] = useState({});

  const colKeys = Array.from({ length: colCount }, (_, i) => `col${i}`);
  const cols    = Array.from({ length: colCount }, (_, i) => ({ key: `col${i}`, label: `Col ${i + 1}` }));
  const rows    = Array.from({ length: rowCount }, (_, i) =>
    ({ id: i + 1, ...Object.fromEntries(cols.map((c, j) => [c.key, `R${i+1}C${j+1}`])) })
  );

  const output = [`stui-data-grid`, `${colCount} col${colCount !== 1 ? 's' : ''}`, `${rowCount} row${rowCount !== 1 ? 's' : ''}`, clickable ? 'clickable' : 'not clickable', noResize ? 'no-resize' : 'resizable', withFilter ? 'FilterableDataGrid + Column Filter' : 'plain table'].join(' · ');

  return (
    <div className="stui-col-12">
      <div className="stui-row-12-wrap">
        {[['Columns', colCount, setColCount, 1, 6], ['Rows', rowCount, setRowCount, 2, 8]].map(([lbl, val, set, mn, mx]) => (
          <div key={lbl} className="stui-row-8">
            <span className="stui-label-xs">{lbl}</span>
            <input type="number" min={mn} max={mx} value={val} onChange={e => set(Math.min(mx, Math.max(mn, +e.target.value)))} className="stui-cfg-input-sm" />
          </div>
        ))}
        {[['clickable','Clickable rows',clickable,setClickable],['noResize','No resize',noResize,setNoResize],['withFilter','With filter',withFilter,setWithFilter]].map(([k,l,v,s]) => (
          <label key={k} className="stui-cfg-label">
            <input type="checkbox" checked={v} onChange={e => s(e.target.checked)} /> {l}
          </label>
        ))}
        {withFilter && <span className="stui-hint">Click a column header — the menu is the Column Filter element</span>}
      </div>

      {withFilter ? (
        <FilterableDataGrid
          data={applyColumnFilters(rows, filters)}
          allData={rows}
          columns={cols}
          filters={filters}
          setFilters={setFilters}
          onRowClick={clickable ? () => {} : undefined}
        />
      ) : (
        <div className="stui-data-grid-container" style={{ maxHeight: 180 }}>
          <div className="stui-data-grid-scroll-area">
            <table className={`stui-data-grid${noResize ? ' stui-no-column-resize' : ''} stui-w-full`}>
              <thead>
                <tr>{cols.map(c => <th key={c.key}><span>{c.label}</span></th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className={clickable ? 'stui-clickable' : ''}>
                    {colKeys.map(k => <td key={k}>{row[k]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfigOutput text={output} />
    </div>
  );
};

// Sample values per column type for the Column Filter previews.
const COLUMN_FILTER_SAMPLES = {
  text:   ['Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo', 'Foxtrot', ''],
  number: ['1', '5', '12', '40', '250', ''],
  date:   ['12/31/2025', '08/18/2026', '08/30/2026', '09/02/2026', '10/14/2026', ''],
};

const COLUMN_FILTER_VARIANTS = [
  { id: 'text',     label: 'Text',             type: 'text',   textOnly: false },
  { id: 'number',   label: 'Number',           type: 'number', textOnly: false },
  { id: 'date',     label: 'Date',             type: 'date',   textOnly: false },
  { id: 'textOnly', label: "Conditions only (filterable: 'text')", type: 'text', textOnly: true },
  { id: 'none',     label: 'Sort only (filterable: false)', type: 'none', textOnly: false },
];

// One live Column Filter. inline = rendered in the page flow; outside clicks do not close it.
const LiveColumnFilter = ({ variant, onChange }) => {
  const [filter, setFilter] = useState([]);
  const [sort, setSort] = useState({ key: null, direction: null });
  const col = { key: `demo-${variant.id}`, label: variant.label.split(' (')[0] };
  return (
    <ColumnFilterMenu
      inline
      column={col}
      type={variant.type}
      textOnly={variant.textOnly}
      filter={filter}
      options={COLUMN_FILTER_SAMPLES[variant.type] || []}
      sortConfig={sort}
      onSort={dir => setSort({ key: col.key, direction: dir })}
      onApply={f => { setFilter(f); onChange?.(f); }}
      onClose={() => {}}
    />
  );
};

const ColumnFilterConfigurator = () => {
  const [variantId, setVariantId] = useState('date');
  const [applied, setApplied] = useState([]);
  const variant = COLUMN_FILTER_VARIANTS.find(v => v.id === variantId);
  const typeProp = variant.type === 'none' ? 'filterable={false}' : variant.textOnly ? "filterable=\"text\"" : `filterType="${variant.type}"`;
  const output = `column { ${typeProp} } · filter: ${Array.isArray(applied) && !applied.length ? '(none)' : JSON.stringify(applied)}`;
  return (
    <div className="stui-col-12">
      <div className="stui-row-10-wrap">
        <span className="stui-cfg-key">Column</span>
        {COLUMN_FILTER_VARIANTS.map(v => (
          <label key={v.id} className="stui-cfg-label">
            <input type="radio" className="stui-radio-input" name="cf-variant" checked={variantId === v.id} onChange={() => { setVariantId(v.id); setApplied([]); }} /> {v.label}
          </label>
        ))}
      </div>
      <div className="stui-example-cell">
        <LiveColumnFilter key={variantId} variant={variant} onChange={setApplied} />
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const SectionLayoutConfigurator = () => {
  const [hasChip, setHasChip] = useState(true);
  const [hasDesc, setHasDesc] = useState(true);
  const [subsections, setSubsections] = useState(2);
  const [chipFile, setChipFile] = useState('STUI.css');
  const [chipLine, setChipLine] = useState(724);
  const output = ['stui-section-card', hasDesc ? 'with description' : 'no description', hasChip ? `${chipFile} : L${chipLine} chip` : 'no chip', `${subsections} subsection${subsections !== 1 ? 's' : ''}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-12-wrap">
        <label className="stui-cfg-label">
          <input type="checkbox" checked={hasChip} onChange={e => setHasChip(e.target.checked)} /> CSS chip
        </label>
        {hasChip && (
          <>
            <StuiSelect className="stui-select" value={chipFile} onChange={e => setChipFile(e.target.value)}>
              <option value="STUI.css">STUI.css</option>
              <option value="App.css">App.css</option>
            </StuiSelect>
            <input type="number" value={chipLine} onChange={e => setChipLine(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 64 }} placeholder="line" />
          </>
        )}
        <label className="stui-cfg-label">
          <input type="checkbox" checked={hasDesc} onChange={e => setHasDesc(e.target.checked)} /> description
        </label>
        <div className="stui-row-8">
          <span className="stui-label-xs">Subsections</span>
          <input type="number" min="0" max="6" value={subsections} onChange={e => setSubsections(+e.target.value)} className="stui-cfg-input-sm" />
        </div>
      </div>
      <div className="stui-section-card" style={{ maxHeight: 200, overflow: 'hidden' }}>
        <div className="stui-section-header">
          <div className="stui-section-header-left">
            <h2>Section Title</h2>
            {hasDesc && <p>Optional description of what this section documents.</p>}
          </div>
          {hasChip && <span className="stui-css-chip" style={{ pointerEvents: 'none' }}>{chipFile} : L{chipLine} ↗</span>}
        </div>
        <div style={{ padding: '12px 16px', overflowY: 'auto' }}>
          {Array.from({ length: subsections }, (_, i) => (
            <div key={i} className="stui-subsection-divider">Subsection {i + 1}</div>
          ))}
          {subsections === 0 && <span className="stui-text-italic">No subsections</span>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ReportHeaderConfigurator = () => {
  const [icon, setIcon] = useState('FileSpreadsheet');
  const [title, setTitle] = useState('I/O Assignments');
  const [desc, setDesc] = useState('Tag IO assignment details by panel, drop, slot, channel');
  const [hasExport, setHasExport] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const IconMap = { FileSpreadsheet, FileText, FileDown };
  const IconComp = IconMap[icon] || FileSpreadsheet;
  const output = [`stui-page-header`, `${icon} icon`, `title "${title}"`, desc ? `desc "${desc.slice(0, 28)}…"` : 'no description', hasExport ? 'with export action menu' : 'no export menu'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Icon</span>
          {['FileSpreadsheet','FileText','FileDown'].map(v => (
            <label key={v} className="stui-cfg-label">
              <input type="radio" className="stui-radio-input" name="rh-icon" checked={icon === v} onChange={() => setIcon(v)} /> {v}
            </label>
          ))}
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Title</span>
          <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Desc</span>
          <input value={desc} onChange={e => setDesc(e.target.value)} className="stui-cfg-input" style={{ width: 260 }} />
        </div>
        <div className="stui-row-10">
          <span className="stui-cfg-key-md">Options</span>
          <label className="stui-cfg-label">
            <input type="checkbox" checked={hasExport} onChange={e => setHasExport(e.target.checked)} /> Export action menu
          </label>
        </div>
      </div>
      <div style={{ border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div className="stui-page-header">
          <div className="stui-row-md">
            <h1 className="stui-page-title">
              <IconComp size={20} />{title || 'Report Title'}
            </h1>
            {desc && <span className="stui-text-sm-muted">{desc}</span>}
          </div>
          {hasExport && (
            <div className="stui-action-menu">
              <button className="stui-action-menu-btn" onClick={() => setExportOpen(o => !o)}><GripVertical size={16} /></button>
              {exportOpen && (
                <div className="stui-action-menu-dropdown">
                  <button className="stui-action-menu-item" onClick={() => setExportOpen(false)}><FileText size={16} /> Download PDF</button>
                  <button className="stui-action-menu-item" onClick={() => setExportOpen(false)}><FileSpreadsheet size={16} /> Export Excel</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Helpers shared across new configurators ───────────────── */
const cfgRow = (label, control, width = 80) => (
  <div className="stui-row-10">
    <span style={{ fontSize: 11, color: 'var(--color-gray-500)', width, flexShrink: 0 }}>{label}</span>
    {control}
  </div>
);
const cfgRadios = (name, items, val, set) => items.map(v => (
  <label key={v} className="stui-cfg-label">
    <input type="radio" className="stui-radio-input" name={name} checked={val === v} onChange={() => set(v)} /> {v}
  </label>
));
const cfgTextInput = (val, set, width = 180) => (
  <input value={val} onChange={e => set(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', width, color: 'var(--color-gray-700)' }} />
);
const cfgCheck = (val, set, label) => (
  <label className="stui-cfg-label">
    <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} /> {label}
  </label>
);

/* ── Sort Controls Configurator ─────────────────────────────── */
const SortControlsConfigurator = () => {
  const [col, setCol]   = useState('Tag Name');
  const [dir, setDir]   = useState('asc');
  const output = `stui-btn-sort-state · col "${col}" · dir ${dir}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Column', cfgTextInput(col, setCol, 160))}
        {cfgRow('Direction', <div className="stui-flex-10">{cfgRadios('sc-dir', ['asc','desc'], dir, setDir)}</div>)}
      </div>
      <div className="stui-example-cell" style={{ gap: 10 }}>
        <span className="stui-btn-sort-state">{dir === 'asc' ? '↑' : '↓'} {col || 'Column'}</span>
        <button className="stui-btn-sort-reset">✕ Clear Sort</button>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Column Widths Configurator ─────────────────────────────── */
const ColumnWidthsConfigurator = () => {
  const [span, setSpan] = useState(12);
  const output = `stui-col-${span} · span ${span}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Span (1–24)', <input type="number" min={1} max={24} value={span} onChange={e => setSpan(Math.min(24, Math.max(1, +e.target.value)))} className="stui-cfg-input-sm" style={{ width: 64 }} />)}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, width: '100%' }}>
        <div style={{ gridColumn: `span ${span}`, background: 'var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '6px 0', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 600 }}>{span}</div>
        {span < 24 && <div style={{ gridColumn: `span ${24 - span}`, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', padding: '6px 0', textAlign: 'center', fontSize: 11, color: 'var(--color-gray-500)' }}>{24 - span}</div>}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Module Header Configurator ─────────────────────────────── */
const ModuleHeaderConfigurator = () => {
  const [icon, setIcon]   = useState('Baby');
  const [title, setTitle] = useState('Instruments');
  const [count, setCount] = useState(42);
  const IconMap = { Baby, FileSpreadsheet, FileText, Database };
  const IconComp = IconMap[icon] || Baby;
  const output = `stui-module-header · ${icon} icon · title "${title}" · count ${count}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Icon', <div className="stui-flex-10">{cfgRadios('mh-icon', ['Baby','FileSpreadsheet','FileText','Database'], icon, setIcon)}</div>)}
        {cfgRow('Title', cfgTextInput(title, setTitle))}
        {cfgRow('Count', <input type="number" min={0} value={count} onChange={e => setCount(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 72 }} />)}
      </div>
      <div style={{ border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        <div className="stui-module-header-styleguideonly">
          <div className="stui-module-header-left">
            <IconComp size={20} className="stui-page-icon" />
            <h1>{title || 'Module'}</h1>
            <span className="stui-module-header-meta">{count} records</span>
          </div>
          <div className="stui-module-header-actions">
            <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
            <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
            <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
          </div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Boolean Display Configurator ───────────────────────────── */
const BooleanDisplayConfigurator = () => {
  const [val, setVal] = useState('true');
  const output = `stui-boolean-display · value ${val}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Value', <div className="stui-flex-10">{cfgRadios('bd-val', ['true','false'], val, setVal)}</div>)}
      <div className="stui-example-cell">
        <div className="stui-boolean-display">
          {val === 'true' && <span className="stui-boolean-check">✓</span>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Breadcrumb Configurator ────────────────────────────────── */
const BreadcrumbConfigurator = () => {
  const [p1, setP1] = useState('Projects');
  const [p2, setP2] = useState('BU28');
  const [cur, setCur] = useState('Instrumentation');
  const output = `stui-breadcrumb · "${p1}" > "${p2}" > "${cur}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Parent 1', cfgTextInput(p1, setP1, 140))}
        {cfgRow('Parent 2', cfgTextInput(p2, setP2, 140))}
        {cfgRow('Current', cfgTextInput(cur, setCur, 140))}
      </div>
      <div className="stui-example-cell">
        <div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center' }}>
          <span className="stui-breadcrumb-parent">{p1 || 'Parent 1'}</span>
          <span className="stui-breadcrumb-arrow"> › </span>
          <span className="stui-breadcrumb-parent">{p2 || 'Parent 2'}</span>
          <span className="stui-breadcrumb-arrow"> › </span>
          <span className="stui-breadcrumb-current">{cur || 'Current'}</span>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Badge Configurator ─────────────────────────────────────── */
const BADGE_ICONS = { Check, CheckCircle, Clock, AlertTriangle, Info, UserCheck, X };

const STATUS_TEXT_ICONS = { AlertTriangle, Info, Clock, CheckCircle, X };

const StatusTextConfigurator = () => {
  const [tone, setTone]   = useState('warning');
  const [size, setSize]   = useState('xs');
  const [icon, setIcon]   = useState('AlertTriangle');
  const [label, setLabel] = useState('');
  const defaultLabels = { none: 'Last synced 4 minutes ago', warning: '3 new values need assignment', error: 'No ConnectionStrings section found' };
  const display = label || defaultLabels[tone];
  const IconComp = STATUS_TEXT_ICONS[icon] || null;
  const classes = ['stui-status-text', tone !== 'none' && `stui-status-text-${tone}`, size === 'xs' && 'stui-status-text-xs'].filter(Boolean).join(' ');
  const output = [classes, IconComp ? `<${icon} size={${size === 'xs' ? 13 : 14}} /> first` : 'no icon', `text "${display}"`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Tone', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('stext-t', ['none', 'warning', 'error'], tone, setTone)}</div>)}
        {cfgRow('Size', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('stext-s', ['inherit', 'xs'], size, setSize)}</div>)}
        {cfgRow('Icon', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('stext-i', ['none', ...Object.keys(STATUS_TEXT_ICONS)], icon, setIcon)}</div>)}
        {cfgRow('Text', cfgTextInput(label, setLabel, 220))}
      </div>
      <div className="stui-example-cell">
        <span className={classes}>{IconComp && <IconComp size={size === 'xs' ? 13 : 14} />}{display}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const BadgeConfigurator = () => {
  const VARIANTS = ['green','amber','orange','red','blue','purple','teal','indigo','gray','primary','secondary'];
  const [variant, setVariant] = useState('green');
  const [icon, setIcon]       = useState('none');
  const [label, setLabel]     = useState('');
  const defaultLabels = { green: 'Complete', amber: 'Pending', orange: 'In Progress', red: 'CRIT', blue: 'Validate', purple: 'Retry', teal: 'Refactor', indigo: 'Core', gray: 'Not Started', primary: 'MAJOR', secondary: 'MINOR' };
  const display = label || defaultLabels[variant];
  const IconComp = BADGE_ICONS[icon] || null;
  const output = [`stui-badge-${variant}`, IconComp ? `<${icon} size={12} /> first` : 'no icon', `label "${display}"`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Variant', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('badge-v', VARIANTS, variant, setVariant)}</div>)}
        {cfgRow('Icon', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{cfgRadios('badge-i', ['none', ...Object.keys(BADGE_ICONS)], icon, setIcon)}</div>)}
        {cfgRow('Label', cfgTextInput(label, setLabel, 160))}
      </div>
      <div className="stui-example-cell">
        <span className={`stui-badge-${variant}`}>{IconComp && <IconComp size={12} />}{display}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Tile Configurator ──────────────────────────────────────── */
const TileConfigurator = () => {
  const [title, setTitle]       = useState('Drawing Log');
  const [icon, setIcon]         = useState('FileText');
  const [clickable, setClickable] = useState(true);
  const [legend, setLegend]     = useState(true);
  const IconMap = { FileText, FileSpreadsheet, Database };
  const IconComp = IconMap[icon] || FileText;
  const output = `stui-tile · title "${title}" · ${icon} icon${clickable ? ' · clickable' : ''}${legend ? ' · with legend' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Icon', <div className="stui-flex-10">{cfgRadios('tile-icon', ['FileText','FileSpreadsheet','Database'], icon, setIcon)}</div>)}
        {cfgRow('Title', cfgTextInput(title, setTitle))}
        {cfgRow('Options', <div style={{ display: 'flex', gap: 12 }}>{cfgCheck(clickable, setClickable, 'Clickable')}{cfgCheck(legend, setLegend, 'Legend')}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={`stui-tile${clickable ? ' stui-clickable' : ''}`} style={{ width: 200 }}>
          <div className="stui-tile-header"><IconComp size={16} className="stui-tile-icon" /><span className="stui-tile-title">{title || 'Tile Title'}</span></div>
          <div className="stui-tile-content stui-text-placeholder">…chart…</div>
          {legend && <div className="stui-tile-legend"><span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span></div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Stat Row Configurator ──────────────────────────────────── */
const StatRowConfigurator = () => {
  const [stats, setStats] = useState('Rows:1,284 | Pages:43 | Layout:Tabloid | Time:312ms');
  const parsed = stats.split('|').map(s => s.trim()).filter(Boolean).map(s => { const [l, v] = s.split(':'); return { label: l?.trim() || '', value: v?.trim() || '' }; });
  const output = `stui-stat-row · ${parsed.map(s => `"${s.label}:${s.value}"`).join(' ')}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Stats', <input value={stats} onChange={e => setStats(e.target.value)} placeholder="Label:Value | Label:Value" className="stui-cfg-input" style={{ width: 300 }} />, 48)}
      <div className="stui-hint" style={{ marginTop: -4 }}>Separate stats with |, label:value pairs with :</div>
      <div className="stui-stat-row">
        <div className="stui-stat-group">
          {parsed.map((s, i) => (
            <div key={i} className="stui-stat-item">
              <span className="stui-stat-label">{s.label}</span>
              <span className="stui-stat-value">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Label Configurator ─────────────────────────────────────── */
const LabelConfigurator = () => {
  const [text, setText] = useState('Sort By:');
  const output = `stui-label · text "${text}"`;
  return (
    <div className="stui-col-12">
      {cfgRow('Text', cfgTextInput(text, setText))}
      <div className="stui-example-cell" style={{ gap: 8 }}>
        <span className="stui-label">{text || 'Label'}</span>
        <StuiSelect className="stui-select"><option>Option A</option></StuiSelect>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Checkbox Configurator ──────────────────────────────────── */
const CheckboxConfigurator = () => {
  const [label, setLabel]   = useState('Show QR Code in report header');
  const [checked, setChecked] = useState(true);
  const output = `stui-checkbox · label "${label}"${checked ? ' · checked' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 220))}
        {cfgRow('State', cfgCheck(checked, setChecked, 'Checked'))}
      </div>
      <div className="stui-example-cell">
        <label className="stui-checkbox"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} /> {label || 'Checkbox label'}</label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Checkbox Input Configurator ────────────────── */
const CheckboxInputConfigurator = () => {
  const [label, setLabel]       = useState('Include archived records');
  const [checked, setChecked]   = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [danger, setDanger]     = useState(false);
  const cls = `stui-checkbox-input${danger ? ' stui-checkbox-input-danger' : ''}`;
  const output = `stui-checkbox > ${cls} · label "${label}"${checked ? ' · checked' : ''}${disabled ? ' · disabled' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 220))}
        {cfgRow('State', cfgCheck(checked, setChecked, 'Checked'))}
        {cfgRow('', cfgCheck(disabled, setDisabled, 'Disabled'))}
        {cfgRow('Variant', cfgCheck(danger, setDanger, 'Danger (red when selected)'))}
      </div>
      <div className="stui-example-cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
        <div className="stui-hint">Themed — .stui-checkbox label + {cls}</div>
        <label className="stui-checkbox"><input type="checkbox" className={cls} checked={checked} disabled={disabled} onChange={e => setChecked(e.target.checked)} /> {label || 'Checkbox label'}</label>
        <div className="stui-hint">Unthemed — same label class, bare native input</div>
        <label className="stui-checkbox"><input type="checkbox" checked={checked} disabled={disabled} readOnly /> {label || 'Checkbox label'}</label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Radio Input Configurator ───────────────────── */
const RADIO_DEMO_OPTIONS = ['Physical', 'Comms', 'Omit'];
const RadioInputConfigurator = () => {
  const [selected, setSelected] = useState('Physical');
  const [disabled, setDisabled] = useState(false);
  const output = `stui-checkbox > stui-radio-input · name "io" · selected "${selected}"${disabled ? ' · disabled' : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('State', cfgCheck(disabled, setDisabled, 'Disabled'))}
      </div>
      <div className="stui-example-cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
        <div className="stui-hint">Themed — .stui-checkbox label + stui-radio-input</div>
        {RADIO_DEMO_OPTIONS.map(o => (
          <label key={o} className="stui-checkbox"><input type="radio" name="stui-radio-cfg" className="stui-radio-input" checked={selected === o} disabled={disabled} onChange={() => setSelected(o)} /> {o}</label>
        ))}
        <div className="stui-hint">Unthemed — same label class, bare native input</div>
        {RADIO_DEMO_OPTIONS.map(o => (
          <label key={o} className="stui-checkbox"><input type="radio" name="stui-radio-cfg-native" checked={selected === o} disabled={disabled} readOnly /> {o}</label>
        ))}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Inner Table Configurator ───────────────────── */
const INNER_TABLE_DEMO = [
  { path: 'Test.Path.One', source: 'test', target: 'test' },
  { path: 'Test.Path.Two', source: 'test', target: 'test' },
  { path: 'Test.Path.Three', source: 'test', target: 'test' },
];
const InnerTableConfigurator = () => {
  const [header, setHeader] = useState(true);
  const [mono, setMono] = useState(true);
  const [tone, setTone] = useState('none');
  const [empty, setEmpty] = useState(false);
  const toneClass = tone === 'none' ? undefined : `stui-row-tone-${tone}`;
  const output = ['stui-inner-table', header ? 'thead' : 'no header', mono ? 'value cells .stui-inner-table-cell-mono' : 'plain value cells',
    empty ? 'empty: tr.stui-inner-table-empty' : tone === 'none' ? 'no row tone' : `row 2 .stui-row-tone-${tone}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Content', <>{cfgCheck(header, setHeader, 'Header row')}{cfgCheck(mono, setMono, 'Mono values')}{cfgCheck(empty, setEmpty, 'Empty state')}</>)}
        <div className="stui-row-10-wrap">
          <span className="stui-cfg-key">Row 2 tone</span>
          {['none', 'error', 'warning'].map(v => (
            <label key={v} className="stui-cfg-label">
              <input type="radio" className="stui-radio-input" name="it-tone" checked={tone === v} onChange={() => setTone(v)} /> {v}
            </label>
          ))}
        </div>
      </div>
      <div className="stui-example-cell">
        <table className="stui-inner-table">
          {header && <thead><tr><th>Path</th><th>Source</th><th>Target</th></tr></thead>}
          <tbody>
            {empty ? (
              <tr className="stui-inner-table-empty"><td colSpan={3}>No rows</td></tr>
            ) : INNER_TABLE_DEMO.map((r, i) => (
              <tr key={r.path} className={i === 1 ? toneClass : undefined}>
                <td><code>{r.path}</code></td>
                <td className={mono ? 'stui-inner-table-cell-mono' : undefined}>{r.source}</td>
                <td className={mono ? 'stui-inner-table-cell-mono' : undefined}>{r.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Radio Card Configurator ───────────────────── */
const RADIO_CARD_DEMO = [
  { id: 'a', label: 'Prod → PreProd', desc: 'Azure production to Azure preprod' },
  { id: 'b', label: 'PreProd → Prod', desc: 'Azure preprod to Azure production' },
  { id: 'c', label: 'AWA Prod → Azure PreProd', desc: 'On-prem production to Azure preprod' },
];
const RadioCardConfigurator = () => {
  const [selected, setSelected] = useState('a');
  const [showDesc, setShowDesc] = useState(true);
  const [disabledIds, setDisabledIds] = useState(['c']);
  const toggleDisabled = (id) => setDisabledIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const disabledLabels = RADIO_CARD_DEMO.filter(o => disabledIds.includes(o.id)).map(o => `"${o.label}"`);
  const output = `stui-radio-card · .selected on the checked option > stui-radio-input + stui-form-field (label${showDesc ? ' + stui-list-item-secondary' : ''})${disabledLabels.length ? ` · .disabled: ${disabledLabels.join(', ')}` : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Content', cfgCheck(showDesc, setShowDesc, 'Description line'))}
        {cfgRow('Disabled', <>{RADIO_CARD_DEMO.map(o => <React.Fragment key={o.id}>{cfgCheck(disabledIds.includes(o.id), () => toggleDisabled(o.id), o.label)}</React.Fragment>)}</>)}
      </div>
      <div className="stui-example-cell" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--spacing-md)' }}>
        {RADIO_CARD_DEMO.map(o => {
          const disabled = disabledIds.includes(o.id);
          return (
            <label key={o.id} className={`stui-radio-card${disabled ? ' disabled' : ''}${selected === o.id ? ' selected' : ''}`}>
              <input type="radio" name="stui-radio-card-cfg" className="stui-radio-input" checked={selected === o.id} disabled={disabled} onChange={() => setSelected(o.id)} />
              <div className="stui-form-field">
                <span className="stui-row-8 stui-fw-semibold">{o.label}{disabled && <Lock size={12} className="stui-text-muted" />}</span>
                {showDesc && <span className="stui-list-item-secondary">{o.desc}</span>}
              </div>
            </label>
          );
        })}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Small Text Configurator ── */
const SMALL_TEXT_CLASSES = [
  ['stui-text-sm-muted',      'Muted Body Text'],
  ['stui-text-placeholder',   'Placeholder Text'],
  ['stui-label',              'Field Label'],
  ['stui-example-row-label',  'Example Row Label'],
  ['stui-sublabel',          'Sub-Label'],
  ['stui-label-xs',           'Small Label'],
  ['stui-label-note',         'Field Note'],
  ['stui-label-xs-muted',     'Small Label, Muted'],
  ['stui-example-label',      'Example Caption'],
  ['stui-hint',               'Hint'],
  ['stui-c-muted',            'Muted Colour Only'],
];
const SmallTextConfigurator = () => {
  const [cls, setCls]   = useState('stui-hint');
  const [text, setText] = useState('Separate stats with |, label:value pairs with :');
  const output = `${cls} · text "${text || 'Sample text'}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Text', cfgTextInput(text, setText, 320))}
        {cfgRow('Type', <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {cfgRadios('small-text', SMALL_TEXT_CLASSES.map(c => c[1]),
            (SMALL_TEXT_CLASSES.find(c => c[0] === cls) || [])[1],
            lbl => setCls(SMALL_TEXT_CLASSES.find(c => c[1] === lbl)[0]))}
        </div>)}
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'flex-start' }}>
        <span className={cls}>{text || 'Sample text'}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Sub-Label Configurator ─────────────────────────────────── */
const SubLabelConfigurator = () => {
  const [label, setLabel]    = useState('Theme');
  const [sublabel, setSublabel] = useState('Controls the color palette across all pages');
  const output = `stui-sublabel · label "${label}" · sublabel "${sublabel}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 120))}
        {cfgRow('Sub-Label', cfgTextInput(sublabel, setSublabel, 260))}
      </div>
      <div className="stui-example-cell" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <span className="stui-label">{label || 'Label'}</span>
        <div className="stui-sublabel">{sublabel || 'Helper text'}</div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Pill Configurator ──────────────────────────────────────── */
const PillConfigurator = () => {
  const [options, setOptions] = useState('Light,Dark,Dim,Sepia,Hi-Con');
  const [selected, setSelected] = useState(0);
  const opts = options.split(',').map(o => o.trim()).filter(Boolean);
  const output = `stui-pill · options "${options}" · selected ${selected}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Options', <input value={options} onChange={e => setOptions(e.target.value)} placeholder="comma-separated" className="stui-cfg-input" style={{ width: 220 }} />, 64)}
        {cfgRow('Selected', <input type="number" min={0} max={opts.length - 1} value={selected} onChange={e => setSelected(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 56 }} />, 64)}
      </div>
      <div className="stui-example-cell">
        <div className="stui-pill-group">
          {opts.map((o, i) => <button key={i} className={`stui-pill${i === selected ? ' selected' : ''}`} onClick={() => setSelected(i)}>{o}</button>)}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Toggle Configurator ────────────────────────────────────── */
const ToggleConfigurator = () => {
  const [label, setLabel] = useState('Enable notifications');
  const [on, setOn]       = useState(true);
  const output = `stui-toggle · label "${label}" · ${on ? 'on' : 'off'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 200))}
        {cfgRow('State', cfgCheck(on, setOn, 'On'))}
      </div>
      <div className="stui-example-cell" style={{ gap: 10 }}>
        <label className="stui-toggle">
          <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} />
          <span className="stui-toggle-slider" />
        </label>
        <span className="stui-label">{label || 'Toggle label'}</span>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Setting Row Configurator ───────────────────────────────── */
const SettingRowConfigurator = () => {
  const [type, setType]       = useState('toggle');
  const [label, setLabel]     = useState('Enable Feature');
  const [sublabel, setSublabel] = useState('Turns this option on for all users');
  const [on, setOn]           = useState(true);
  const output = `stui-row-setting · type ${type} · label "${label}" · sublabel "${sublabel}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Type', <div className="stui-flex-10">{cfgRadios('sr-type', ['toggle','select','buttons'], type, setType)}</div>)}
        {cfgRow('Label', cfgTextInput(label, setLabel))}
        {cfgRow('Sub-label', cfgTextInput(sublabel, setSublabel, 240))}
      </div>
      <div className="stui-example-cell" style={{ padding: 0 }}>
        <div className="stui-row-setting" style={{ width: '100%', padding: '12px 16px' }}>
          <div className="stui-row-setting-label">
            <div className="stui-label">{label || 'Setting'}</div>
            <div className="stui-sublabel">{sublabel}</div>
          </div>
          {type === 'toggle' && <label className="stui-toggle"><input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} /><span className="stui-toggle-slider" /></label>}
          {type === 'select' && <StuiSelect className="stui-select"><option>Option A</option><option>Option B</option></StuiSelect>}
          {type === 'buttons' && <div className="stui-row-setting-controls"><button className="stui-btn stui-btn-secondary">Browse</button><button className="stui-btn stui-btn-primary">Apply</button></div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Frame Configurator ─────────────────────────────────────── */
const FrameConfigurator = () => {
  const [captionOn, setCaptionOn] = useState(true);
  const [caption, setCaption]     = useState('Match ALL of these');
  const [content, setContent]     = useState('fields');
  const output = captionOn
    ? `<StuiFrame caption="${caption}"> · fieldset.stui-frame + legend · content ${content}`
    : `<StuiFrame> · fieldset.stui-frame, no caption · content ${content}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Caption', cfgCheck(captionOn, setCaptionOn, 'Show caption'))}
        {cfgRow('Text', cfgTextInput(caption, setCaption, 200))}
        {cfgRow('Content', <div className="stui-flex-10">{cfgRadios('fr-content', ['fields', 'checkboxes', 'buttons'], content, setContent)}</div>)}
      </div>
      <div className="stui-example-cell">
        <StuiFrame caption={captionOn ? caption : undefined}>
          {content === 'fields' && (
            <div className="stui-form-row-inline">
              <div className="stui-form-field"><label>First</label><input className="stui-field-input" defaultValue="Alpha" /></div>
              <div className="stui-form-field"><label>Second</label><input className="stui-field-input" defaultValue="Beta" /></div>
            </div>
          )}
          {content === 'checkboxes' && (
            <div className="stui-toggle-group">
              {['Email', 'Teams', 'Footer'].map(c => (
                <label key={c} className="stui-cfg-label"><input type="checkbox" defaultChecked={c !== 'Teams'} /> {c}</label>
              ))}
            </div>
          )}
          {content === 'buttons' && (
            <div className="stui-btn-row">
              <button className="stui-btn stui-btn-secondary">Reset</button>
              <button className="stui-btn stui-btn-primary">Apply</button>
            </div>
          )}
        </StuiFrame>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Search Builder Configurator ────────────────────────────── */
const DEMO_SEARCH_FIELDS = [
  { key: 'owner', label: 'Owner' }, { key: 'status', label: 'Status' }, { key: 'entryType', label: 'Type' },
];
const DEMO_SEARCH_ROWS = [['owner', 'josh, danny'], ['status', 'Pending'], ['entryType', 'Bug']];

const SearchBuilderConfigurator = () => {
  const [groups, setGroups] = useState('2');
  const [rows, setRows]     = useState('2');
  const [op, setOp]         = useState('contains');
  const model = Array.from({ length: Number(groups) }, (_, gi) => DEMO_SEARCH_ROWS
    .slice(0, Number(rows))
    .map(([field, value], ri) => ({ field: gi % 2 && ri === 0 ? 'entryType' : field, value: gi % 2 && ri === 0 ? 'Feature' : value })));
  const query = buildSearchQuery(model.map(g => ({
    clauses: g.map(r => ({ field: r.field, values: r.value.split(',').map(v => v.trim()), negated: op === 'not' })),
  })), DEMO_SEARCH_FIELDS);
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Groups', <div className="stui-flex-10">{cfgRadios('sb-groups', ['1', '2', '3'], groups, setGroups)}</div>)}
        {cfgRow('Rows', <div className="stui-flex-10">{cfgRadios('sb-rows', ['1', '2', '3'], rows, setRows)}</div>)}
        {cfgRow('Operator', <div className="stui-flex-10">{cfgRadios('sb-op', ['contains', 'not'], op, setOp)}</div>)}
      </div>
      <div className="stui-example-cell">
        <div>
          {model.map((g, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <div className="stui-search-builder-or">OR</div>}
              <StuiFrame caption="Match ALL of these">
                {g.map((r, ri) => (
                  <div key={ri} className="stui-search-builder-row">
                    <StuiSelect className="stui-field-input" value={r.field} onChange={() => {}}>
                      {DEMO_SEARCH_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </StuiSelect>
                    <StuiSelect className="stui-field-input" value={op} onChange={() => {}}>
                      <option value="contains">contains</option>
                      <option value="not">does not contain</option>
                    </StuiSelect>
                    <input className="stui-field-input" value={r.value} readOnly />
                    <button type="button" className="stui-crud-btn" title="Remove condition"><X size={16} /></button>
                  </div>
                ))}
              </StuiFrame>
            </React.Fragment>
          ))}
        </div>
      </div>
      <ConfigOutput text={`stui-search-builder · ${groups} group(s) × ${rows} row(s) → ${query}`} />
    </div>
  );
};

/* ── List Layout Configurator (static) ──────────────────────── */
const ListLayoutConfigurator = () => {
  const output = 'stui-list-view · list-panel left · detail-panel flex-1 · 24-col grid';
  return (
    <div className="stui-col-12">
      <div className="stui-text-sm-muted">Structural layout — no interactive options. Wrap <code>stui-list-panel</code> + <code>stui-detail-panel</code> inside <code>stui-list-view</code>.</div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Form Fields Configurator ───────────────────────────────── */
const FormFieldsConfigurator = () => {
  const [label, setLabel]     = useState('Tag Name');
  const [state, setState]     = useState('normal');
  const [position, setPosition] = useState('top');
  const stateClass = state === 'normal' ? '' : ` ${state}`;
  const posClass   = position === 'top' ? '' : ` label-${position}`;
  const output = `stui-field-group · label "${label}" · state ${state} · position ${position}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Label', cfgTextInput(label, setLabel, 140))}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('ff-state', ['normal','dirty','fk','derived','readonly'], state, setState)}</div>)}
        {cfgRow('Position', <div className="stui-flex-10">{cfgRadios('ff-pos', ['top','left','right','bottom'], position, setPosition)}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={`stui-field-group${posClass}`} style={{ width: 240 }}>
          <label>{label || 'Field'}</label>
          {state === 'readonly' && <div className="stui-field-readonly">System managed</div>}
          {state === 'derived'  && <input className="stui-field-input derived" value="AUTO-2024" readOnly />}
          {state === 'fk'       && <div className="stui-field-input fk" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}><span>Panel A</span><Link2 size={12} className="stui-c-muted" /></div>}
          {(state === 'normal' || state === 'dirty') && <input className={`stui-field-input${stateClass}`} defaultValue="Sample value" />}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── SearchSelect Configurator ──────────────────────────────── */
const SearchSelectConfigurator = () => {
  const [value, setValue] = useState('Panel A');
  const [state, setState] = useState('normal');
  const output = `stui-searchselect-trigger · value "${value}" · state ${state}`;
  const cls = `stui-searchselect-trigger${state !== 'normal' ? ` ${state}` : ''}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Value', cfgTextInput(value, setValue, 140))}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('ss-state', ['normal','open','disabled','dirty'], state, setState)}</div>)}
      </div>
      <div className="stui-example-cell">
        <div className={cls} style={{ width: 200 }}>
          <span className={`stui-searchselect-value${!value ? ' placeholder' : ''}`}>{value || '-- Select --'}</span>
          <ChevronDown size={14} className="stui-searchselect-chevron" />
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Related Tables Configurator ────────────────────────────── */
const RelatedTablesConfigurator = () => {
  const [tabs, setTabs]           = useState('Drawings,Tags,Equipment');
  const [collapsed, setCollapsed] = useState(false);
  const [active, setActive]       = useState(0);
  const tabArr = tabs.split(',').map(t => t.trim()).filter(Boolean);
  const output = `stui-related-section · tabs "${tabs}"${collapsed ? ' · collapsed' : ' · expanded'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Tabs', <input value={tabs} onChange={e => setTabs(e.target.value)} placeholder="comma-separated" className="stui-cfg-input" style={{ width: 200 }} />, 56)}
        {cfgRow('State', cfgCheck(collapsed, setCollapsed, 'Collapsed'), 56)}
      </div>
      <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', maxWidth: 480 }}>
        <div className={`stui-related-section${collapsed ? ' collapsed' : ''}`}>
          <div className="stui-related-header">
            <button className="stui-related-collapse-btn" onClick={() => setCollapsed(c => !c)}>{collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
            <div className="stui-related-tabs">
              {tabArr.map((t, i) => <button key={i} className={`stui-related-tab${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>{t}<span className="stui-related-tab-count">{(i + 1) * 4}</span></button>)}
            </div>
          </div>
          {!collapsed && <div className="stui-related-content stui-related-body">Showing {tabArr[active] || 'records'} for this record…</div>}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Datasheet Extras Configurator ──────────────────────────── */
const DatasheetExtrasConfigurator = () => {
  const [mode, setMode] = useState('delete');
  const output = `stui-data-grid · mode ${mode}`;
  return (
    <div className="stui-col-12">
      {cfgRow('Mode', <div className="stui-flex-10">{cfgRadios('de-mode', ['delete','edit'], mode, setMode)}</div>)}
      <div style={{ overflowX: 'auto' }}>
        {mode === 'delete' ? (
          <table className="stui-data-grid delete-mode stui-w-full">
            <thead><tr><th style={{ width: 40 }}></th><th>Tag</th><th>Panel</th></tr></thead>
            <tbody>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>AI-100</td><td>Panel A</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>DI-201</td><td>Panel B</td></tr>
            </tbody>
          </table>
        ) : (
          <table className="stui-data-grid edit-mode stui-w-full">
            <thead><tr><th>Tag</th><th>Description</th><th>Readonly</th></tr></thead>
            <tbody>
              <tr>
                <td className="editable-cell"><input defaultValue="AI-100" className="stui-editable-input" /></td>
                <td className="editable-cell modified"><input defaultValue="Modified desc" className="stui-editable-input" /></td>
                <td className="readonly-cell">Panel A</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Pagination Configurator ────────────────────────────────── */
const PaginationConfigurator = () => {
  const [page, setPage]         = useState(2);
  const [total, setTotal]       = useState(143);
  const [pageSize, setPageSize] = useState(50);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);
  const output = `stui-pagination · page ${page} · total ${total} · pageSize ${pageSize}`;
  const pages  = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1);
  return (
    <div className="stui-col-12">
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['Page', page, setPage, 1, totalPages], ['Total', total, setTotal, 1, 9999], ['Page Size', pageSize, setPageSize, 10, 200]].map(([l, v, s, mn, mx]) => (
          <div key={l} className="stui-row-8">
            <span className="stui-label-xs">{l}</span>
            <input type="number" min={mn} max={mx} value={v} onChange={e => s(Math.min(mx, Math.max(mn, +e.target.value)))} className="stui-cfg-input-sm" style={{ width: 60 }} />
          </div>
        ))}
      </div>
      <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', maxWidth: 420 }}>
        <div className="stui-pagination">
          <div className="stui-pagination-controls">
            <button className="stui-pagination-btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronLeft size={14} /></button>
            {pages.map(p => <button key={p} className={`stui-pagination-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="stui-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}><ChevronRight size={14} /></button>
          </div>
          <span className="stui-pagination-info">{start}–{end} of {total}</span>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── FK Select Configurator ─────────────────────────────────── */
const FkSelectConfigurator = () => {
  const [path, setPath]   = useState('Building → Drawing');
  const [title, setTitle] = useState('Drawing');
  const output = `stui-fk-select · path "${path}" · title "${title}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Path', cfgTextInput(path, setPath, 200))}
        {cfgRow('Title', cfgTextInput(title, setTitle, 140))}
      </div>
      <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', maxWidth: 460, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        <div className="stui-fk-select-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            {path.split('→').map((seg, i, arr) => (
              <span key={i} style={{ color: i === arr.length - 1 ? 'var(--color-gray-900)' : 'var(--color-gray-400)', fontWeight: i === arr.length - 1 ? 600 : 400 }}>
                {seg.trim()}{i < arr.length - 1 && <span style={{ margin: '0 4px' }}>→</span>}
              </span>
            ))}
          </div>
          <div className="stui-flex-4">
            <button className="stui-crud-btn" title="Close"><X size={16} /></button>
          </div>
        </div>
        <div className="stui-fk-select-body" style={{ padding: '12px 16px', minHeight: 60, fontSize: 12, color: 'var(--color-gray-500)', fontStyle: 'italic' }}>
          …{title} records…
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Composer Select Configurator ───────────────────────────── */
const ComposerSelectConfigurator = () => {
  const [module, setModule] = useState('Building');
  const [open, setOpen]     = useState(false);
  const modules = ['Building', 'Instrument', 'Panel', 'Drawing', 'Equipment'];
  const output = `stui-composer-select · module "${module}" · ${open ? 'open' : 'closed'}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Module', cfgTextInput(module, setModule, 140))}
        {cfgRow('State', cfgCheck(open, setOpen, 'Open'))}
      </div>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div className="stui-composer-select">
          <label className="stui-composer-select-label">Module</label>
          <div className={`stui-composer-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
            <span>{module || 'Select…'}</span>
            <ChevronDown size={14} />
          </div>
          {open && (
            <div className="stui-composer-select-panel" style={{ position: 'relative', marginTop: 4, top: 'auto', left: 'auto' }}>
              <input className="stui-composer-select-search" placeholder="Search modules…" />
              <div className="stui-composer-select-options">
                {modules.map((m, i) => (
                  <div key={m} className={`stui-composer-select-option${m === module ? ' selected' : ''}`} onClick={() => { setModule(m); setOpen(false); }}>
                    <span className="stui-composer-select-option-name">{m}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Module Header CRUD Configurator ────────────────────────── */
const ModuleHeaderCrudConfigurator = () => {
  const [mode, setMode]   = useState('list');
  const [state, setState] = useState('view');
  const output = `stui-module-header-crud · mode ${mode} · state ${state}`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Mode', <div className="stui-flex-10">{cfgRadios('mhc-mode', ['list','datasheet'], mode, setMode)}</div>)}
        {cfgRow('State', <div className="stui-flex-10">{cfgRadios('mhc-state', ['view','editing'], state, setState)}</div>)}
      </div>
      <div style={{ padding: '12px 16px', background: 'var(--color-gray-50)', border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', display: 'inline-flex' }}>
        {mode === 'list' ? (
          <div className="stui-module-header-crud">
            {state === 'view' ? <>
              <button className="stui-crud-btn" title="Add"><Plus size={18} /></button>
              <button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button>
              <button className="stui-crud-btn disabled" disabled title="Save"><Save size={18} /></button>
            </> : <>
              <button className="stui-crud-btn stui-delete-btn" title="Delete"><Trash2 size={18} /></button>
              <button className="stui-crud-btn" title="Cancel"><X size={18} /></button>
              <button className="stui-crud-btn" title="Save"><Save size={18} /></button>
            </>}
          </div>
        ) : (
          <div className="stui-module-header-datasheet-controls">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button>
              <button className={`stui-crud-btn${state === 'view' ? ' disabled' : ''}`} disabled={state === 'view'} title="Save"><Save size={18} /></button>
            </div>
          </div>
        )}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Base Reset Static Configurators ────────────────────────── */
const BoxSizingConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Universal rule — no options. Applied globally to every element and pseudo-element.</div>
    <ConfigOutput text="* { box-sizing: border-box } · *::before { box-sizing: border-box } · *::after { box-sizing: border-box }" />
  </div>
);

const BodyConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Applied once to the <code>&lt;body&gt;</code> element — sets base font, weight, color, and anti-aliasing for the entire app.</div>
    <ConfigOutput text="body · font-family var(--font-secondary) · font-weight 500 · color var(--color-gray-800) · -webkit-font-smoothing antialiased" />
  </div>
);

const FormControlsConfigurator = () => (
  <div className="stui-col-8">
    <div className="stui-text-sm-muted">Applied to all native form elements — forces them to inherit the body font instead of the browser's system font.</div>
    <ConfigOutput text="input,select,textarea,button · font-family inherit · font-weight inherit" />
  </div>
);

/* ── Demo components ───────────────────────────────────────── */

const MiniGrid = () => (
  <div className="stui-data-grid-container" style={{ maxWidth: 420, maxHeight: 180 }}>
    <div className="stui-data-grid-scroll-area">
      <table className="stui-data-grid stui-no-column-resize stui-w-full">
        <thead><tr><th>Tag</th><th>Panel</th><th>I/O Type</th></tr></thead>
        <tbody>
          {[['AI-1001','MCP-101','Analog Input'],['DI-2201','MCP-101','Digital Input'],['AO-3301','MCP-102','Analog Output'],['DO-4401','MCP-102','Digital Output']].map(([t,p,io]) => (
            <tr key={t} className="stui-clickable"><td>{t}</td><td>{p}</td><td>{io}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DemoActionMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="stui-action-menu">
      <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
      {open && (
        <div className="stui-action-menu-dropdown">
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><RefreshCw size={16} /> Refresh</button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><Download size={16} /> Export Excel</button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><SlidersHorizontal size={16} /> Modify View</button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item" disabled title="Disabled items dim and stop reacting"><FileText size={16} /> Download PDF (disabled)</button>
          <div className="stui-action-menu-divider-strong" />
          <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Trash2 size={16} /> Delete</button>
        </div>
      )}
    </div>
  );
};

const DemoExportMenu = () => {
  const [open, setOpen] = useState(false);
  const [separate, setSeparate] = useState(false);
  return (
    <div className="stui-action-menu">
      <button className="stui-action-menu-btn" onClick={() => setOpen(o => !o)} title="Export"><GripVertical size={16} /></button>
      {open && (
        <div className="stui-action-menu-dropdown">
          <label className="stui-action-menu-item" style={{ cursor:'pointer' }}><input type="checkbox" checked={separate} onChange={e => setSeparate(e.target.checked)} style={{ marginRight:4 }} /><span>Separate Files</span></label>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileText size={16} /><span>Download PDF</span></button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileSpreadsheet size={16} /><span>Export Excel</span></button>
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FileDown size={16} /><span>Export CSV</span></button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => setOpen(false)}><Ban size={16} /><span>Cancel Export</span></button>
          <div className="stui-action-menu-divider" />
          <button className="stui-action-menu-item" onClick={() => setOpen(false)}><FilterX size={16} /><span>Clear Filters &amp; Sort</span></button>
        </div>
      )}
    </div>
  );
};

/* ── Color Picker Configurator ──────────────────────────────── */
const ColorPickerConfigurator = () => {
  const [color, setColor]       = useState('var(--color-primary)');
  const [allowAlpha, setAlpha]  = useState(true);
  const [disabled, setDisabled] = useState(false);
  const output = [
    'stui-cp-trigger',
    `value "${color}"`,
    allowAlpha ? 'alpha on' : 'alpha off',
    disabled ? 'disabled' : 'enabled',
  ].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Alpha', (
          <label className="stui-cfg-label">
            <input type="checkbox" checked={allowAlpha} onChange={e => setAlpha(e.target.checked)} /> allowAlpha
          </label>
        ), 64)}
        {cfgRow('State', (
          <label className="stui-cfg-label">
            <input type="checkbox" checked={disabled} onChange={e => setDisabled(e.target.checked)} /> disabled
          </label>
        ), 64)}
      </div>
      <div className="stui-example-cell">
        <ColorPickerField
          value={color}
          onChange={setColor}
          allowAlpha={allowAlpha}
          disabled={disabled}
          title="Colors"
        />
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Color Harmony Configurator ────────────────────── */
const ColorHarmonyConfigurator = () => {
  const [seed, setSeed] = useState('#1a5568');
  const [count, setCount] = useState(3);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const output = ['stui-ch-modal', `seed "${seed}"`, `count ${count}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Seed', <ColorPickerField value={seed} onChange={setSeed} allowAlpha={false} title="Seed color" />, 64)}
        {cfgRow('Colors', (
          <input type="number" min={3} max={5} value={count} onChange={e => setCount(+e.target.value)} className="stui-cfg-input-sm" style={{ width: 56 }} />
        ), 64)}
      </div>
      <div className="stui-example-cell">
        <button className="stui-btn stui-btn-primary" onClick={() => { setResult(null); setOpen(true); }}>
          <Blend size={14} /> Open
        </button>
        {result && (
          <div className="stui-ch-tiles" style={{ border: 'none', padding: '8px 0 0' }}>
            {result.map(hex => (
              <span key={hex} className="stui-ch-tileswatch" style={{ background: hex, cursor: 'default' }} title={hex} />
            ))}
          </div>
        )}
      </div>
      <ConfigOutput text={output} />
      <ColorHarmonyModal
        isOpen={open}
        seed={seed}
        count={count}
        onClose={() => setOpen(false)}
        onApply={hexes => setResult(hexes)}
      />
    </div>
  );
};

/* ── Tab: Elements ─────────────────────────────────────────── */

const ELEMENT_SECTIONS = [
  { id: 'access-colors',  label: 'Access Colors' },
  { id: 'action-menu',    label: 'Action Menu' },
  { id: 'badge',          label: 'Badge' },
  { id: 'boolean',        label: 'Boolean Display' },
  { id: 'breadcrumb',     label: 'Breadcrumb' },
  { id: 'buttons',        label: 'Buttons' },
  { id: 'checkbox',       label: 'Checkbox' },
  { id: 'checkbox-input', label: 'Checkbox Input' },
  { id: 'checkbox-list',  label: 'Checkbox List' },
  { id: 'code-block',     label: 'Code Block' },
  { id: 'color-harmony',  label: 'Color Harmony' },
  { id: 'color-picker',   label: 'Color Picker' },
  { id: 'column-filter',  label: 'Column Filter' },
  { id: 'column-widths',  label: 'Column Widths' },
  { id: 'css-source-viewer', label: 'CSS Source Viewer' },
  { id: 'data-grid',      label: 'Data Grid' },
  { id: 'date-time',      label: 'Date & Time' },
  { id: 'frame',          label: 'Frame' },
  { id: 'icon-browser',   label: 'Icon Browser' },
  { id: 'image-upload',   label: 'Image Upload' },
  { id: 'inner-table',    label: 'Inner Table' },
  { id: 'label',          label: 'Label' },
  { id: 'layout-utilities', label: 'Layout Utilities' },
  { id: 'lightbox',       label: 'Lightbox' },
  { id: 'module-header',  label: 'Module Header' },
  { id: 'org-images',     label: 'Org Images' },
  { id: 'pill',           label: 'Pill' },
  { id: 'popover',        label: 'Popover' },
  { id: 'radio-card',     label: 'Radio Card' },
  { id: 'radio-input',    label: 'Radio Input' },
  { id: 'rich-text',      label: 'Rich Text' },
  { id: 'search-builder', label: 'Search Builder' },
  { id: 'section-layout', label: 'Section Layout' },
  { id: 'section-panels', label: 'Section Panels' },
  { id: 'segmented',      label: 'Segmented Control' },
  { id: 'select',         label: 'Select / Combobox' },
  { id: 'setting-row',    label: 'Setting Row' },
  { id: 'small-text',     label: 'Small Text' },
  { id: 'sort-controls',  label: 'Sort Controls' },
  { id: 'spinning',       label: 'Spinning' },
  { id: 'stat-row',       label: 'Stat Row' },
  { id: 'status-dot',     label: 'Status Dot' },
  { id: 'status-text',    label: 'Status Text' },
  { id: 'sub-label',      label: 'Sub-Label' },
  { id: 'tab-bar',        label: 'Tab Bar' },
  { id: 'tile',           label: 'Tile' },
  { id: 'toggle',         label: 'Toggle' },
];

const ElementsTab = ({ usageMap, pageSlugMap, elementTexts, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('action-menu');
  // Every theme that can be previewed, straight from the database. Standard has no override
  // tokens of its own, so its zone simply inherits the :root defaults.
  const { themes } = usePreferences();
  const previewThemes = themes.filter(t => t.status !== 'Retired');
  const [rteDemoHtml, setRteDemoHtml] = useState('<h2>Sample</h2><p>Rich <strong>text</strong> with a <a href="#">link</a>.</p><ul><li>Bullet</li></ul>');
  const [tabBarDemo, setTabBarDemo] = useState('Overview');
  // Live upload demos — nothing is stored, the result is just reported back
  const [uploadDemo, setUploadDemo] = useState(null);   // 'logo' | 'picture' | null
  const [uploadDemoResult, setUploadDemoResult] = useState(null);
  const [cpFieldValue, setCpFieldValue] = useState('var(--color-primary)');
  const [cpModalOpen, setCpModalOpen] = useState(false);
  const [cpResult, setCpResult] = useState(null);
  const [cpTokenValue, setCpTokenValue] = useState('var(--color-primary)');
  const [cpEmptyValue, setCpEmptyValue] = useState('');
  const [chOpen, setChOpen] = useState(false);
  const [chResult, setChResult] = useState(null);
  const [sbOpen, setSbOpen] = useState(false);
  const [sbQuery, setSbQuery] = useState('owner:josh,danny status:Pending OR type:Bug');
  const [dtDate, setDtDate] = useState('2026-10-03');
  const [dtTime, setDtTime] = useState('22:00');
  const [dtLocal, setDtLocal] = useState('2026-10-03T22:00');
  const [dtUtc, setDtUtc] = useState('2026-10-04T02:00:00.000Z');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {ELEMENT_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {/* Date & Time */}
      {activeSection === 'date-time' && (
        <Section title="Date & Time" description="StuiDateTime (components/common/StuiDateTime.js) is the one date, time and date-time input. It is a single box of segments, MM / DD / YYYY  hh : mm AM: click a segment and type, or step it with the arrow keys or the spinner (minuteStep 1, 5 or 15), and Left / Right move between segments. The calendar button opens a STUI month grid rather than the browser's picker, whose drop-downs cannot be styled; it opens on the entered date's month, and its footer has Clear and Today. A box too narrow for the spinner goes compact on its own. The value it takes and returns is set by valueZone, and that is the point of the component: 'utc' is a database timestamp, shown and typed in displayZone and converted back on the way out; 'local' is a wall-clock time; 'none' is a plain date or time that is never shifted, so a due date of Oct 3 is Oct 3 in every zone. The page never does zone arithmetic. onChange receives the stored-format value once every segment is filled, and '' while any is empty. For read-only display use utils/dateTime: formatDateTime(value, options) takes the same mode, valueZone and displayZone, and the shortcuts are formatTimestamp (a UTC timestamp in the person's zone, '10/03/2026 9:30 PM'), formatTimestampDate, formatDateOnly (a date column, literally) and generatedStamp (a printed time with its zone). The SQL column type decides valueZone: datetime / datetime2 are 'utc', date and time are 'none'. 'local' means the person's Timezone preference, else the browser's zone." cssFile={CSS_LOCATIONS.dateTime.file} cssSelector={CSS_LOCATIONS.dateTime.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-row-10-wrap">
            <Example label='mode="date" valueZone="none"'>
              <div className="stui-form-field"><label>Due date</label><StuiDateTime mode="date" valueZone="none" value={dtDate} onChange={setDtDate} /></div>
              <p className="stui-hint">value: <code>{dtDate || "''"}</code></p>
            </Example>
            <Example label='mode="time" minuteStep={15}'>
              <div className="stui-form-field"><label>Start time</label><StuiDateTime mode="time" minuteStep={15} value={dtTime} onChange={setDtTime} /></div>
              <p className="stui-hint">value: <code>{dtTime || "''"}</code></p>
            </Example>
            <Example label='mode="datetime" valueZone="local" showZone'>
              <div className="stui-form-field"><label>Maintenance start</label><StuiDateTime mode="datetime" valueZone="local" minuteStep={15} showZone value={dtLocal} onChange={setDtLocal} /></div>
              <p className="stui-hint">value: <code>{dtLocal || "''"}</code></p>
            </Example>
            <Example label='mode="datetime" valueZone="utc" displayZone="America/Chicago"'>
              <div className="stui-form-field"><label>Stored UTC, shown Central</label><StuiDateTime mode="datetime" valueZone="utc" displayZone="America/Chicago" minuteStep={5} showZone value={dtUtc} onChange={setDtUtc} /></div>
              <p className="stui-hint">value: <code>{dtUtc || "''"}</code> &middot; formatDateTime: {formatDateTime(dtUtc, { valueZone: 'utc', showZone: true }) || '—'}</p>
            </Example>
          </div>
          <CSSRef file="STUI.css" selector=".stui-datetime / .stui-datetime-seg / .stui-datetime-sep / .stui-datetime-spin / .stui-datetime-zone / .stui-calendar" />
          <Subsection label="Reference" />
          <RefTable headers={['Prop', 'Purpose']} rows={[
            ['mode', "'date' | 'time' | 'datetime' — which segments show"],
            ['valueZone', "'utc' database timestamp (ISO with Z) | 'local' wall clock (YYYY-MM-DDTHH:mm) | 'none' plain date (YYYY-MM-DD) or time (HH:mm), never shifted. Default 'local' for datetime, 'none' otherwise"],
            ['displayZone', "'local' (the browser's zone, default) or an IANA zone such as America/Chicago — what the person sees and types"],
            ['minuteStep', '1 | 5 | 15 — arrow and spinner step for the minutes segment'],
            ['showZone', 'Append the zone abbreviation (EDT) inside the box'],
            ['value / onChange', "Stored-format value in; the same format out once complete, '' while any segment is empty"],
            ['disabled / title', 'Gray, not focusable / tooltip'],
            ['dirty', 'Edited but not saved: the same highlight as .stui-field-input.dirty'],
          ]} />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-datetime', 'The box: a .stui-field-input look (border, height, focus colour) holding the segments and buttons; .dirty, .disabled'],
            ['.stui-datetime.compact', 'Set by the component when the box is too narrow for its content (a two-column form field), and the look in a datasheet edit cell: no spinner, tighter padding, the calendar button only on hover or focus, an empty box blank until focused'],
            ['.stui-datetime-seg', 'One focusable segment; .empty shows its placeholder in gray; focus fills it with the primary colour'],
            ['.stui-datetime-sep', 'The / and : between segments'],
            ['.stui-datetime-spin', 'Stacked up / down buttons that step the last focused segment'],
            ['.stui-datetime-zone', 'Zone abbreviation, small gray text'],
            ['.stui-calendar', 'Month-grid popover, portalled and fixed under the box, above nested popups; -header, -grid, -day with .outside / .today / .selected; -footer holds Clear (empties and closes) and Today (selects today and shows its month, staying open)'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-datetime" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {/* Frame */}
      {activeSection === 'frame' && (
        <Section title="Frame" description="A captioned group box, like a Frame in VB forms: a bordered, lightly shaded container that groups related controls inside a body. It is one element with an optional caption — the markup is a native <fieldset className=&quot;stui-frame&quot;> whose optional <legend> is the caption, which the browser draws on the border, so no positioning trick is involved. Call it as <StuiFrame caption=&quot;…&quot;> (components/common/StuiFrame.js) and omit caption for a plain frame. Reach for it when a popup or panel body has several groups that need telling apart; the Advanced Search builder uses one frame per AND-group." cssFile={CSS_LOCATIONS.frame.file} cssSelector={CSS_LOCATIONS.frame.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-row-10-wrap">
            <Example label='<StuiFrame caption="Delivery">'>
              <StuiFrame caption="Delivery">
                <div className="stui-toggle-group">
                  <label className="stui-cfg-label"><input type="checkbox" defaultChecked /> Email</label>
                  <label className="stui-cfg-label"><input type="checkbox" /> Teams</label>
                </div>
              </StuiFrame>
            </Example>
            <Example label="<StuiFrame> (no caption)">
              <StuiFrame>
                <div className="stui-form-field"><label>Name</label><input className="stui-field-input" defaultValue="Plain frame" /></div>
              </StuiFrame>
            </Example>
          </div>
          <CSSRef file="STUI.css" selector=".stui-frame / .stui-frame > legend" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-frame', 'On a native <fieldset>: 1px border, radius-sm, gray-50 fill, bottom margin; resets fieldset min-width so it can shrink'],
            ['.stui-frame > legend', 'The caption — text-sm, 600, gray-700, drawn on the top border by the browser'],
            ['<StuiFrame caption className>', 'components/common/StuiFrame.js — renders the fieldset, adds the legend only when caption is given'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-frame" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FrameConfigurator />
        </Section>
      )}

      {/* Search Builder */}
      {activeSection === 'search-builder' && (
        <Section title="Search Builder" description="The Advanced Search popup (components/common/AdvancedSearchModal.js), opened from the 6-dot menu of every list/datasheet page. It builds the list-search syntax visually: one frame per AND-group, an OR divider between groups, one row per condition (field | operator | value | remove). A specific field's value is a comma list meaning any of; quote an item to keep a comma literal. The page's search box stays the single source of truth — the popup parses whatever is in the box on open, a live preview shows the string each change produces, and Apply writes that string back. Syntax: terms AND; OR (uppercase) between groups; &quot;quoted phrase&quot;; -exclude; field:value with field:a,b for any of. Field prefixes accept the key or the column label with spaces removed. Parsing and building live in utils/listSearch.js (parseSearchQuery / buildSearchQuery), round-trip tested in listSearch.test.js." cssFile={CSS_LOCATIONS.searchBuilder.file} cssSelector={CSS_LOCATIONS.searchBuilder.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Live — the real popup</div>
          <div className="stui-row-10-wrap">
            <button className="stui-btn stui-btn-primary" onClick={() => setSbOpen(true)}><ListFilter size={14} /> Open Advanced Search</button>
            <span className="stui-hint">Search box holds: <code>{sbQuery || '(empty)'}</code> — edit it in the popup and Apply. Nothing is saved.</span>
          </div>
          <AdvancedSearchModal isOpen={sbOpen} onClose={() => setSbOpen(false)} query={sbQuery} fields={DEMO_SEARCH_FIELDS} onApply={setSbQuery} />
          <div className="stui-subsection-divider">Row and OR divider</div>
          <Example label=".stui-search-builder-row inside .stui-frame, then .stui-search-builder-or">
            <div>
              <StuiFrame caption="Match ALL of these">
                <div className="stui-search-builder-row">
                  <StuiSelect className="stui-field-input" defaultValue="owner">{DEMO_SEARCH_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}</StuiSelect>
                  <StuiSelect className="stui-field-input" defaultValue="contains"><option value="contains">contains</option><option value="not">does not contain</option></StuiSelect>
                  <input className="stui-field-input" defaultValue="josh, danny" />
                  <button type="button" className="stui-crud-btn" title="Remove condition"><X size={16} /></button>
                </div>
              </StuiFrame>
              <div className="stui-search-builder-or">OR</div>
            </div>
          </Example>
          <CSSRef file="STUI.css" selector=".stui-search-builder-row / .stui-search-builder-or" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / API', 'Role']} rows={[
            ['.stui-search-builder-row', 'Grid row: field 180px | operator 150px | value 1fr | remove 32px, gap sm, centered'],
            ['.stui-search-builder-or', 'Centered OR label on a horizontal rule, between frames'],
            ['<AdvancedSearchModal isOpen onClose query fields onApply>', 'fields = [{ key, label }] — every field the page searches; onApply(queryString)'],
            ['parseSearchQuery(q, fields, labels)', '→ { groups: [{ clauses: [{ field, values, negated }] }] } — OR of AND-groups'],
            ['buildSearchQuery(groups, labels)', 'Inverse of parse; writes label aliases, quotes only when needed'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-search-builder-row" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SearchBuilderConfigurator />
        </Section>
      )}

      {/* Buttons */}
      {activeSection === 'buttons' && (
        <Section title="Buttons" description="Base .stui-btn with .stui-btn-primary (filled teal), .stui-btn-secondary (ghost gray), .stui-btn-danger (filled red, destructive confirm) and .stui-btn-danger-subtle (gray outline that turns red on hover, for quiet destructive actions like removing a row). Add .stui-btn-icon for a square icon-only button. .stui-btn-link is a word inside a sentence that acts: no box, underlined, semibold, in the sentence's own font and colour. App.css live: .btn / .btn-primary / .btn-secondary" cssFile={CSS_LOCATIONS.buttons.file} cssSelector={CSS_LOCATIONS.buttons.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <Example label=".stui-btn.stui-btn-primary"><button className="stui-btn stui-btn-primary"><Download size={14} /> Download</button></Example>
            <Example label=".stui-btn.stui-btn-primary :disabled"><button className="stui-btn stui-btn-primary" disabled><Download size={14} /> Download</button></Example>
            <Example label=".stui-btn.stui-btn-secondary"><button className="stui-btn stui-btn-secondary">Cancel</button></Example>
            <Example label=".stui-btn.stui-btn-secondary :disabled"><button className="stui-btn stui-btn-secondary" disabled>Cancel</button></Example>
            <Example label=".stui-btn.stui-btn-danger"><button className="stui-btn stui-btn-danger"><Trash2 size={14} /> Delete</button></Example>
            <Example label=".stui-btn.stui-btn-danger-subtle.stui-btn-icon"><button className="stui-btn stui-btn-danger-subtle stui-btn-icon" title="Remove"><X size={16} /></button></Example>
            <Example label=".stui-btn.stui-btn-danger-subtle.stui-btn-icon :disabled"><button className="stui-btn stui-btn-danger-subtle stui-btn-icon" disabled title="Remove"><X size={16} /></button></Example>
            <Example label=".stui-btn-link (inside a warning notice)"><div className="stui-inline-notice stui-inline-notice-warning"><AlertTriangle size={13} /><span>gwa sync completed — switch back to review, or <button className="stui-btn-link">dismiss</button></span></div></Example>
            <CSSRef file="STUI.css" selector=".stui-btn / .stui-btn-primary / .stui-btn-secondary / .stui-btn-danger / .stui-btn-danger-subtle / .stui-btn-icon / .stui-btn-link" />
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Label','Variant','Context']} rows={[['Run Report','btn-primary','Report Preview — starts run'],['Running…','btn-primary','Report Preview — disabled while running'],['Download','btn-primary','File / export trigger'],['Apply','btn-primary','Filter and config panels'],['Save','btn-primary','Settings and forms'],['Add','btn-primary','Create / insert record'],['Cancel','btn-secondary','Dismiss dialog'],['Close','btn-secondary','Modal dismiss']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-btn" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.buttons} emptyMsg="No button labels found" />
          <Subsection label="Configurator" />
          <ButtonConfigurator />
        </Section>
      )}

      {/* Small Text */}
      {activeSection === 'small-text' && (
        <Section title="Small Text" description="The small-text family — captions, labels, hints and muted body copy. Reach for one of these before hand-rolling a font-size and colour inline. Label and Sub-Label have their own objects; this is the ladder they sit in." cssFile="STUI.css" cssSelector=".stui-text-sm-muted" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8" style={{ gap: 4 }}>
            {[
              ['stui-text-sm-muted', '12px, gray-500'],
              ['stui-text-placeholder', '12px, gray-300, italic'],
              ['stui-label', '13px, 600, gray-700, min-width 200px, nowrap'],
              ['stui-example-row-label', '11px, 600, gray-500, full width'],
              ['stui-sublabel', '11px, gray-500, margin-top 1px'],
              ['stui-label-xs', '11px, gray-500'],
              ['stui-label-note', '11px, gray-500, italic, margin-top 6px'],
              ['stui-label-xs-muted', '11px, gray-400'],
              ['stui-example-label', '10px, gray-400, monospace'],
              ['stui-hint', '10px, gray-400, margin-bottom 4px'],
              ['stui-c-muted', 'gray-400, colour only'],
              ['stui-text-error', 'error colour, colour only'],
              ['stui-text-warning', 'warning colour, colour only'],
              ['stui-text-primary', 'primary colour as text, colour only'],
              ['stui-text-xs', 'text-xs (12px), size only'],
              ['stui-text-muted', '--color-text-muted, colour only'],
              ['stui-fw-medium', 'weight 500'],
              ['stui-break-word', 'word-break: break-word — long strings wrap instead of overflowing'],
              ['stui-break-all', 'word-break: break-all — breaks anywhere, for file names and keys'],
              ['stui-truncate', 'one line, cut off with an ellipsis — needs a bounded width (cell, flex item, max-width)'],
            ].map(([cls, spec]) => (
              <div key={cls} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <code className="stui-ssg-cssref" style={{ display: 'inline-block', minWidth: 190, flexShrink: 0 }}>.{cls}</code>
                <span className={cls}>Sample text — {spec}</span>
              </div>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-text-sm-muted / .stui-label-xs / .stui-label-xs-muted / .stui-hint / .stui-c-muted" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Spec', 'Notes']} rows={[['.stui-text-sm-muted', '12px, gray-500', 'General muted body text'],['.stui-text-placeholder', '12px, gray-300, italic', 'Empty-state placeholder'],['.stui-label', '13px, 600, gray-700, min-width 200px, nowrap', 'Two conflicting definitions. Section 19 specifies 11px gray-600 nowrap for toolbar labels; Section 22 sets the form-row values, which win. Has its own object.'],['.stui-example-row-label', '11px, 600, gray-500, full width', 'Defined, used nowhere'],['.stui-sublabel', '11px, gray-500, margin-top 1px', 'Has its own object.'],['.stui-label-xs', '11px, gray-500', '.stui-sublabel without the margin'],['.stui-label-note', '11px, gray-500, italic, margin-top 6px', 'Inline caveat under a field'],['.stui-label-xs-muted', '11px, gray-400', 'Quieter .stui-label-xs'],['.stui-example-label', '10px, gray-400, monospace', 'Two definitions; the later adds text-align center and nowrap'],['.stui-hint', '10px, gray-400, margin-bottom 4px', 'Two definitions; the earlier supplies margin: 0, the later everything else'],['.stui-c-muted', 'gray-400, colour only', 'Recolours without touching size'],['.stui-text-error', 'error colour, colour only', 'Compose onto a text class for an error message rather than defining a per-class error variant — pair it with .stui-sublabel or .stui-label-note. A required-field asterisk uses .stui-required instead.']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-hint" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-sublabel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-text-sm-muted" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SmallTextConfigurator />
        </Section>
      )}
      {/* Sort Controls */}
      {activeSection === 'sort-controls' && (
        <Section title="Sort Controls" description="Toolbar chips and icon buttons used in grid headers and toolbars to show and reset active sort state. The sort rows inside a column header menu belong to the Column Filter element." cssFile={CSS_LOCATIONS.sortControls.file} cssSelector={CSS_LOCATIONS.sortControls.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="stui-hint">Sort state chip</div>
              <span className="stui-btn-sort-state">↑ Tag Name</span>
            </div>
            <div>
              <div className="stui-hint">Reset button</div>
              <button className="stui-btn-sort-reset">✕ Clear Sort</button>
            </div>
            <div>
              <div className="stui-hint">Sort chip</div>
              <span className="stui-sort-chip">↑ Name <span className="stui-sort-chip-index">1</span></span>
            </div>
            <div>
              <div className="stui-hint">Icon tab btn</div>
              <div className="stui-flex-4">
                <button className="stui-tab-btn-icon"><List size={14} /></button>
                <button className="stui-tab-btn-icon active"><LayoutGrid size={14} /></button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-btn-sort-state / .stui-btn-sort-reset / .stui-sort-chip / .stui-tab-btn-icon" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-btn-sort-state', 'Read-only chip showing active sort column — teal tinted'], ['.stui-btn-sort-reset', 'Clickable button to clear sort — amber tinted'], ['.stui-sort-chip', 'Compact sort indicator pill — teal bg, primary text'], ['.stui-sort-chip-index', 'Multi-sort priority badge — small teal circle inside chip'], ['.stui-tab-btn-icon', 'Icon-only toggle button — bordered, .active fills teal']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-btn-sort-state" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-tab-btn-icon" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SortControlsConfigurator />
        </Section>
      )}

      {/* Tab Bar */}
      {activeSection === 'tab-bar' && (
        <Section title="Tab Bar" description="Underline-style page/section tab navigation — the bar at the top of this page, Preferences, and Claude Notes. Distinct from .stui-tab-btn-icon (icon-only view toggles under Sort Controls)." cssFile="STUI.css" cssSelector=".stui-ssg-tab-bar" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-ssg-tab-bar" style={{ marginBottom: 8 }}>
            {['Overview', 'Elements', 'Popups'].map(t => (
              <button key={t} className={`stui-ssg-tab-btn${tabBarDemo === t ? ' active' : ''}`} onClick={() => setTabBarDemo(t)}>{t}</button>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-ssg-tab-bar / .stui-ssg-tab-btn" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-ssg-tab-bar', 'Horizontal tab container — bottom border, white bg, 24px side padding, flex-shrink 0'], ['.stui-ssg-tab-btn', 'Text tab — gray at rest, hover darkens, .active goes semibold primary with teal underline']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-ssg-tab-btn" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {/* Column Widths */}
      {activeSection === 'column-widths' && (
        <Section title="Column Widths" description="Utility span classes for the 24-column grid layout used in detail forms. Apply stui-col-N to any grid child element to span N of 24 columns. stui-delete-col is a fixed-width checkbox column for row-delete mode." cssFile={CSS_LOCATIONS.columnWidths.file} cssSelector={CSS_LOCATIONS.columnWidths.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Column span scale</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, marginBottom: 16, width: '100%' }}>
            {[1,2,3,4,6,8,12,16,18,20,24].map(n => (
              <div key={n} className={`stui-col-${n}`} style={{ background: 'var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '6px 4px', textAlign: 'center', color: 'white', fontSize: 10, fontWeight: 600, gridColumn: `span ${n}` }}>
                {n}
              </div>
            ))}
          </div>
          <div className="stui-subsection-divider">Delete column</div>
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="stui-data-grid stui-w-full">
              <thead>
                <tr>
                  <th className="stui-delete-col"><input type="checkbox" readOnly /></th>
                  <th>Tag Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="stui-delete-col"><input type="checkbox" readOnly /></td>
                  <td>AI-100</td><td>Analog Input</td>
                </tr>
                <tr>
                  <td className="stui-delete-col"><input type="checkbox" readOnly /></td>
                  <td>DI-201</td><td>Digital Input</td>
                </tr>
              </tbody>
            </table>
          </div>
          <CSSRef file="STUI.css" selector=".stui-col-1 … .stui-col-24 / .stui-delete-col" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'CSS', 'Notes']} rows={[['.stui-col-N', 'grid-column: span N', 'N = 1–24, applied to grid children inside 24-col form grid'], ['.stui-delete-col', 'width: 40px; text-align: center', 'Fixed checkbox column shown in delete mode on datasheet grids']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-col-1" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-delete-col" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ColumnWidthsConfigurator />
        </Section>
      )}

      {/* Action Menu */}
      {activeSection === 'access-colors' && (
        <Section title="Access Colors" description="Semantic color for a Grant / Deny permission value. Color only — pair with .stui-fw-semibold where the value should also read bold, so a cell can take the color without the weight. Applies to a value span, a select trigger, an option, or a table cell." cssFile={CSS_LOCATIONS.accessColors.file} cssSelector={CSS_LOCATIONS.accessColors.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <div className="stui-row-10">
              <span className="stui-hint">Value, bold</span>
              <span className="stui-fw-semibold stui-access-grant">Grant</span>
              <span className="stui-fw-semibold stui-access-deny">Deny</span>
              <span className="stui-fw-semibold stui-access-warning">Overridden</span>
            </div>
            <div className="stui-row-10">
              <span className="stui-hint">Color only</span>
              <span className="stui-access-grant">Granted globally</span>
              <span className="stui-access-deny">Denied globally</span>
              <span className="stui-access-warning">Overridden by a global</span>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-access-grant" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-access-grant', 'Success color for a granted permission — no weight of its own'], ['.stui-access-deny', 'Error color for a denied permission — no weight of its own'], ['.stui-access-warning', 'Warning color for a value that is outranked or overridden rather than plainly granted or denied'], ['.stui-data-grid td.stui-access-*', 'Scoped twin, because .stui-data-grid td sets its own color and outranks a lone utility class'], ['Pair with .stui-fw-semibold', 'Where the value should read bold. A source or provenance cell takes the color without it']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-access-grant" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'action-menu' && (
        <Section title="Action Menu" description="Six-dot GripVertical trigger + dropdown (devs call it the 6-dot). The dropdown sizes to its longest item between --action-menu-min-width (180px) and --action-menu-max-width (280px); items never wrap, and a label in a span truncates with an ellipsis past the max. Dividers: .stui-action-menu-divider (subtle) between groups, .stui-action-menu-divider-strong before the destructive group. Disabled items dim. The shared ActionMenu component renders these classes." cssFile={CSS_LOCATIONS.actionMenu.file} cssSelector={CSS_LOCATIONS.actionMenu.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-hint">Generic</div>
          <div className="stui-example-cell stui-row-end-mb"><DemoActionMenu /></div>
          <div className="stui-hint">Report / Export</div>
          <div className="stui-example-cell stui-row-end-mb"><DemoExportMenu /></div>
          <div className="stui-hint">Disabled</div>
          <div className="stui-example-cell stui-row-end-mb">
            <button className="stui-action-menu-btn disabled" disabled title="Disabled"><GripVertical size={16} /></button>
          </div>
          <CSSRef file="STUI.css" selector=".stui-action-menu-btn / .stui-action-menu-dropdown / .stui-action-menu-item / .stui-action-menu-item-danger / .stui-action-menu-divider[-strong]" />
          <Subsection label="Reference" />
          <RefTable headers={['Item','Class','Variant','Notes']} rows={[['Refresh','stui-action-menu-item','Generic',''],['Export Excel','stui-action-menu-item','Generic',''],['Modify View','stui-action-menu-item','Generic',''],['Delete','stui-action-menu-item-danger','Generic','Red — destructive'],['Separate Files','stui-action-menu-item','Report','Checkbox inside row'],['Download PDF','stui-action-menu-item','Report','FileText icon'],['Export Excel','stui-action-menu-item','Report','FileSpreadsheet icon'],['Export CSV','stui-action-menu-item','Report','FileDown icon'],['Cancel Export','stui-action-menu-item-danger','Report','Red — Ban icon'],['Clear Filters & Sort','stui-action-menu-item','Report','FilterX icon'],['Divider','stui-action-menu-divider','Any','Subtle line between groups'],['Divider (strong)','stui-action-menu-divider-strong','Any','Heavier line before the destructive group'],['Disabled item','stui-action-menu-item (disabled)','Any','Native disabled or .disabled: dimmed, no hover']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-action-menu" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.menuItems} emptyMsg="No menu items found" />
          <Subsection label="Configurator" />
          <ActionMenuConfigurator />
        </Section>
      )}

      {/* Select / Combobox */}
      {activeSection === 'select' && (
        <Section title="Select / Combobox" description="Every dropdown is a StuiSelect (drop-in for <select>: same props and <option> children) or a SearchSelect (options as data). Both draw the same trigger and the same STUI list; the browser's own select popup is never used, since its list ignores the theme and differs by browser. A raw <select> fails the pre-commit check. The control class sets the size: .stui-field-input for forms, .stui-select (shown here) for toolbars and option rows, .stui-ds-cell-input in datasheet rows. Lists over 10 options get a search box. Keyboard: Up/Down/Home/End change the value while closed, typing jumps to a match, Enter/Space/Alt+Down open, Escape closes." cssFile={CSS_LOCATIONS.select.file} cssSelector={CSS_LOCATIONS.select.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <Example label=".stui-select">
              <StuiSelect className="stui-select" defaultValue=""><option value="">-- None --</option><option value="panel">Panel</option><option value="io_type">I/O Type</option><option value="drawing">Drawing</option></StuiSelect>
            </Example>
            <Example label=".stui-select :disabled">
              <StuiSelect className="stui-select" disabled defaultValue=""><option value="">-- None --</option></StuiSelect>
            </Example>
            <CSSRef file="STUI.css" selector=".stui-select" />
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-select', 'Compact size — text-xs, 2px padding, auto width (as wide as its longest option); its list takes text-xs too'],
            ['.stui-field-input / .stui-field-input-xs', 'Form size, same height as the inputs beside it / compact header-bar size'],
            ['.stui-ds-cell-input', 'Datasheet row size — 26px, 12px text'],
            ['StuiSelect', 'components/common/StuiSelect.js — <select> markup, onChange(event), string values'],
            ['SearchSelect', 'components/common/SearchSelect.js — options as data, onChange(value); FK fields'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-select" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <TextInventory items={elementTexts?.selectOptions?.filter(o => o && !o.match(/^[-—\s]*(none|select|all)[-—\s]*$/i))} emptyMsg="No select options found" />
          <Subsection label="Configurator" />
          <SelectConfigurator />
        </Section>
      )}

      {/* Section Panels */}
      {activeSection === 'section-panels' && (
        <Section title="Section Panels" description="Collapsible card panels with numbered badge, h3 title, optional description, and body. All classes in STUI.css Section 14." cssFile={CSS_LOCATIONS.stepFamily.file} cssSelector={CSS_LOCATIONS.stepFamily.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">1</span>
                <h3>Report Configuration</h3>
                <span style={{ marginLeft: 'auto', color: 'var(--color-gray-500)' }}><ChevronDown size={16} /></span>
              </div>
              <p className="stui-step-desc">Select filter values to narrow report results.</p>
              <div style={{ padding: '8px 0', color: 'var(--color-gray-500)', fontSize: 11, fontStyle: 'italic' }}>…body content…</div>
            </div>
            <div className="stui-step-panel">
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Report Preview</h3>
                <button className="stui-btn stui-btn-primary stui-ml-auto-mr"><Play size={14} /> Run Report</button>
                <span className="stui-c-muted"><ChevronRight size={16} /></span>
              </div>
            </div>
            <CSSRef file="STUI.css" selector=".stui-step-panel / .stui-step-header / .stui-step-number / .stui-step-desc" />
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'File', 'Role']} rows={[['.stui-step-panel', 'STUI.css', 'Collapsible card shell — white bg, gray border, rounded, padding lg'], ['.stui-step-header', 'STUI.css', 'Flex row: badge + h3 + optional right controls'], ['.stui-step-number', 'STUI.css', 'Teal circle badge with step number'], ['.stui-step-desc', 'STUI.css', 'Subtitle paragraph — text-sm, gray-500'], ['.stui-steps-layout', 'STUI.css', 'Row of step panels sharing the width equally, gap lg; panels can shrink below their content, and the description wraps at 46ch']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-step-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div><div className="stui-hint">Panel titles (h3)</div><TextInventory items={elementTexts?.panelTitles} emptyMsg="No panel titles found" /></div>
            <div><div className="stui-hint">Descriptions</div><TextInventory items={elementTexts?.panelDescs} emptyMsg="No panel descriptions found" /></div>
          </div>
          <Subsection label="Configurator" />
          <SectionPanelConfigurator />
        </Section>
      )}

      {/* Data Grid */}
      {activeSection === 'data-grid' && (
        <Section title="Data Grid" description="Scrollable table with sticky headers, alternating rows, teal row hover. Clickable rows use .clickable. FilterableDataGrid is presentational (renders data as given, never filters) and its column header menus are the Column Filter element — see Column Filter for every filter variant. App.css live: .data-grid-container / .data-grid" cssFile={CSS_LOCATIONS.dataGrid.file} cssSelector={CSS_LOCATIONS.dataGrid.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ marginBottom: 4 }}>
            <MiniGrid />
            <CSSRef file="STUI.css" selector=".stui-data-grid-container / .stui-data-grid / .stui-data-grid th / .stui-data-grid td" />
          </div>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-data-grid-container', 'Outer wrapper — overflow hidden, border, rounded, shadow'], ['.stui-data-grid-scroll-area', 'Horizontal scroll wrapper inside the container'], ['.stui-data-grid', 'Table element — full width, collapse borders, alternating rows, sticky thead'], ['.stui-data-grid th', 'Header cell — primary bg, white text, resizable, position relative'], ['.stui-data-grid td', 'Data cell — text-sm, gray-700, left-aligned'], ['.stui-no-column-resize', 'Modifier — disables the column resize handle on th'], ['.stui-data-grid-auto', 'Modifier — table-layout: auto, so columns size to their content instead of splitting the width evenly. For small read-only grids with no resizable columns'], ['tr.stui-row-selected', 'Single-row selection — primary-light fill and a primary bar on the first cell, outranks zebra and hover. Set through FilterableDataGrid getRowClassName; the page action menu acts on that row']]} />
          <p className="stui-hint">Column header menus are the <strong>Column Filter</strong> element — its entry documents every class and variant.</p>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-data-grid-container" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-filter-dropdown" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DataGridConfigurator />
        </Section>
      )}

      {/* Column Filter */}
      {activeSection === 'column-filter' && (
        <Section title="Column Filter" description="The grid column header menu, on Excel's layout — ONE element for every variant: components/common/columnfilter/ColumnFilterMenu, used by FilterableDataGrid for every header. Sections switch on by column type: Sort (A to Z / Smallest to Largest / Oldest to Newest), Clear Filter From the column, a Text / Number / Date Filters flyout whose operators open the Custom Filter dialog (two conditions, And / Or), a Search box that auto-ticks exactly the matching values, and the value list — flat, or a Year > Month > Day tree for dates — with OK / Cancel. A column's type is inferred from its data (numbers, dates, else text) or set with filterType; filterable: false gives sort only and filterable: 'text' gives conditions without a value list. A column's filter is either the ticked values or a condition filter; the same JSON drives client-side grids (utils/listSearch) and server-paged ones (Services/Search). Big columns stay fast: values are computed once per open, ticks use a Set, and at most 300 checkboxes are drawn. Sort Controls (toolbar chips) is a separate element." cssFile={CSS_LOCATIONS.columnFilter.file} cssSelector={CSS_LOCATIONS.columnFilter.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <p className="stui-hint">Every menu below is the real component. Open the Filters flyout, pick an operator, tick values, press OK — nothing is saved.</p>
          <div className="stui-row-10-wrap">
            {COLUMN_FILTER_VARIANTS.map(v => (
              <Example key={v.id} label={v.label}>
                <LiveColumnFilter variant={v} />
              </Example>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-filter-dropdown / .stui-filter-menu-item / .stui-filter-flyout / .stui-filter-tree / .stui-filter-condition-row / .stui-filter-resize-grip" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / API', 'Role']} rows={[
            ['.stui-filter-dropdown (+ .dialog-open)', 'The menu container — fixed popup, white, shadowed, flex column. Opens 260px wide (wider only for a long row label, decided before paint) with a 7-row value list, capped to the viewport; .dialog-open hides the box behind the Custom Filter dialog'],
            ['.stui-filter-sort-section / .stui-filter-sort-btn', 'Sort rows (labels per type); .active marks the current sort'],
            ['.stui-filter-divider', 'Separator between sections and between flyout operator groups'],
            ['.stui-filter-menu-item (+ .disabled)', 'Full-width, non-wrapping menu row: Clear Filter From the column, the Filters flyout row, flyout operators'],
            ['.stui-filter-menu-chevron', 'Right-aligned ▸ on a row that opens a flyout'],
            ['.stui-filter-flyout (+ .flip-left)', 'Operator submenu beside the menu; flips left near the right screen edge'],
            ['.stui-filter-search', 'Value search box — typing auto-ticks exactly the matching values'],
            ['.stui-filter-options', 'Scrolling value area (flat list or the date tree)'],
            ['.stui-filter-option / .stui-filter-option-select-all', 'Checkbox row / the (Select All) row'],
            ['.stui-filter-option-label', 'Value text — truncates with … rather than widening the menu; the title shows it whole'],
            ['.stui-filter-resize-grip (+ .flip-left)', 'Bottom-right diagonal grip — drag to resize the menu for this opening (not remembered); .flip-left puts it bottom-left and grows the menu leftward when it sits against the right screen edge'],
            ['.stui-filter-tree / .stui-filter-tree-node (.l2 .l3) / .stui-filter-tree-toggle (.leaf)', 'Year > Month > Day tree: rows, indent per level, expand/collapse'],
            ['.stui-checkbox-input:indeterminate', 'Half-ticked tree parent (some children ticked)'],
            ['.stui-filter-footer / .stui-filter-ok-btn / .stui-filter-cancel-btn', 'OK / Cancel'],
            ['.stui-filter-text-body / -text-operator / -text-input', 'Custom Filter dialog: the conditions block, each operator select and value input'],
            ['.stui-filter-condition-row / .stui-filter-join', 'Dialog: operator + value(s) row / the And · Or pair'],
            ['<ColumnFilterMenu column type textOnly filter options optionsLoading optionsTruncated sortConfig onSort onApply onClose anchor inline>', 'The component — FilterableDataGrid renders it for the open header and passes the header rect as anchor; the menu places itself from its measured size'],
            ["column: filterType · filterable: false | 'text'", "Grid column props — type override; sort only; conditions only"],
            ['filter: string[] | { type, join, conds: [{ op, v, v2 }] }', 'Ticked values, or 1-2 typed conditions (utils/columnFilterTypes)'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-filter-dropdown" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ColumnFilterConfigurator />
        </Section>
      )}

      {/* Section Layout */}
      {activeSection === 'section-layout' && (
        <Section title="Section Layout" description="The style guide's own scaffolding: section cards, header bar, CSS chip, subsection dividers, example cells, and config output blocks." cssFile={CSS_LOCATIONS.sectionLayout.file} cssSelector={CSS_LOCATIONS.sectionLayout.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-hint">Section card + CSS chip</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Example label=".stui-section-card"><div className="stui-section-card" style={{ width: 140, maxHeight: 60, overflow: 'hidden' }}><div className="stui-section-header"><div className="stui-section-header-left"><h2 style={{ fontSize: 12 }}>Title</h2></div></div></div></Example>
            <Example label=".stui-css-chip"><span className="stui-css-chip" style={{ pointerEvents: 'none' }}>STUI.css : L724 ↗</span></Example>
          </div>
          <div className="stui-hint">Heading tiers — teal bar for the four standard sections, ruled divider for named variants inside a Preview</div>
          <div className="stui-detail-section-header stui-ssg-subsection">Reference</div>
          <div className="stui-subsection-divider">Report / Export</div>
          <div className="stui-hint">Example cell</div>
          <div className="stui-example-cell" style={{ width: 180 }}>Demo content here</div>
          <div className="stui-hint">Config output</div>
          <div className="stui-config-output">stui-btn-primary · Download icon · label "Download" · enabled</div>
          <div className="stui-hint">Placeholder — a tab with nothing documented yet</div>
          <Example label=".stui-ssg-placeholder">
            <div className="stui-ssg-placeholder">
              <div className="stui-ssg-placeholder-icon">🚧</div>
              <div className="stui-ssg-placeholder-title">Tab Name</div>
              <div className="stui-ssg-placeholder-sub">This template is being documented.</div>
            </div>
          </Example>
          <CSSRef file="STUI.css" selector=".stui-section-card / .stui-css-chip / .stui-css-chip.static / .stui-detail-section-header / .stui-subsection-divider / .stui-example-cell / .stui-config-output / .stui-ssg-placeholder" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-section-card', 'Outer card wrapping one element section'], ['.stui-css-chip', 'Clickable CSS location chip in the card header'], ['.stui-css-chip.static', 'Display-only chip: no hover, no pointer. Used off dev VMs, where the CSS source cannot be opened'], ['.stui-detail-section-header', 'Teal filled bar — the four standard section headings, rendered by &lt;Subsection&gt;'], ['.stui-subsection-divider', '9px uppercase ruled label — named variants inside a Preview. Left empty it is a plain full-width rule, e.g. above a collapsible History block'], ['.stui-example-cell', 'Gray bordered cell holding a single demo'], ['.stui-config-output', 'Monospace block echoing a configurator&apos;s current output'], ['.stui-ssg-placeholder', 'Stands in for a tab with nothing documented yet — 48px padding, centred, gray-400. Wraps the three below, in this order. No tab uses it at present; copy the Preview markup to stand a new one up'], ['.stui-ssg-placeholder-icon', '32px glyph above the title'], ['.stui-ssg-placeholder-title', '15px semibold gray-500 — the tab name'], ['.stui-ssg-placeholder-sub', '13px line saying why the tab is empty']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-section-card" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SectionLayoutConfigurator />
        </Section>
      )}

      {/* Module Header */}
      {activeSection === 'module-header' && (
        <Section title="Module Header" description="Full-width header bar with icon, title, and meta count on the left; plus, pencil/save, and action menu on the right." cssFile={CSS_LOCATIONS.moduleHeader.file} cssSelector={CSS_LOCATIONS.moduleHeader.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          {previewThemes.map(theme => (
            <div key={theme.themeKey} style={{ marginBottom: 8 }}>
              <div className="stui-subsection-divider">{theme.label}{theme.status !== 'Active' ? ` (${theme.status})` : ''}</div>
              <div className="stui-preview-zone stui-theme-preview" style={{ ...previewStyle(theme.tokens), border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)'}}>
                <div className="stui-module-header-styleguideonly">
                  <div className="stui-module-header-left">
                    <Baby size={20} className="stui-page-icon" />
                    <h1>Module Title</h1>
                    <span className="stui-module-header-meta">42 records</span>
                  </div>
                  <div className="stui-module-header-actions">
                    <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
                    <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
                    <button className="stui-crud-btn" disabled title="Save"><Save size={16} /></button>
                    <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <CSSRef file="STUI.css" selector=".stui-module-header / .stui-module-header-left / .stui-module-header-actions / .stui-crud-btn" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-module-header', 'Outer bar — flex, space-between, bottom border'], ['.stui-module-header-left', 'Icon + title + meta group (flex row, gap-lg)'], ['.stui-module-header-left h1', 'Title — text-lg, primary color, no margin'], ['.stui-module-header-meta', 'Record count / subtitle — text-sm, gray-500'], ['.stui-module-header-actions', 'Right side button group (flex row, gap-sm)'], ['.stui-crud-btn', '32×32 circular icon button — primary color'], ['.stui-crud-btn:disabled', 'Gray-out — 50% opacity, not-allowed cursor'], ['.stui-crud-btn.active', 'On while its option is in effect (a filter applied) — primary border and icon']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-module-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModuleHeaderConfigurator />
        </Section>
      )}

      {activeSection === 'boolean' && (
        <Section title="Boolean Display" description="Read-only boolean cell used inside datasheet grids. A centered container with an optional success-colored check icon." cssFile={CSS_LOCATIONS.booleanDisplay.file} cssSelector={CSS_LOCATIONS.booleanDisplay.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="stui-hint">True</div>
              <div className="stui-boolean-display">
                <span className="stui-boolean-check">✓</span>
              </div>
            </div>
            <div>
              <div className="stui-hint">False</div>
              <div className="stui-boolean-display" />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-boolean-display / .stui-boolean-check" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-boolean-display', 'Flex container — centered, min-height 20px'], ['.stui-boolean-check', 'Check mark — success color']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-boolean-display" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BooleanDisplayConfigurator />
        </Section>
      )}

      {activeSection === 'breadcrumb' && (
        <Section title="Breadcrumb" description="Inline navigation trail showing parent context and current location. Use inside a flex row." cssFile={CSS_LOCATIONS.breadcrumb.file} cssSelector={CSS_LOCATIONS.breadcrumb.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span className="stui-breadcrumb-parent">Projects</span>
            <span className="stui-breadcrumb-arrow"> › </span>
            <span className="stui-breadcrumb-parent">BU28</span>
            <span className="stui-breadcrumb-arrow"> › </span>
            <span className="stui-breadcrumb-current">Instrumentation</span>
          </div>
          <CSSRef file="STUI.css" selector=".stui-breadcrumb / .stui-breadcrumb-parent / .stui-breadcrumb-arrow / .stui-breadcrumb-current" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-breadcrumb', 'Container — font-primary, text-sm, weight 500'], ['.stui-breadcrumb-parent', 'Ancestor segment — opacity 0.7'], ['.stui-breadcrumb-arrow', 'Separator character — opacity 0.6, margin 0 2px'], ['.stui-breadcrumb-current', 'Active segment — font-weight 600']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-breadcrumb" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BreadcrumbConfigurator />
        </Section>
      )}

      {activeSection === 'badge' && (
        <Section title="Badge" description="Inline pill. The class names a COLOUR, the visible label carries the meaning — one family, no per-feature variant sets. One colour class is always enough; never pair a base with a variant. The only add-on is the size modifier .stui-badge-xs for compact list rows. An icon is optional: put a Lucide icon at size 12 before the label and the badge centres it with a gap, the same way a button does." cssFile={CSS_LOCATIONS.badge.file} cssSelector={CSS_LOCATIONS.badge.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">The palette</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-green">green</span>
            <span className="stui-badge-amber">amber</span>
            <span className="stui-badge-orange">orange</span>
            <span className="stui-badge-red">red</span>
            <span className="stui-badge-blue">blue</span>
            <span className="stui-badge-purple">purple</span>
            <span className="stui-badge-teal">teal</span>
            <span className="stui-badge-indigo">indigo</span>
            <span className="stui-badge-gray">gray</span>
            <span className="stui-badge-primary">primary</span>
            <span className="stui-badge-secondary">secondary</span>
          </div>
          <div className="stui-subsection-divider">In use</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-green">complete</span>
            <span className="stui-badge-orange">in_progress</span>
            <span className="stui-badge-blue">validate</span>
            <span className="stui-badge-purple">retry</span>
            <span className="stui-badge-red">CRIT</span>
            <span className="stui-badge-indigo">Core</span>
            <span className="stui-badge-teal">Refactor</span>
            <span className="stui-badge-red">Bug</span>
          </div>
          <div className="stui-subsection-divider">Compact (xs)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-primary stui-badge-xs">MAJ</span>
            <span className="stui-badge-secondary stui-badge-xs">MIN</span>
            <span className="stui-badge-indigo stui-badge-xs">PAT</span>
            <span className="stui-badge-green stui-badge-xs">PROD</span>
          </div>
          <div className="stui-subsection-divider">Semantic aliases</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-accepted">Accepted</span>
            <span className="stui-badge-expired">Expired</span>
            <span className="stui-badge-none">Not Invited</span>
            <span className="stui-badge-pending">Invite Sent</span>
            <span className="stui-badge-construction">Construction</span>
          </div>
          <div className="stui-subsection-divider">Audit Trail</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="stui-badge-audit stui-badge-audit-create">CREATE</span>
            <span className="stui-badge-audit stui-badge-audit-update">UPDATE</span>
            <span className="stui-badge-audit stui-badge-audit-delete">DELETE</span>
            <span className="stui-badge-audit stui-badge-audit-login">LOGIN</span>
            <span className="stui-badge-audit stui-badge-audit-security">Security</span>
            <span className="stui-badge-audit stui-badge-audit-config">Config</span>
            <span className="stui-badge-audit stui-badge-audit-preference">Preference</span>
            <span className="stui-badge-audit stui-badge-audit-auth">Auth</span>
          </div>
          <CSSRef file="STUI.css" selector=".stui-badge-accepted" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'File', 'Appearance']}
            rows={[
              ['.stui-badge', 'STUI.css', 'Shape only — rounded pill, 11px bold. No colour.'],
              ['.stui-badge-green', 'STUI.css', '#dcfce7 bg / #166534 text — complete, Infrastructure'],
              ['.stui-badge-amber', 'STUI.css', '#fef9c3 bg / #92400e text — pending, MED, Milestone'],
              ['.stui-badge-orange', 'STUI.css', '#fed7aa bg / #9a3412 text — in_progress, HIGH, DevOps'],
              ['.stui-badge-red', 'STUI.css', '#fee2e2 bg / #b91c1c text — CRIT, Bug, Security'],
              ['.stui-badge-blue', 'STUI.css', '#dbeafe bg / #1e40af text — validate, Feature entry, UI'],
              ['.stui-badge-purple', 'STUI.css', '#ede9fe bg / #5b21b6 text — retry, Feature step'],
              ['.stui-badge-teal', 'STUI.css', '#ccfbf1 bg / #0f766e text — Refactor'],
              ['.stui-badge-indigo', 'STUI.css', '#e0e7ff bg / #4338ca text — Core'],
              ['.stui-badge-gray', 'STUI.css', 'var(--color-gray-100) bg / var(--color-gray-500) text, follows the theme — Not Started, LOW, Task, untriaged'],
              ['.stui-badge-primary', 'STUI.css', 'var(--color-primary) bg / white text, follows the theme — MAJOR revision'],
              ['.stui-badge-secondary', 'STUI.css', 'var(--color-primary-light) bg / var(--color-primary) text, follows the theme — the lighter partner of primary: MINOR revision'],
              ['.stui-badge-xs', 'STUI.css', <>Size only — 9px, 1px 4px. Always paired with one colour class, never alone: rev log list rows (<code className="stui-mono">stui-badge-primary stui-badge-xs</code>).</>],
              ['.stui-badge-accepted', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-green</code></>],
              ['.stui-badge-expired', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-gray</code></>],
              ['.stui-badge-none', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-red</code></>],
              ['.stui-badge-pending', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-amber</code></>],
              ['.stui-badge-construction', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-orange</code></>],
              ['.stui-badge-view', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-blue</code></>],
              ['.stui-badge-query', 'STUI.css', <>Alias of <code className="stui-mono">.stui-badge-purple</code></>],
              ['.stui-badge-audit', 'STUI.css', <>Audit base — 10px bold, 1px 6px, radius-sm, flex-shrink 0. No color. <strong>10px is deliberate, not var(--text-xs)=12px</strong> — these sit inline in dense grid rows.</>],
              ['.stui-badge-audit-create', 'STUI.css', 'Green — #dcfce7 bg / #16a34a text'],
              ['.stui-badge-audit-update', 'STUI.css', 'Blue — #dbeafe bg / #2563eb text'],
              ['.stui-badge-audit-delete', 'STUI.css', 'Red — #fee2e2 bg / #dc2626 text'],
              ['.stui-badge-audit-login', 'STUI.css', 'Purple — #f3e8ff bg / #7e22ce text'],
              ['.stui-badge-audit-security', 'STUI.css', 'Amber — #fef3c7 bg / #b45309 text (Category)'],
              ['.stui-badge-audit-config', 'STUI.css', 'Indigo — #e0e7ff bg / #4338ca text (Category)'],
              ['.stui-badge-audit-preference', 'STUI.css', 'Teal — #ccfbf1 bg / #0f766e text (Category)'],
              ['.stui-badge-audit-auth', 'STUI.css', 'Purple — #f3e8ff bg / #7e22ce text (Category)'],
            ]}
          />
          <div className="stui-text-placeholder" style={{ marginBottom: 12, fontSize: 12 }}>
            Colour badges are self-contained — one class, never a base plus a variant.
            Pages map meaning to colour in a single helper
            (<code className="stui-mono">getStatusClass</code>,{' '}
            <code className="stui-mono">getTypeClass</code>,{' '}
            <code className="stui-mono">getPriorityClass</code>) that returns a colour
            class and falls back to <code className="stui-mono">.stui-badge-gray</code>,
            so an unrecognised value still renders as a badge.
            The semantic aliases exist because some call sites build the class name from
            stored data — a lookup category renders{' '}
            <code className="stui-mono">.stui-badge-&#36;&#123;cat.type&#125;</code> — where
            renaming would break silently. Prefer the colour name in new code.
            Audit badges are the one exception: they remain a base plus a colour variant,
            built by a one-line <code className="stui-mono">auditBadge(value)</code> helper
            on both audit pages.
          </div>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-badge-accepted" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <BadgeConfigurator />
        </Section>
      )}

      {activeSection === 'tile' && (
        <Section title="Tile" description="Dashboard tile card — white bg, border, rounded corners, hover lift with teal border. Sub-elements: header (icon + title), content area, animated legend." cssFile={CSS_LOCATIONS.tile.file} cssSelector={CSS_LOCATIONS.tile.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="stui-tile stui-clickable" style={{ width: 220 }}>
              <div className="stui-tile-header">
                <FileText size={16} className="stui-tile-icon" />
                <span className="stui-tile-title">Drawing Log</span>
              </div>
              <div className="stui-tile-content stui-text-placeholder">
                …chart content…
              </div>
              <div className="stui-tile-legend">
                <span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span>
                <span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: '#6b7280' }} />Series B</span>
              </div>
            </div>
            <div className="stui-tile" style={{ width: 220 }}>
              <div className="stui-tile-header">
                <FileText size={16} className="stui-tile-icon" />
                <span className="stui-tile-title">Not Clickable</span>
              </div>
              <div className="stui-tile-content stui-text-placeholder">
                …chart content…
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-tile" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-tile', 'Outer card — white bg, border, rounded, hover lift'], ['.stui-tile.clickable', 'Pointer cursor (explicit opt-in)'], ['.stui-tile:not(.clickable)', 'Default cursor, no hover lift'], ['.stui-tile-header', 'Flex row: icon + title, bottom border'], ['.stui-tile-icon', 'Primary color icon'], ['.stui-tile-title', 'text-sm, bold, gray-700'], ['.stui-tile-content', 'Centered flex area — chart goes here'], ['.stui-tile-legend', 'Animated legend — hidden, fades in on hover after 0.6s'], ['.stui-legend-item', 'Dot + label row'], ['.stui-legend-dot', '8px circle — color set inline per series']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-tile" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <TileConfigurator />
        </Section>
      )}

      {activeSection === 'stat-row' && (
        <Section title="Stat Row" description="Horizontal strip of KPI stats separated by a top border. Each stat is a label (9px uppercase) + value (13px bold) pair. Used in report toolbars." cssFile={CSS_LOCATIONS.statRow.file} cssSelector={CSS_LOCATIONS.statRow.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-stat-row">
            <div className="stui-stat-group">
              {[
                { label: 'Rows',   value: '1,284' },
                { label: 'Pages',  value: '43' },
                { label: 'Layout', value: 'tabloid' },
                { label: 'Time',   value: '312ms' },
              ].map(({ label, value }) => (
                <div key={label} className="stui-stat-item">
                  <span className="stui-stat-label">{label}</span>
                  <span className="stui-stat-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-stat-row" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-stat-row', 'Outer strip — top border, flex wrap, gap 16px'], ['.stui-stat-group', 'Inner flex group — gap 24px between stat items'], ['.stui-stat-item', 'Label + value pair — baseline aligned, gap 5px'], ['.stui-stat-label', '9px bold uppercase gray-400 — the KPI name'], ['.stui-stat-value', '13px semibold gray-700 — the KPI value']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-stat-row" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <StatRowConfigurator />
        </Section>
      )}

      {activeSection === 'label' && (
        <Section title="Label" description="Inline field label — 11px semibold, gray-600, no-wrap. Use alongside selects and inputs in toolbar option rows." cssFile={CSS_LOCATIONS.label.file} cssSelector={CSS_LOCATIONS.label.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="stui-label">Sort By:</span>
            <StuiSelect className="stui-select"><option>Column A</option></StuiSelect>
            <span className="stui-label">Group By:</span>
            <StuiSelect className="stui-select"><option>Column B</option></StuiSelect>
          </div>
          <CSSRef file="STUI.css" selector=".stui-label" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-label', '11px semibold gray-600, no-wrap — toolbar field label']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-label" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <LabelConfigurator />
        </Section>
      )}

      {activeSection === 'checkbox' && (
        <Section title="Checkbox" description="Inline checkbox + label row. Apply to a <label> element wrapping an <input type=checkbox>. Flex, gap-6, text-xs, gray-600, pointer cursor." cssFile={CSS_LOCATIONS.checkbox.file} cssSelector={CSS_LOCATIONS.checkbox.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-checkbox"><input type="checkbox" defaultChecked /> Show QR Code in report header</label>
            <label className="stui-checkbox"><input type="checkbox" /> Include cover page</label>
            <label className="stui-checkbox"><input type="checkbox" defaultChecked /> Separate sheets per group</label>
          </div>
          <CSSRef file="STUI.css" selector=".stui-checkbox" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-checkbox', 'On &lt;label&gt; — flex row, gap-6, text-xs, gray-600, pointer, no select'], ['.stui-checkbox + .stui-checkbox-input', 'A pair, not alternatives — .stui-checkbox is the wrapper row (box + text); .stui-checkbox-input styles the box itself. Use the wrapper even when the row has no text'], ['.stui-field-group &gt; label + .stui-checkbox', 'Under a field label in a form grid, takes --input-height so the checkbox centers against neighbouring inputs']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-checkbox" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <CheckboxConfigurator />
        </Section>
      )}

      {activeSection === 'checkbox-input' && (
        <Section title="Checkbox Input" description="Fully custom-styled native checkbox replacing App.css .themed-checkbox. Apply stui-checkbox-input directly to <input type=checkbox>. Use inside a .stui-checkbox label wrapper." cssFile={CSS_LOCATIONS.checkboxInput.file} cssSelector={CSS_LOCATIONS.checkboxInput.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" defaultChecked /> Show QR Code in report header</label>
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" /> Include cover page</label>
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input" disabled defaultChecked /> Disabled checked</label>
            <label className="stui-checkbox"><input type="checkbox" className="stui-checkbox-input stui-checkbox-input-danger" defaultChecked /> Danger variant — red when selected</label>
          </div>
          <CSSRef file="STUI.css" selector=".stui-checkbox-input" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / State', 'Role']} rows={[['.stui-checkbox-input', 'On &lt;input type=checkbox&gt; — 1rem × 1rem, gray border, white bg, rounded-3'], ['.stui-checkbox-input:checked', 'Teal fill + border-color — matches --color-primary'], ['.stui-checkbox-input:checked::after', 'White checkmark drawn via border transform trick'], ['.stui-checkbox-input:focus-visible', '2px teal outline for keyboard nav accessibility'], ['.stui-checkbox-input:disabled', 'Grey fill + lighter border, not-allowed cursor — appearance:none means this must be drawn, not inherited'], ['.stui-checkbox-input:disabled:checked', 'Grey fill keeps the white check — on, but locked'], ['.stui-checkbox-input-danger', 'Variant — red fill when selected, for destructive pickers. Pair with the base class; unchecked and disabled are unchanged']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-checkbox-input" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <CheckboxInputConfigurator />
        </Section>
      )}

      {activeSection === 'checkbox-list' && (
        <Section title="Checkbox List" description="Scrollable multi-select list: a bordered container of label rows, each pairing a checkbox with a monospace name and a muted secondary count. Used where a set of records or keys is picked in bulk — copy pickers and label libraries. Put stui-checkbox-input on the input; the row supplies the layout." cssFile={CSS_LOCATIONS.checkboxList.file} cssSelector={CSS_LOCATIONS.checkboxList.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <div className="stui-checkbox-list">
              <label className="stui-checkbox-list-item">
                <input type="checkbox" className="stui-checkbox-input" defaultChecked />
                <span className="stui-checkbox-list-item-name">DINT</span>
                <span className="stui-checkbox-list-item-count">12 members</span>
              </label>
              <label className="stui-checkbox-list-item">
                <input type="checkbox" className="stui-checkbox-input" />
                <span className="stui-checkbox-list-item-name">REAL</span>
                <span className="stui-checkbox-list-item-count">4 members</span>
              </label>
              <label className="stui-checkbox-list-item">
                <input type="checkbox" className="stui-checkbox-input" defaultChecked />
                <span className="stui-checkbox-list-item-name">BOOL</span>
                <span className="stui-checkbox-list-item-count">1 member</span>
              </label>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-checkbox-list / .stui-checkbox-list-item" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-checkbox-list', 'Scrollable container — max-height 300px, bordered, radius-sm'], ['.stui-checkbox-list-item', 'Row, on a &lt;label&gt; — flex, gap-sm, 7px/md padding, pointer, bottom divider'], ['.stui-checkbox-list-item input', '1rem box that will not shrink — pair with .stui-checkbox-input'], ['.stui-checkbox-list-item-name', 'Primary text — monospace, 600 weight, text-xs'], ['.stui-checkbox-list-item-count', 'Secondary count — gray-500, text-xs']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-checkbox-list-item" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'popover' && (
        <Section title="Popover" description="Small panel anchored under a trigger button, for a date range, a quick filter or a short form. Wrap the trigger and the panel in .stui-popover-anchor. The page owns open/close and click-outside; the classes only draw it." cssFile={CSS_LOCATIONS.popover.file} cssSelector={CSS_LOCATIONS.popover.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <PopoverPreview />
          <CSSRef file="STUI.css" selector=".stui-popover-anchor / .stui-popover / .stui-popover-left / .stui-popover-footer" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-popover-anchor', 'Wrapper around the trigger button and the panel — position: relative'], ['.stui-popover', 'The panel: opens below the anchor, right-aligned, white card with border, radius-md and shadow-md, column layout with spacing-md gap'], ['.stui-popover.stui-popover-left', 'Left-aligns the panel, for a trigger near the left edge'], ['.stui-popover-footer', 'Right-aligned button row at the bottom of the panel']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-popover" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'status-dot' && (
        <Section title="Status Dot" description="Small circle at the start of a status line. Gray while idle; add .stui-status-dot-live while something is running or polling. Pair it with a short text label — the dot alone does not say what is running." cssFile={CSS_LOCATIONS.statusDot.file} cssSelector={CSS_LOCATIONS.statusDot.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <div className="stui-row-10"><span className="stui-status-dot stui-status-dot-live" /> Polling every 10s</div>
            <div className="stui-row-10"><span className="stui-status-dot" /> Not polling</div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-status-dot / .stui-status-dot-live" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-status-dot', '8px circle, gray — idle / not running'], ['.stui-status-dot.stui-status-dot-live', 'Primary colour with a slow pulse — running, polling, in flight']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-status-dot" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'layout-utilities' && (
        <Section title="Layout Utilities" description="Single-purpose rows and margins for laying out small groups of items: icon and label, a label beside its controls, stacked field groups. Use these before writing display: flex or a margin inline. Gaps use the spacing tokens; the scale has no 6px step." cssFile={CSS_LOCATIONS.layoutUtilities.file} cssSelector={CSS_LOCATIONS.layoutUtilities.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8" style={{ maxWidth: 460 }}>
            <div className="stui-row-8 stui-mb-md"><Check size={14} /> <span>.stui-row-8 — icon and label</span></div>
            <div className="stui-row-between stui-mb-md"><span>.stui-row-between</span><button className="stui-btn stui-btn-secondary stui-btn-sm">Action</button></div>
            <div className="stui-field-group stui-mb-md"><label>Field with .stui-mb-md</label><input className="stui-field-input" placeholder="12px below" /></div>
            <div className="stui-field-group"><label>Next field</label><input className="stui-field-input" /></div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-row-4 / .stui-row-8 / .stui-row-md / .stui-row-between / .stui-mb-md / .stui-mb-lg" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-row-4', 'Row: flex, centred, 4px gap'], ['.stui-row-8', 'Row: flex, centred, spacing-sm (8px) gap'], ['.stui-row-md', 'Row: flex, centred, spacing-md (12px) gap'], ['.stui-row-10', 'Row: flex, centred, 10px gap'], ['.stui-row-10-wrap / .stui-row-12-wrap', 'Row that wraps, 10px / 12px gap'], ['.stui-row-between', 'Row with content pushed to both ends: flex, centred, space-between, spacing-sm gap. A label or summary left, controls right'], ['.stui-row-end-mb', 'Row aligned right, 6px bottom margin'], ['.stui-flex-4 / .stui-flex-10', 'Flex with a 4px / 10px gap and no vertical centring'], ['.stui-mb-xs / -sm / -md / -lg', 'Bottom margin by spacing token: 4 / 8 / 12 / 16px, e.g. between stacked .stui-field-group rows'], ['.stui-mt-xs / -sm / -md / -lg', 'Top margin by spacing token: 4 / 8 / 12 / 16px'], ['.stui-mr-xs / -sm / -md / -lg', 'Right margin by spacing token: 4 / 8 / 12 / 16px, e.g. the space between an icon and its label'], ['.stui-ml-xs / -sm / -md / -lg / -xl', 'Left margin by spacing token: 4 / 8 / 12 / 16 / 24px, e.g. indenting a line under a checkbox label'], ['.stui-divided-list', 'Stack whose children are separated by a thin border-subtle rule, with xs spacing either side; the first child has none'], ['.stui-scroll-cap', 'max-height 460px, scrolls — for a block that can grow without limit'], ['.stui-results-panel-header.stui-results-panel-header-warning', 'Warning-light header bar on a results panel whose contents need review, e.g. drift warnings'], ['.stui-code-block', 'Read-only code (SQL, DDL): gray-50 box, mono xs, wraps long lines, scrolls past 320px'], ['.stui-inner-table tr.stui-row-tone-error / -warning', 'Row tones: error-light tint with a 3px error bar on the left edge, or a warning-light tint — a row the reader must not skim past'], ['.stui-ml-auto', 'margin-left: auto — pushes an item to the far end of its flex row'], ['.stui-flex-1 / .stui-min-w-0 / .stui-shrink-0', 'Flex-item sizing: grow to fill, allow shrinking below content width, never shrink'], ['.stui-hidden', 'display: none — e.g. the native file input behind a custom drop zone'], ['.stui-mb-20', 'Bottom margin of 20px'], ['.stui-m-0', 'Margin 0']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-row-between" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'segmented' && (
        <Section title="Segmented Control" description="Two or three mutually exclusive options shown as equal-width buttons sharing the row. Mark the chosen button .selected. For more than three options use a select." cssFile={CSS_LOCATIONS.segmented.file} cssSelector={CSS_LOCATIONS.segmented.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <SegmentedPreview />
          <CSSRef file="STUI.css" selector=".stui-segmented / .stui-segmented-btn / .stui-segmented-btn.selected" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-segmented', 'Row of options — flex, spacing-sm gap'], ['.stui-segmented-btn', 'One option — equal width (flex: 1), 2px gray-200 border, white fill, 600 weight'], ['.stui-segmented-btn.selected', 'The chosen option — primary fill and border, white text'], ['.stui-segmented-btn:disabled', 'Half opacity, not-allowed cursor']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-segmented" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'status-text' && (
        <Section title="Status Text" description="A line of text with an optional leading icon, coloured by tone. No box — for a contained note in a modal body use .stui-inline-notice. Put a Lucide icon at size 13-14 before the text; the line centres it with a gap." cssFile={CSS_LOCATIONS.statusText.file} cssSelector={CSS_LOCATIONS.statusText.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <div className="stui-row-10"><span className="stui-status-text stui-status-text-xs"><Info size={13} /> Last synced 4 minutes ago</span></div>
            <div className="stui-row-10"><span className="stui-status-text stui-status-text-warning stui-status-text-xs"><AlertTriangle size={13} /> 3 new values need assignment</span></div>
            <div className="stui-row-10"><span className="stui-status-text stui-status-text-error"><AlertTriangle size={14} /> No ConnectionStrings section found</span></div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-status-text / .stui-status-text-warning / .stui-status-text-error / .stui-status-text-xs" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-status-text', 'Inline-flex line: optional leading icon, spacing-xs gap, gray-500 text, inherits its font size'], ['.stui-status-text.stui-status-text-warning', 'Amber — something needs attention before the user can continue'], ['.stui-status-text.stui-status-text-error', 'Red — something is broken or blocked'], ['.stui-status-text.stui-status-text-xs', '12px, for toolbar and footer lines']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-status-text" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <StatusTextConfigurator />
        </Section>
      )}

      {activeSection === 'inner-table' && (
        <Section title="Inner Table" description="A light, compact table for data nested inside a panel, popup or results block: sync history, diff details, small config lists. No zebra and no hover; rows are separated by a border-subtle line. Use .stui-data-grid instead for a page's main grid with sorting, filtering or resizable columns. Width classes are fixed pixels; with none, the table sizes its columns to their content." cssFile={CSS_LOCATIONS.innerTable.file} cssSelector={CSS_LOCATIONS.innerTable.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <table className="stui-inner-table">
              <thead><tr><th>Path</th><th>Source</th><th>Target</th></tr></thead>
              <tbody>
                <tr><td><code>Test.Path.One</code></td><td className="stui-inner-table-cell-mono">test</td><td className="stui-inner-table-cell-mono">test</td></tr>
                <tr className="stui-row-tone-error stui-fw-semibold">
                  <td><code>Test.Path.Two</code><span className="stui-text-error stui-ml-xs" title="Permission or access field"><ShieldAlert size={12} /></span></td>
                  <td className="stui-inner-table-cell-mono">test</td><td className="stui-inner-table-cell-mono">test</td>
                </tr>
                <tr className="stui-row-tone-warning">
                  <td><code>Test.Path.Three</code><span className="stui-text-warning stui-ml-xs" title="Matched by position"><AlertTriangle size={12} /></span></td>
                  <td className="stui-inner-table-cell-mono">test</td><td className="stui-inner-table-cell-mono">test</td>
                </tr>
              </tbody>
            </table>
          </div>
          <CSSRef file="STUI.css" selector=".stui-inner-table / .stui-row-tone-error / .stui-row-tone-warning / .stui-inner-table-*" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-inner-table', 'On &lt;table&gt; — full width, collapsed borders, text-sm'],
            ['thead tr / th', 'bg-subtle header row with a border-bottom; th is 600 weight, left-aligned, padding 6/8'],
            ['tbody tr / td', 'Each row has a border-subtle bottom line; td padding 6/8. No zebra, no hover'],
            ['tr.stui-row-tone-error', 'error-light tint with a 3px error bar on the first cell — a row the reader must not skim past'],
            ['tr.stui-row-tone-warning', 'warning-light tint — a row that may be inexact'],
            ['.stui-inner-table-cell-mono', 'Monospace text-xs cell, for values and keys'],
            ['.stui-inner-table-cell-actions', 'Flex row of icon buttons in a cell, 4px gap'],
            ['tr.stui-inner-table-edit-row', 'Tighter 4/6 cell padding for a row holding inputs'],
            ['tr.stui-inner-table-add-row', 'bg-subtle row for adding a new entry'],
            ['tr.stui-inner-table-empty', 'Centred italic gray-500 empty state, padding 12 — use one td with colSpan'],
            ['.stui-inner-table-col-actions / -col-sm', 'Fixed column widths: 64px actions, 240px'],
            ['.stui-inner-table-input-xs / -input-sm', 'Inputs in a cell: 80px / full width'],
            ['.stui-inner-table-section / -section-header', 'Spacing above a stacked table (16px) / a space-between title row over it'],
            ['Alignment', 'th is left-aligned at higher specificity, so .stui-cell-* alignment only takes on td'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-inner-table" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <InnerTableConfigurator />
        </Section>
      )}

      {activeSection === 'code-block' && (
        <Section title="Code Block" description="Read-only code on a &lt;pre&gt;: SQL, DDL, generated scripts. Light gray box in the theme's mono font at text-xs. Long lines wrap rather than scrolling sideways, and the block scrolls once it passes 320px. Compose a margin utility for spacing; the block itself has none." cssFile={CSS_LOCATIONS.codeBlock.file} cssSelector={CSS_LOCATIONS.codeBlock.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <pre className="stui-code-block">{"INSERT INTO AppCfg_Test (Test_Key, Test_Value)\nVALUES ('test', 'test');\n\nALTER TABLE dbo.Test ADD Test_Column NVARCHAR(100) NULL;"}</pre>
          </div>
          <CSSRef file="STUI.css" selector=".stui-code-block" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-code-block', 'On &lt;pre&gt; — gray-50 bg, border-subtle border, radius sm, padding sm/md, --font-mono at text-xs, gray-800 text'],
            ['Wrapping', 'white-space: pre-wrap with overflow-wrap: anywhere, so a long line wraps instead of widening its container'],
            ['Height', 'max-height 320px, then scrolls'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-code-block" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'radio-card' && (
        <Section title="Radio Card" description="A bordered single-choice option: a .stui-radio-input beside a bold label and a description line. Use when each choice needs a sentence of explanation, e.g. a sync direction. The card is the label element, so a click anywhere on it picks the option. Add .selected to the chosen card and .disabled to one that cannot be picked." cssFile={CSS_LOCATIONS.radioCard.file} cssSelector={CSS_LOCATIONS.radioCard.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-radio-card selected">
              <input type="radio" name="stui-radio-card-preview" className="stui-radio-input" defaultChecked />
              <div className="stui-form-field">
                <span className="stui-row-8 stui-fw-semibold">Selected</span>
                <span className="stui-list-item-secondary">Primary border and a light primary fill</span>
              </div>
            </label>
            <label className="stui-radio-card">
              <input type="radio" name="stui-radio-card-preview" className="stui-radio-input" />
              <div className="stui-form-field">
                <span className="stui-row-8 stui-fw-semibold">Not selected</span>
                <span className="stui-list-item-secondary">Subtle border; turns primary on hover</span>
              </div>
            </label>
            <label className="stui-radio-card disabled">
              <input type="radio" name="stui-radio-card-preview" className="stui-radio-input" disabled />
              <div className="stui-form-field">
                <span className="stui-row-8 stui-fw-semibold">Disabled <Lock size={12} className="stui-text-muted" /></span>
                <span className="stui-list-item-secondary stui-text-error">Why it cannot be picked</span>
              </div>
            </label>
          </div>
          <CSSRef file="STUI.css" selector=".stui-radio-card / .stui-radio-card.selected / .stui-radio-card.disabled" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / State', 'Role']} rows={[['.stui-radio-card', 'On the &lt;label&gt; — flex row, gap sm, padding sm/md, subtle border, radius sm, pointer'], ['.stui-radio-card:hover', 'Primary border, unless disabled'], ['.stui-radio-card.selected', 'Primary border and --color-primary-tint fill — set it on the checked option'], ['.stui-radio-card.disabled', '45% opacity, not-allowed cursor — pair with a disabled input'], ['.stui-radio-card .stui-radio-input', 'Nudged 2px down to sit on the label line'], ['Content', '.stui-form-field holding a .stui-row-8 .stui-fw-semibold label and a .stui-list-item-secondary description; compose .stui-text-error onto the description to say why an option is disabled']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-radio-card" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <RadioCardConfigurator />
        </Section>
      )}

      {activeSection === 'radio-input' && (
        <Section title="Radio Input" description="Fully custom-styled native radio, the single-choice twin of Checkbox Input. Apply stui-radio-input directly to <input type=radio>. Use inside a .stui-checkbox label wrapper, or bare in a .stui-td-center cell for grid pickers." cssFile={CSS_LOCATIONS.radioInput.file} cssSelector={CSS_LOCATIONS.radioInput.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8">
            <label className="stui-checkbox"><input type="radio" name="stui-radio-preview" className="stui-radio-input" defaultChecked /> Physical</label>
            <label className="stui-checkbox"><input type="radio" name="stui-radio-preview" className="stui-radio-input" /> Comms</label>
            <label className="stui-checkbox"><input type="radio" name="stui-radio-preview" className="stui-radio-input" /> Omit</label>
            <label className="stui-checkbox"><input type="radio" name="stui-radio-preview-locked" className="stui-radio-input" disabled defaultChecked /> Disabled selected</label>
            <label className="stui-checkbox"><input type="radio" name="stui-radio-preview-locked" className="stui-radio-input" disabled /> Disabled</label>
          </div>
          <CSSRef file="STUI.css" selector=".stui-radio-input" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / State', 'Role']} rows={[['.stui-radio-input', 'On &lt;input type=radio&gt; — 1rem circle, gray border, white bg'], ['.stui-radio-input:checked', 'Teal ring — matches --color-primary'], ['.stui-radio-input:checked::after', 'Centered 0.5rem teal dot, white gap between dot and ring'], ['.stui-radio-input:focus-visible', '2px teal outline for keyboard nav accessibility'], ['.stui-radio-input:disabled', 'Grey fill + lighter border, not-allowed cursor — appearance:none means this must be drawn, not inherited'], ['.stui-radio-input:disabled:checked', 'Grey ring and dot — selected, but locked'], ['.stui-checkbox + .stui-radio-input', 'Same wrapper row as checkboxes — gives the circle its label, gap and pointer'], ['.stui-td-center &gt; .stui-radio-input', 'One radio per cell in a choice-per-column grid; share a name across the row so each row picks one']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-radio-input" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <RadioInputConfigurator />
        </Section>
      )}

      {activeSection === 'spinning' && (
        <Section title="Spinning" description="Continuous rotation animation for loading and refresh icons. Three classes spin at 1s per revolution: stui-spinning (animation only), stui-spinner (animation + primary colour), stui-tab-spinner (animation + left margin for inline tab labels)." cssFile={CSS_LOCATIONS.spinning.file} cssSelector={CSS_LOCATIONS.spinning.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-8" style={{ gap: 16 }}>
            <div className="stui-hint">.stui-spinning — animation only, inherits colour</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg className="stui-spinning" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              <span className="stui-label">Loading spinner (Loader2 icon)</span>
            </div>
            <div className="stui-hint">.stui-spinner — animation + primary colour</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg className="stui-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              <span className="stui-label">Module / dashboard loader</span>
            </div>
            <div className="stui-hint">.stui-tab-spinner — animation + 6px left margin</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg className="stui-tab-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              <span className="stui-label">Related-table tab loader</span>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-spinning / .stui-spinner / .stui-tab-spinner / @keyframes stui-spin" />
          <CSSRef file="App.css" selector="@keyframes spin" />
          <Subsection label="Reference" />
          <RefTable headers={['Class / Rule', 'Role']} rows={[['@keyframes stui-spin', '0→360° rotation, defined in STUI.css'], ['@keyframes spin', '0→360° rotation, defined in App.css — drives .stui-spinner and .stui-tab-spinner'], ['.stui-spinning', 'Animation only, 1s linear infinite — inherits colour from the element. Preferred for new work.'], ['.stui-spinner', 'Animation + color: var(--color-primary) — 1s linear infinite'], ['.stui-tab-spinner', 'Animation + margin-left: 6px — inline loader beside a related-table tab label']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-spinning" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-spinner" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-tab-spinner" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'lightbox' && (
        <Section title="Lightbox" description="Full-screen image preview overlay. Triggered when clicking an image in view mode. Click overlay to dismiss." cssFile={CSS_LOCATIONS.lightbox.file} cssSelector={CSS_LOCATIONS.lightbox.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-lightbox-overlay', 'Fixed full-screen overlay — rgba(0,0,0,0.8) bg, z-index 9999, flex center'], ['.stui-lightbox-content', 'Inner content box — white border, checkerboard bg (shows transparency), relative position'], ['.stui-lightbox-img', 'Image element — max 90vw × 80vh, object-fit contain'], ['.stui-lightbox-label', 'Caption below image — 11px gray-500, text-center, padding-top 8px'], ['.stui-lightbox-close', 'X button — absolute top-right, icon button style']]} />
          <CSSRef file="STUI.css" selector=".stui-lightbox-overlay" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-lightbox-overlay" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'css-source-viewer' && (
        <Section title="CSS Source Viewer" description="What a CSS chip opens: STUI.css rendered with line numbers, centred on the rule the chip names. Shows a window of ±120 lines, not the whole file. The pane sits directly in .stui-modal rather than .stui-modal-body, which would pad a full-bleed code block." cssFile={CSS_LOCATIONS.cssSourceViewer.file} cssSelector={CSS_LOCATIONS.cssSourceViewer.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-cssview', 'Scroll pane for the source — monospace 11px, gray-50 fill, overflow auto on both axes'], ['.stui-cssview-row', 'One source line — flex row, gutter then code'], ['.stui-cssview-num', 'Line-number gutter — 52px, right-aligned, gray-400, user-select none so dragging across the pane copies the code without the numbers'], ['.stui-cssview-code', 'The code cell — white-space pre with flex-shrink 0, so a long line keeps full width and the pane scrolls sideways instead of clipping it'], ['.stui-cssview-row.target', 'The line the chip named — primary-100 fill and a 3px inset left accent']]} />
          <CSSRef file="STUI.css" selector=".stui-cssview / .stui-cssview-row / .stui-cssview-num / .stui-cssview-code" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-cssview" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'org-images' && (
        <Section title="Org Images" description="Organization logo and icon management table — upload, preview, hover-to-delete. Used in Organizations admin page." cssFile={CSS_LOCATIONS.orgImages.file} cssSelector={CSS_LOCATIONS.orgImages.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-org-img-table', 'Full-width collapsing table for logo/icon rows'], ['.stui-org-img-label', 'Left td — semibold label cell, 80px wide, middle-aligned'], ['.stui-org-img-cell', 'Center td — 50px wide, center-aligned (reserved)'], ['.stui-org-img-actions', 'Right td — right-aligned, holds upload btn or image wrap'], ['.stui-org-upload-btn', 'Upload trigger — icon+arrow button, shows on hover (pure CSS, no JS)'], ['.stui-org-img-wrap', 'Wrapper for image + delete overlay — relative, inline-block'], ['.stui-org-icon-img', 'Icon image — 32×32px display'], ['.stui-org-logo-img', 'Logo image — max 120px wide, max 40px tall'], ['.stui-org-icon-img.dimmed, .stui-org-logo-img.dimmed', 'Opacity 0.3 — applied on hover to reveal delete button'], ['.stui-org-img-delete', 'Delete overlay button — absolute centered Trash2 icon, shows on JS hover']]} />
          <CSSRef file="STUI.css" selector=".stui-org-img-table" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-org-img-table" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'color-harmony' && (
        <Section title="Color Harmony" description="The second tool in components/common/colorpicker/, and the companion to Color Picker: the picker answers what one color is, this answers what set of colors goes together and whether that set is legible. Harmony builds the set on a rule-locked wheel or by sampling an image; Contrast judges every pairing in it. The tile strip along the bottom belongs to neither tab, so switching between them never loses the working set, and clicking a tile opens the picker to override that one color. Stateless by design — colors in, colors out, no persistence, no naming, no token mapping. It knows nothing about themes; a caller that needs those composes this with its own page, which is what keeps it reusable. Generation math is pure and lives in ./harmony, image sampling in ./imageColors, contrast in ./colorMath." cssFile={CSS_LOCATIONS.colorHarmony.file} cssSelector={CSS_LOCATIONS.colorHarmony.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Live — the real dialog</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            <button className="stui-btn stui-btn-primary" onClick={() => { setChResult(null); setChOpen(true); }}>
              <Blend size={14} /> Open Color Harmony
            </button>
            {chResult && (
              <span className="stui-hint">
                Returned {chResult.length} colors: <code>{chResult.join(', ')}</code>
              </span>
            )}
          </div>
          <p className="stui-hint" style={{ marginBottom: 16 }}>
            The real component. The demo onApply only reports what it was handed —
            <strong> nothing is saved</strong>. Drag any handle and the whole set follows the rule;
            pick Custom to release them.
          </p>

          <CSSRef file="STUI.css" selector=".stui-ch-modal / .stui-ch-wheel / .stui-ch-tiles / .stui-ch-matrix" />

          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-modal-overlay-nested', 'z-index 1100 — the ONLY class here outside the stui-ch- prefix, because a second-level modal belongs to the shared modal family. Sits above .stui-alert-bar (1001), below the 9999 layers'],
            ['.stui-ch-modal', 'Width override on .stui-modal — 600px, max-height 94vh'],
            ['.stui-ch-sourcerow / .stui-ch-countrow', 'Wheel/Image toggle beside the color count'],
            ['.stui-ch-wheelrow', 'Wheel next to its rule strip'],
            ['.stui-ch-wheel', 'The disc — conic-gradient hue from 3 o’clock (agreeing with atan2 in ./harmony) under a radial white fade for saturation'],
            ['.stui-ch-links', 'Inline SVG overlay: spokes and the polygon joining the handles, decorative only'],
            ['.stui-ch-handle', 'One draggable handle; .base is the seed and is larger with an inner ring; .active is the tile being edited'],
            ['.stui-ch-rules / .stui-ch-rule', 'Rule list and one entry; .selected is filled primary'],
            ['.stui-ch-drop / .stui-ch-drop.over', 'Image dropzone and its drag-over state'],
            ['.stui-ch-dropicon / .stui-ch-droptitle / .stui-ch-dropsub', 'Dropzone icon, headline and hint'],
            ['.stui-ch-imagewrap / .stui-ch-image', 'Sample surface — crosshair cursor, capped at 260px tall'],
            ['.stui-ch-imagehint / .stui-ch-imageerror', 'Sampling instruction row; read failure or size-guard message'],
            ['.stui-ch-tiles', 'The working set — outside the body so both tabs share it'],
            ['.stui-ch-tile / .stui-ch-tile.active', 'One tile; active is ringed and tinted'],
            ['.stui-ch-tileswatch', '64x40 swatch — click opens the picker for that color'],
            ['.stui-ch-tilelight', 'Per-tile lightness range; dragging the wheel sets hue and saturation only'],
            ['.stui-ch-tilehex / .stui-ch-tiletoken', 'Hex, and the --color-* token when one matches exactly'],
            ['.stui-ch-tiledrop / .stui-ch-tileadd', 'Remove a tile (never below 3) and add one (never above 5)'],
            ['.stui-ch-matrixwrap / .stui-ch-matrixhead', 'Matrix column and its category pills plus pass count'],
            ['.stui-ch-matrix / .stui-ch-axis / .stui-ch-chip', 'The grid, its corner cell, and a row/column color chip. Axis is white, black, then the set — on BOTH sides, because text-on-this-color and this-color-as-text are different questions'],
            ['.stui-ch-axislabel', 'The corner legend — rows are the background, columns are the text'],
            ['.stui-ch-cell', 'Every pairing renders its real background/text combination — failures included, since hiding them removed the one thing the grid is for'],
            ['.stui-ch-sample', 'The rendered pair. Fixed 40px height across categories so switching pills never reflows the grid; .small / .large / .nontext change the sample itself, not just the threshold'],
            ['.stui-ch-strip', 'Verdict bar along the cell’s bottom edge, OUTSIDE the pair — a marker drawn in the pair’s own colors is invisible exactly when it fails. Reads as a pattern across the grid'],
            ['.stui-ch-sample.na', 'The diagonal — a color on itself is not a pairing, so it reads n/a in grey'],
            ['.stui-cp-num', 'Reused from the picker for the count input — same control, same family'],
          ]} />

          <Subsection label="Used By" />
          <UsedBy cssClass="stui-ch-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-ch-wheel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />

          <Subsection label="Text Inventory" />
          <TextInventory items={[
            'Color Harmony', 'Harmony', 'Contrast', 'Wheel', 'Image', 'Colors',
            'Analogous', 'Complementary', 'Split complementary', 'Triad', 'Square',
            'Compound', 'Monochromatic', 'Shades', 'Custom',
            'Drop an image, paste, or click to browse', 'Clear image',
            'Small text', 'Large text', 'UI and graphics',
            'Cancel', 'Use these colors',
          ]} />

          <Subsection label="Configurator" />
          <ColorHarmonyConfigurator />

          <ColorHarmonyModal
            isOpen={chOpen}
            seed="#1a5568"
            count={3}
            onClose={() => setChOpen(false)}
            onApply={hexes => setChResult(hexes)}
          />
        </Section>
      )}

      {activeSection === 'color-picker' && (
        <Section title="Color Picker" description="Canonical color selection, in components/common/colorpicker/ — laid out as the Office Colors dialog so it needs no explaining. Standard is a generated hex grid (hue by angle, pale through vivid to dark by radius) plus a neutral strip; Custom is a hue x saturation field with a separate luminance bar and a Color model selector, so a value typed as RGB, HSL or hex can be watched land on the field. A New/Current rail compares against the incoming color. It is a PURE picker: it knows nothing about themes or palettes, and a caller wanting to offer its own colors renders them itself and opens this to change one. The status band recommends a text color rather than listing readings: no color can carry both white and dark text, so presenting the two as equals made every color lead with a Fail — including the page background, where white-on-white is true but useless. It leads with the winner as advice, over a faded Aa sample of the pairing it rejected, and an info button opens the thresholds for small text, large text and UI/graphics so a Fail says what it actually failed. The only bridge back to application colors is that same band, which names the nearest --color-* token — discovered from the CSSOM, so a token added to App.css is found without a code change — and can return var(--token) instead of a frozen hex. Conversion and WCAG contrast live in ./colorMath as pure functions, token discovery in ./cssTokens, grid geometry in ./honeycomb. ColorPickerField is the standard call site; prefer it over a bare <input type=color>, which offers no alpha, no tokens and no contrast check." cssFile={CSS_LOCATIONS.colorPicker.file} cssSelector={CSS_LOCATIONS.colorPicker.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Live — the real dialog</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            <ColorPickerField value={cpFieldValue} onChange={setCpFieldValue} title="Colors" />
            <button className="stui-btn stui-btn-primary" onClick={() => { setCpResult(null); setCpModalOpen(true); }}>
              <Palette size={14} /> Open dialog directly
            </button>
            {cpResult && (
              <span className="stui-hint">
                Returned <code>{cpResult.value}</code> — hex {cpResult.hex}, {cpResult.rgba},
                {' '}{cpResult.hsl}, token {cpResult.token || 'none'}
              </span>
            )}
          </div>
          <p className="stui-hint" style={{ marginBottom: 16 }}>
            Both open the real component. The demo onSelect only reports what it was handed —
            <strong> nothing is saved</strong>. Recent colors do persist, in localStorage under
            <code> pmd-stui-color-recents</code>.
          </p>

          <div className="stui-subsection-divider">Trigger states</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            <ColorPickerField value={cpTokenValue} onChange={setCpTokenValue} title="Filled" />
            <ColorPickerField value={cpEmptyValue} onChange={setCpEmptyValue} title="Empty" />
            <ColorPickerField value="#8db2bf6e" onChange={() => {}} disabled />
            <span className="stui-hint">filled · empty · disabled — all three are the real component</span>
          </div>

          <div className="stui-subsection-divider">Compare rail and status band</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start', marginBottom: 4 }}>
            <div className="stui-cp-compare">
              <span className="stui-cp-comparelabel">New</span>
              <div className="stui-cp-swatch stui-cp-checker"><span style={{ background: 'var(--color-primary)' }} /></div>
              <div className="stui-cp-swatch stui-cp-checker"><span style={{ background: 'var(--color-maroon)' }} /></div>
              <span className="stui-cp-comparelabel">Current</span>
            </div>
            <div className="stui-cp-status" style={{ flex: '1 1 320px', minWidth: 300 }}>
              <div className="stui-cp-statusrow">
                <span className="stui-cp-statuslabel">Token</span>
                <code className="stui-cp-tokenname">= --color-primary</code>
              </div>
              <div className="stui-cp-statusrow stui-cp-contrastrow">
                <span className="stui-cp-statuslabel">Contrast</span>
                <div className="stui-cp-pairs">
                  <div className="stui-cp-pair">
                    <span className="stui-cp-pairsample" style={{ background: 'var(--color-primary)', color: '#fff' }}>Aa</span>
                    <span className="stui-cp-advice">Use white text</span>
                    <span className="stui-cp-ratio">8.26:1</span>
                    <span className="stui-badge stui-badge-green">AAA</span>
                  </div>
                  <div className="stui-cp-pair muted">
                    <span className="stui-cp-pairsample" style={{ background: 'var(--color-primary)', color: 'var(--color-gray-900)' }}>Aa</span>
                    <span className="stui-cp-alsotext">dark text would be 2.15:1 — Fail</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="stui-hint" style={{ marginBottom: 4 }}>
            A near-miss reads <code className="stui-cp-tokenname near">≈ --color-primary (Δ 12)</code> instead,
            so you know you are a couple of shades off rather than guessing.
          </p>
          <CSSRef file="STUI.css" selector=".stui-cp-modal / .stui-cp-comb / .stui-cp-field / .stui-cp-trigger" />

          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-cp-modal', 'Width override on .stui-modal — 480px, max-height 92vh'],
            ['.stui-cp-checker', 'Transparency checkerboard; a direct child span is filled absolutely, so a swatch is one span'],
            ['.stui-cp-main / .stui-cp-left / .stui-cp-rail', 'Two-column body — controls on the left, 52px compare rail on the right'],
            ['.stui-cp-tabbar / .stui-cp-tab', 'Standard / Custom strip — .active is primary text + underline'],
            ['.stui-cp-caption', 'The "Colors:" label above the active tab content'],
            ['.stui-cp-comb / .stui-cp-hex', 'Hex grid container (cells absolutely placed from axial coords) and one clip-path hexagon'],
            ['.stui-cp-hex.selected', 'Scaled with a gray-900 ring — the current color in the grid'],
            ['.stui-cp-grays', 'Neutral strip under the hexagon; its cells are statically positioned'],
            ['.stui-cp-custom', 'Row holding the field and the luminance bar'],
            ['.stui-cp-field / .stui-cp-crosshair', 'Hue (x) by saturation (y) field at fixed 50% luminance, and its round marker'],
            ['.stui-cp-lum / .stui-cp-lum-marker', 'Vertical luminance bar (gradient set inline from hue/sat) and its triangular pointer'],
            ['.stui-cp-modelrow / .stui-cp-model', 'The "Color model:" row and its select'],
            ['.stui-cp-numrow / .stui-cp-numlabel', 'One labelled input row and its 76px caption'],
            ['.stui-cp-num / .stui-cp-hexinput', 'Numeric channel input; the wider paste-tolerant text input'],
            ['.stui-cp-numsuffix', 'Trailing unit or percentage after an input'],
            ['.stui-cp-alpha / .stui-cp-alpha-fill / .stui-cp-alpha-thumb', 'Opacity track over the checkerboard, its transparent-to-color overlay, and the thumb'],
            ['.stui-cp-compare / .stui-cp-swatch', 'New-over-Current stack and one 46x26 swatch'],
            ['.stui-cp-comparelabel', 'The New and Current captions'],
            ['.stui-cp-status / .stui-cp-statusrow', 'Status band on gray-50 and one row of it'],
            ['.stui-cp-statuslabel', '62px uppercase row caption — Token, Contrast, Return as, Recent'],
            ['.stui-cp-tokenname', 'Matched token in primary; .near grays it for an approximate hit'],
            ['.stui-cp-ratio / .stui-cp-vs', 'Contrast ratio and the pairing it describes'],
            ['.stui-cp-contrastrow / .stui-cp-pairs / .stui-cp-pair', 'Top-aligned contrast row, its stack of readings, and one reading'],
            ['.stui-cp-pairsample', '34x20 "Aa" chip — the chosen color as background, the candidate text color as ink'],
            ['.stui-cp-advice', 'The recommendation — which text color to use, in gray-800 semibold'],
            ['.stui-cp-alsotext / .stui-cp-pair.muted', 'The rejected pairing, demoted to a gray-500 footnote with a faded sample'],
            ['.stui-cp-help', 'Explainer panel toggled by the info button — primary-tinted, holds the threshold table'],
            ['.stui-cp-helptext / .stui-cp-helptable', 'Explainer prose and its Content / AA / AAA / per-pairing verdict table'],
            ['.stui-cp-return', 'Select choosing the returned format — var(--token) only when a token matches exactly'],
            ['.stui-cp-icon-btn', '24px icon button — eyedropper, copy'],
            ['.stui-cp-recents / .stui-cp-recent', 'Recent-color strip and its 22px swatch'],
            ['.stui-cp-trigger', 'Call-site trigger — swatch plus current value'],
            ['.stui-cp-trigger-swatch', '22px swatch inside the trigger'],
            ['.stui-cp-trigger-value', 'Monospace value text — .empty renders the placeholder in italic gray'],
          ]} />

          <Subsection label="Used By" />
          <UsedBy cssClass="stui-cp-trigger" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-cp-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />

          <Subsection label="Text Inventory" />
          <TextInventory items={[
            'Colors', 'Standard', 'Custom', 'Colors:', 'Color model:', 'RGB', 'HSL', 'Hex',
            'Red:', 'Green:', 'Blue:', 'Hue:', 'Sat:', 'Lum:', 'Opacity:',
            'New', 'Current', 'Token', 'Contrast', 'Return as', 'Recent',
            'Use white text', 'Use dark text', 'No text color passes on this',
            'large text and UI only', 'white text', 'dark text',
            'No close token', 'No --color-* tokens found',
            'What do these ratings mean?', 'Content', 'AA needs', 'AAA needs',
            'Small text', 'Large text', 'UI and graphics',
            'Cancel', 'OK', 'No color', 'Pick a color from the screen', 'Copy value',
          ]} />

          <Subsection label="Configurator" />
          <ColorPickerConfigurator />

          <ColorPickerModal
            isOpen={cpModalOpen}
            title="Colors"
            value={cpFieldValue}
            onClose={() => setCpModalOpen(false)}
            onSelect={setCpResult}
          />
        </Section>
      )}

      {activeSection === 'icon-browser' && (
        <Section title="Icon Browser" description="CSS grid of icon cards used in the Icon Browser development page. Each card shows the icon, its name, and a copy indicator." cssFile={CSS_LOCATIONS.iconBrowser.file} cssSelector={CSS_LOCATIONS.iconBrowser.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-icon-grid', '6-column CSS grid, 80px min column, gap 8px, padding 16px'], ['.stui-icon-card', 'Button card — flex column center, white bg, border, rounded-md, hover lift'], ['.stui-icon-card:hover', 'Teal-50 bg, primary border, slight translateY(-1px)'], ['.stui-icon-card.copied', 'Teal border + teal-50 bg — flash state after copy'], ['.stui-icon-preview', '48×48px icon container — flex center'], ['.stui-icon-name', '11px monospace label below icon'], ['.stui-icon-copy-indicator', 'Absolute bottom-right copy icon — opacity 0 until hover or copied']]} />
          <CSSRef file="STUI.css" selector=".stui-icon-grid" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-icon-grid" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'image-upload' && (
        <Section title="Image Upload" description="Two modals in components/common/imageupload/, picked by what the image IS. LogoUploadModal for BRAND MARKS (org logos and icons): forced crop, pipette background removal, color replace, SVG rasterization, transparent-PNG output. PictureUploadModal for PHOTOGRAPHS: free-form crop, JPEG output with a quality control, a white matte before encoding, and no transparency tooling at all — background removal is meaningless on a photo and JPEG carries no alpha. Both share the same pipeline (useImageUpload) and the same chrome (ImageUploadParts), so drag-drop, clipboard paste, orientation, size guard and the modal skeleton behave identically. Shared canvas work lives in ./imageCanvas; transparency and color work in ./logoPixelOps, which only the logo flow imports." cssFile="STUI.css" cssSelector=".stui-imgupload-modal" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Live — the real components</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <button className="stui-btn stui-btn-primary" onClick={() => { setUploadDemoResult(null); setUploadDemo('logo'); }}>
              <Upload size={14} /> Try LogoUploadModal
            </button>
            <button className="stui-btn stui-btn-primary" onClick={() => { setUploadDemoResult(null); setUploadDemo('picture'); }}>
              <Upload size={14} /> Try PictureUploadModal
            </button>
            {uploadDemoResult && (
              <span className="stui-hint">
                Produced <strong>{uploadDemoResult.name}</strong> — {uploadDemoResult.type}, {uploadDemoResult.size}
                {' '}· <strong>discarded, nothing was stored</strong>
              </span>
            )}
          </div>
          <p className="stui-hint" style={{ marginBottom: 16 }}>
            These open the real components, but the demo's onUpload handler only reports the file it
            was handed and then drops it — <strong>no API call, nothing written to blob storage</strong>.
            The modal's own "Upload" button and "Uploading…" state are its normal UI; here they lead nowhere.
          </p>

          <div className="stui-subsection-divider">Logo variant — transparent preview</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <span className="stui-label">Dropzone (static mock — real modal is stateful)</span>
              <div className="stui-imgupload-dropzone">
                <Upload size={40} className="stui-imgupload-drop-icon" />
                <p className="stui-imgupload-drop-title">Drop image here or click to browse</p>
                <p className="stui-imgupload-drop-sub">Max 5MB • .png, .jpg, .jpeg, .svg, .webp</p>
                <div className="stui-imgupload-spec">Image will be converted to <strong>PNG with transparency</strong></div>
              </div>
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <span className="stui-label">Edit toolbar (static mock)</span>
              <div className="stui-imgupload-toolbar" style={{ border: 'var(--border-width) solid var(--color-border-subtle)', minHeight: 220 }}>
                <div className="stui-imgupload-section">
                  <div className="stui-imgupload-step-label"><span className="stui-imgupload-step-num done"><Check size={10} /></span>Crop</div>
                  <button className="stui-imgupload-tool-btn" type="button"><Crop size={12} /> Re-crop</button>
                </div>
                <div className="stui-imgupload-section">
                  <div className="stui-imgupload-step-label"><span className="stui-imgupload-step-num">2</span>Remove Background</div>
                  <div className="stui-imgupload-tool-row">
                    <button className="stui-imgupload-tool-btn active" type="button"><Pipette size={12} /> Click image...</button>
                    <span className="stui-imgupload-swatch" style={{ backgroundColor: '#e0f2f7' }} />
                  </div>
                  <div className="stui-imgupload-tool-row">
                    <button className="stui-imgupload-tool-btn" type="button" disabled><Eraser size={12} /> Remove BG</button>
                    <button className="stui-imgupload-tool-btn square" type="button"><Plus size={10} className="stui-imgupload-plus" /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="stui-subsection-divider">Photo variant — opaque preview + quality slider</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <span className="stui-label">Dropzone (JPEG output)</span>
              <div className="stui-imgupload-dropzone">
                <Upload size={40} className="stui-imgupload-drop-icon" />
                <p className="stui-imgupload-drop-title">Drop image here or click to browse</p>
                <p className="stui-imgupload-drop-sub">Max 5MB • .png, .jpg, .jpeg, .webp</p>
                <div className="stui-imgupload-spec">Image will be saved as <strong>JPEG</strong></div>
              </div>
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 240 }}>
              <span className="stui-label">Quality step — .stui-imgupload-range-row</span>
              <div className="stui-imgupload-toolbar" style={{ border: 'var(--border-width) solid var(--color-border-subtle)' }}>
                <div className="stui-imgupload-section">
                  <div className="stui-imgupload-step-label"><span className="stui-imgupload-step-num">2</span>Quality</div>
                  <div className="stui-imgupload-tolerance-desc">Higher keeps more detail in fine text and edges, at a larger file size</div>
                  <div className="stui-imgupload-range-row">
                    <span className="stui-imgupload-range-end">Smaller</span>
                    <input type="range" min={50} max={100} defaultValue={85} readOnly />
                    <span className="stui-imgupload-range-end">Sharper</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-imgupload-modal / .stui-imgupload-dropzone / .stui-imgupload-toolbar / .stui-imgupload-tool-btn" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-imgupload-modal', 'Width modifier on .stui-modal.stui-modal-lg — 800px wide, 85vh max'],
            ['.stui-imgupload-body', 'Modal body override — no padding, overflow hidden (steps manage their own scroll)'],
            ['.stui-imgupload-error', 'Error banner strip at top of body'],
            ['.stui-imgupload-select', 'Step 1 wrapper — padding, column flex'],
            ['.stui-imgupload-dropzone', 'Drop target — dashed border, .dragover = primary tint'],
            ['.stui-imgupload-spec', 'Output spec box inside dropzone (PNG notice, dimensions)'],
            ['.stui-imgupload-paste-row / -paste-hint', 'Paste-from-clipboard button row + "or Ctrl+V" hint'],
            ['.stui-imgupload-crop / -crop-banner / -canvas-wrap', 'Step 2 — forced crop layout, instruction banner, centered ReactCrop canvas'],
            ['.stui-imgupload-edit / -preview-pane / -preview-img', 'Step 3 — split layout; .picking on pane = crosshair pipette cursor; img has checkerboard bg'],
            ['.stui-imgupload-pick-hint', 'Floating dark pill telling the user to click a color'],
            ['.stui-imgupload-toolbar / -section / -step-label / -step-num', 'Right workflow column — numbered steps, .done = green check'],
            ['.stui-imgupload-tool-row / -tool-btn', 'Small tool buttons — .active = primary fill, .square = 24px icon toggle'],
            ['.stui-imgupload-swatch / -arrow', 'Picked-color chip and from→to arrow; the replace target is a ColorPickerField'],
            ['.stui-imgupload-tolerance*', 'Collapsible fine-tune slider box (desc, row, end labels)'],
            ['.stui-imgupload-meta / -filename-input / -fileinfo', 'File name field + output dimensions/size readout'],
            ['.stui-imgupload-uploading*', 'Uploading state — spinning icon + text'],
            ['.stui-imgupload-preview-img-opaque', 'Modifier for opaque output — drops the checkerboard, which would imply an alpha channel JPEG cannot carry'],
            ['.stui-imgupload-range-row / -range-end', 'Generic labelled slider row. Use for any new slider in the image tools; tolerance-row/-end are the same structure under a feature-specific name'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-imgupload-dropzone" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />

          {uploadDemo === 'logo' && (
            <LogoUploadModal
              isOpen
              title="Demo — Logo Upload"
              suggestedName="demo-logo"
              onClose={() => setUploadDemo(null)}
              onUpload={async (file) => {
                setUploadDemoResult({ name: file.name, type: file.type, size: `${(file.size / 1024).toFixed(1)} KB` });
              }}
            />
          )}
          {uploadDemo === 'picture' && (
            <PictureUploadModal
              isOpen
              title="Demo — Picture Upload"
              suggestedName="demo-picture"
              onClose={() => setUploadDemo(null)}
              onUpload={async (file) => {
                setUploadDemoResult({ name: file.name, type: file.type, size: `${(file.size / 1024).toFixed(1)} KB` });
              }}
            />
          )}
        </Section>
      )}

      {activeSection === 'sub-label' && (
        <Section title="Sub-Label" description="Secondary helper text beneath a field label — 11px, gray-500. Based on App.css .pref-sublabel." cssFile={CSS_LOCATIONS.subLabel.file} cssSelector={CSS_LOCATIONS.subLabel.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-col-12">
            <div>
              <span className="stui-label">Theme</span>
              <div className="stui-sublabel">Controls the color palette across all pages</div>
            </div>
            <div>
              <span className="stui-label">Layout</span>
              <div className="stui-sublabel">Affects report page size and column widths</div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-sublabel" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-sublabel', '11px gray-500, margin-top 1px — secondary helper text']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-sublabel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SubLabelConfigurator />
        </Section>
      )}

      {activeSection === 'pill' && (
        <Section title="Pill" description="Selectable pill button — rounded, teal fill when selected, gray border when not. Use .stui-pill-group as the flex container." cssFile={CSS_LOCATIONS.pill.file} cssSelector={CSS_LOCATIONS.pill.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-pill-group">
            <button className="stui-pill selected">Light</button>
            <button className="stui-pill">Dark</button>
            <button className="stui-pill">Dim</button>
            <button className="stui-pill">Sepia</button>
            <button className="stui-pill">High Contrast</button>
          </div>
          <CSSRef file="STUI.css" selector=".stui-pill" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-pill-group', 'Flex container — gap 6px, wrap'], ['.stui-pill', '12px bold, rounded-12, gray border, pointer'], ['.stui-pill.selected', 'Teal fill — primary bg + border, white text'], ['.stui-pill:hover:not(.selected)', 'Gray-100 bg on hover']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-pill" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <PillConfigurator />
        </Section>
      )}

      {activeSection === 'rich-text' && (
        <Section title="Rich Text" description="Canonical rich text editor (TipTap/ProseMirror, headless — all styling is STUI). StuiRichText edits, StuiRichTextViewer renders sanitized output. Never render stored HTML any other way." cssFile="STUI.css" cssSelector=".stui-rte" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 4 }}>
            <div style={{ flex: '1 1 340px', minWidth: 300 }}>
              <span className="stui-label">StuiRichText — editor</span>
              <StuiRichText value={rteDemoHtml} onChange={setRteDemoHtml} placeholder="Type something..." minHeight={160} />
            </div>
            <div style={{ flex: '1 1 340px', minWidth: 300 }}>
              <span className="stui-label">StuiRichTextViewer — sanitized render (live sync)</span>
              <StuiRichTextViewer html={rteDemoHtml} />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-rte / .stui-rte-toolbar / .stui-rte-content" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[
            ['.stui-rte', 'Editor container — border, radius, white bg, primary border on focus'],
            ['.stui-rte-toolbar', 'Formatting button row — gray-50 bg, bottom border'],
            ['.stui-rte-btn', 'Toolbar icon button — .active = primary-tinted when mark is on'],
            ['.stui-rte-divider', '1px vertical separator between toolbar groups'],
            ['.stui-rte-linkbar', 'Inline link URL prompt row — input + Set/Clear (no window.prompt)'],
            ['.stui-rte-editor', 'ProseMirror editing surface wrapper — padding, min-height'],
            ['.stui-rte-content', 'Read-only rendered output (StuiRichTextViewer) — shares typography with the editor'],
          ]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-rte" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'toggle' && (
        <Section title="Toggle" description="iOS-style toggle switch. Use .stui-toggle-group to wrap multiple toggles. Apply .stui-toggle to a <label> containing an <input type=checkbox> followed by a <span class=stui-toggle-slider>." cssFile={CSS_LOCATIONS.toggle.file} cssSelector={CSS_LOCATIONS.toggle.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-toggle-group" style={{ flexDirection: 'column', gap: 12 }}>
            {[['Enable notifications', true], ['Dark mode override', false], ['Show QR code', true]].map(([lbl, on]) => (
              <div key={lbl} className="stui-row-10">
                <label className="stui-toggle">
                  <input type="checkbox" defaultChecked={on} readOnly />
                  <span className="stui-toggle-slider" />
                </label>
                <span className="stui-label">{lbl}</span>
              </div>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-toggle-group / .stui-toggle" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-toggle-group', 'Flex container for toggle rows — gap 6px, wraps'], ['.stui-toggle', 'On &lt;label&gt; — 40×22px container, position relative'], ['.stui-toggle input', 'Hidden checkbox — opacity 0, no dimensions'], ['.stui-toggle-slider', 'On &lt;span&gt; — gray pill, transitions to teal on checked'], ['.stui-toggle-slider::before', 'White circle knob — slides right 18px on checked']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-toggle-group" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-toggle" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ToggleConfigurator />
        </Section>
      )}

      {activeSection === 'setting-row' && (
        <Section title="Setting Row" description="Label-left / control-right flex row for settings pages. Use stui-row-setting as the outer row, stui-row-setting-label for the label stack, and stui-row-setting-controls for a group of buttons or inputs on the right." cssFile={CSS_LOCATIONS.settingRow.file} cssSelector={CSS_LOCATIONS.settingRow.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <Example label="Toggle row">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Enable Feature</div>
                <div className="stui-sublabel">Turns this option on for all users on this machine</div>
              </div>
              <label className="stui-toggle">
                <input type="checkbox" defaultChecked />
                <span className="stui-toggle-slider" />
              </label>
            </div>
          </Example>
          <Example label="Select row">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Default Database</div>
                <div className="stui-sublabel">Database to open on login</div>
              </div>
              <StuiSelect className="stui-select"><option>Production</option><option>Staging</option></StuiSelect>
            </div>
          </Example>
          <Example label="Controls row (path + buttons)">
            <div className="stui-row-setting">
              <div className="stui-row-setting-label">
                <div className="stui-label">Download Location</div>
                <div className="stui-sublabel">Where exported files are saved</div>
              </div>
              <div className="stui-row-setting-controls">
                <span className="stui-text-sm-muted">~/Downloads/DAWN</span>
                <button className="stui-btn stui-btn-primary">Change</button>
              </div>
            </div>
          </Example>
          <CSSRef file="STUI.css" selector=".stui-row-setting" />
          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={[['.stui-row-setting', 'Outer row — flex, space-between, gap 12px, padding 8px 0'], ['.stui-row-setting-label', 'Left column — flex column for label + sublabel stack'], ['.stui-row-setting-controls', 'Right column — flex row, gap 8px, no-shrink, for buttons/inputs']]} />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-row-setting" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SettingRowConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Reports ──────────────────────────────────────────── */

const REPORTS_SECTIONS = [
  { id: 'page-header',    label: 'Page Header'    },
  { id: 'report-preview', label: 'Report Preview' },
  { id: 'report-viewer',  label: 'Report Viewer'  },
];

const ReportsTab = ({ usageMap, pageSlugMap, elementTexts, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('page-header');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {REPORTS_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {activeSection === 'page-header' && (
        <Section title="Page Header" description="Flex row at the top of every module page. White background with gray bottom border, title left, action menu right." cssFile={CSS_LOCATIONS.pageHeader.file} cssSelector={CSS_LOCATIONS.pageHeader.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 4, maxWidth: 700 }}>
            <div className="stui-page-header">
              <div className="stui-row-md">
                <h1 className="stui-page-title">
                  <FileSpreadsheet size={20} />I/O Assignments
                </h1>
                <span className="stui-text-sm-muted">Tag IO assignment details by panel, drop, slot, channel</span>
              </div>
              <DemoExportMenu />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-page-header" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Property', 'Value', 'Notes']}
            rows={[
              ['display',          'flex',                              ''],
              ['align-items',      'center',                            ''],
              ['justify-content',  'space-between',                     'Title left, action menu right'],
              ['background-color', 'var(--color-bg-white)',             'White'],
              ['border-bottom',    'var(--border-width) solid var(--color-border-subtle)',   'Subtle bottom separator'],
              ['color',            'var(--color-gray-800)',             ''],
              ['font-family',      'var(--font-secondary)',             'Rajdhani Clear / Roboto Condensed'],
              ['font-weight',      '500',                               ''],
              ['padding',          'var(--spacing-sm) 0.75rem',        '8px 12px'],
              ['min-height',       '49px',                              ''],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-page-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Text Inventory" />
          <div className="stui-text-italic">
            Page titles vary per module — not included in element-texts scan.
          </div>
          <Subsection label="Configurator" />
          <ReportHeaderConfigurator />
        </Section>
      )}

      {activeSection === 'report-viewer' && (
        <Section title="Report Viewer" description="Modifier classes applied inside .stui-report-viewer. Extend step-panel behavior for collapsible sections, toolbar rows, and pre-run placeholders." cssFile={CSS_LOCATIONS.reportViewer.file} cssSelector={CSS_LOCATIONS.reportViewer.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
            {/* Clickable step header with action + chevron */}
            <div className="stui-step-panel stui-step-panel-no-overflow">
              <div className="stui-step-header clickable">
                <span className="stui-step-number">2</span>
                <h3>Report Preview</h3>
                <button className="stui-btn stui-btn-primary stui-step-action"><Play size={14} /> Run Report</button>
                <span className="stui-step-chevron"><ChevronDown size={16} /></span>
              </div>
            </div>
            {/* Options row */}
            <div className="stui-step-panel">
              <div className="stui-step-header clickable">
                <span className="stui-step-number">1</span>
                <h3>Report Configuration</h3>
                <span className="stui-step-chevron"><ChevronDown size={16} /></span>
              </div>
              <div className="stui-options-row">
                <div className="stui-sort-slot">
                  <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Sort by</span>
                  <StuiSelect className="stui-select" style={{ fontSize: 12 }}><option>Panel</option><option>Tag</option></StuiSelect>
                </div>
                <div className="stui-sort-slot">
                  <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Then by</span>
                  <StuiSelect className="stui-select" style={{ fontSize: 12 }}><option>None</option><option>Drop</option></StuiSelect>
                </div>
              </div>
            </div>
            {/* Pre-run placeholder */}
            <div className="stui-step-panel">
              <div className="stui-report-placeholder">
                <p>Configure options above, then click Run Report to generate the preview.</p>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-step-panel-no-overflow / .stui-step-header.clickable / .stui-step-chevron / .stui-options-row" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Description', 'Key Properties']}
            rows={[
              ['.stui-step-panel-no-overflow',  'Hides horizontal overflow on preview panel',       'overflow-x: hidden'],
              ['.stui-step-header.clickable',    'Marks step header as collapsible toggle',          'cursor: pointer; user-select: none'],
              ['.stui-step-chevron',             'Chevron pushed to right edge of step header',      'margin-left: auto; display: flex; align-items: center'],
              ['.stui-step-action',              'Primary action button pushed left of chevron',     'margin-left: auto; margin-right: 12px'],
              ['.stui-options-row',              'Toolbar row — sort selects, checkboxes, group-by', 'display: flex; gap: 16px; border-top; flex-wrap: wrap'],
              ['.stui-sort-slot',                'Single label + select(s) unit in options row',     'display: flex; align-items: center; gap: 4px'],
              ['.stui-report-placeholder',       'Empty-state shown before first run',               'padding: 60px 20px; text-align: center; color: #999'],
              ['.stui-report-placeholder p',     'Placeholder message text',                         'font-size: 16px; margin: 0'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-report-viewer" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {activeSection === 'report-preview' && (
        <Section title="Report Preview" description="stui-rpt-preview-* — scaled HTML preview that mirrors PDF export. All pixel values match ReportPdfStyles.js and must not be converted to vars. Dynamic values (scale, column widths, brand color, zebra) remain inline." cssFile={CSS_LOCATIONS.reportPreview.file} cssSelector={CSS_LOCATIONS.reportPreview.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Description', 'Key Properties']}
            rows={[
              ['.stui-rpt-preview-container',    'Root wrapper — pins all CSS vars to light-mode defaults; overflow: hidden',        'CSS var overrides (theme immune); overflow: hidden'],
              ['.stui-rpt-preview-scale',         'Scale transform wrapper — transform-origin fixed; transform/width/marginBottom inline', 'transform-origin: top left'],
              ['.stui-rpt-preview-header',        'Page header — WM logo left, title centered (absolute), params right',              'display: flex; padding: 10px 32px; border-bottom: 2px solid #1a5568'],
              ['.stui-rpt-preview-logo-block',    'Left block containing logo + optional QR code',                                    'flex: 0 0 auto; display: flex; gap: 10px'],
              ['.stui-rpt-preview-qr-block',      'QR code column — stacked code + label',                                           'display: flex; flex-direction: column; align-items: center'],
              ['.stui-rpt-preview-qr-label',      'Label text below QR code',                                                        'font-size: 6px; white-space: nowrap'],
              ['.stui-rpt-preview-spacer',        'Flex spacer pushing params block to the right',                                   'flex: 1 1 auto'],
              ['.stui-rpt-preview-title-block',   'Absolutely centered title overlay (pointer-events: none)',                        'position: absolute; left/right/top/bottom: 0; flex-direction: column; align/justify: center'],
              ['.stui-rpt-preview-title',         'Report title — brand color via inline style',                                     'font-size: 16px; font-weight: 700; Rajdhani Clear; uppercase; letter-spacing: 1px'],
              ['.stui-rpt-preview-subtitle',      'Group subtitle below title — brand color via inline style',                       'font-size: 13px; font-weight: 700; Rajdhani Clear; uppercase'],
              ['.stui-rpt-preview-record-count',  'Row count below title',                                                           'font-size: 8px; margin-top: 2px'],
              ['.stui-rpt-preview-params',        'Right-side parameter grid — 3 col × 2 row container',                            'flex: 0 0 auto; display: flex; flex-direction: column; gap: 2px'],
              ['.stui-rpt-preview-params-row',    'Single row of param slots',                                                       'display: flex; gap: 6px'],
              ['.stui-rpt-preview-param-slot',    'Individual param cell — width set inline (dynamic)',                              'height: 34px; overflow: hidden; padding-left: 6px'],
              ['.stui-rpt-preview-param-label',   'Param field label (5px — PDF mirror constant)',                                   'font-size: 5px; font-weight: 700; border-bottom: 0.5px'],
              ['.stui-rpt-preview-param-value',   'Param field value (4px — PDF mirror constant)',                                   'font-size: 4px; white-space: nowrap; overflow: hidden'],
              ['.stui-rpt-preview-param-none',    '"(none)" italic placeholder when param is empty',                                 'font-size: 6px; font-style: italic'],
              ['.stui-rpt-preview-param-overflow','Overflow indicator when values exceed slot height',                               'font-size: 4px; font-style: italic'],
              ['.stui-rpt-preview-table-wrap',    'Data table scroll/overflow wrapper',                                              'flex: none; overflow-x: visible'],
              ['.stui-rpt-preview-table',         'PDF-mirror data table (7px header, 8px body)',                                    'border-collapse: collapse; table-layout: fixed; width: 100%'],
              ['.stui-rpt-preview-thead',         'Sticky thead wrapper',                                                            'position: sticky; top: 0; z-index: 10'],
              ['.stui-rpt-preview-th',            'Header cell — width + textAlign set inline (dynamic)',                           'padding: 4px 3px; font-size: 7px; background: var(--color-gray-200); uppercase'],
              ['.stui-rpt-preview-th-line',       'Label line block inside th with reserved min-height',                            'display: block; min-height: 1em'],
              ['.stui-rpt-preview-th-sort',       'Sort-field indicator below column label',                                        'display: block; font-size: 6px; font-weight: 400; color: gray-400'],
              ['.stui-rpt-preview-td',            'Data cell — textAlign set inline (dynamic)',                                     'padding: 3px 4px; font-size: 8px; Helvetica; white-space: nowrap'],
              ['.stui-rpt-preview-td.wrap',       'Wrappable cell variant (Note / Comment columns)',                                'vertical-align: top; white-space: normal'],
              ['.stui-rpt-preview-empty-row',     'Empty state row when report has no results',                                     'padding: var(--spacing-xl); text-align: center; color: gray-400'],
              ['.stui-rpt-preview-footer',        'Page footer — org left, WM logo right',                                         'display: flex; justify-content: space-between; border-top: 2px solid #1a5568; font-size: 9px'],
              ['.stui-rpt-preview-footer-org',    'Left footer block — org name + optional icon',                                   'display: flex; align-items: center; gap: 4px'],
              ['.stui-rpt-preview-footer-wm',     'Right footer block — WM logo + text',                                           'display: flex; align-items: center; gap: 6px'],
            ]}
          />
          <CSSRef file="STUI.css" selector=".stui-rpt-preview-container / .stui-rpt-preview-header / .stui-rpt-preview-table / .stui-rpt-preview-footer" />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-rpt-preview-container" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Usage ────────────────────────────────────────────── */

const AUDIT_COLS = [
  { key: 'pageName',         label: 'Page Name'     },
  // Component slugs are long file paths: keep them on one line, full path on hover
  { key: 'slug',             label: 'Slug',          renderCell: (row) => <div className="stui-truncate" title={row.slug}>{row.slug}</div> },
  { key: 'cssFiles',         label: 'CSS Files'     },
  { key: 'inlineStyleCount', label: 'Inline Styles' },
  { key: 'status',           label: 'Status'        },
];

// Labels for the status the scanner reports. 'no-file' means no page file was resolved for
// the nav destination, so nothing was scanned — never read it as the page being clean.
const AUDIT_STATUS_LABELS = {
  standardized: 'Standardized',
  partial:      'Partial',
  'no-file':    'No File',
};

const auditCellValue = (page, key) => {
  if (key === 'cssFiles')         return page.cssFiles?.length ? page.cssFiles.join(', ') : '(none)';
  if (key === 'inlineStyleCount') return String(page.inlineStyleCount ?? 0);
  if (key === 'status') return AUDIT_STATUS_LABELS[page.status] || page.status || 'Unknown';
  return String(page[key] ?? '');
};

const DOC_ONLY_COLS = [
  { key: 'name',         label: 'Class Name'    },
  { key: 'referencedAs', label: 'Referenced As' },
];

const docOnlyCellValue = (row, key) =>
  key === 'referencedAs'
    ? (row.appliedOnPage ? 'Styles the page' : 'Named only')
    : String(row[key] ?? '');

const UsageTab = () => {
  const [auditData, setAuditData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Both grids are the real FilterableDataGrid (Column Filter menus), fed display-value rows so
  // filters and sort act on exactly what is shown. Inline Styles stays a number column.
  const [auditFilters, setAuditFilters] = useState({});
  const [docFilters, setDocFilters] = useState({});
  const auditRows = useMemo(() => (auditData || []).map((page, i) => ({
    id: i + 1,
    pageName: auditCellValue(page, 'pageName'),
    slug: page.slug ? `/${page.slug}` : '—',
    cssFiles: page.cssFiles?.length ? page.cssFiles.join(', ') : 'none',
    inlineStyleCount: page.inlineStyleCount ?? 0,
    status: auditCellValue(page, 'status'),
  })), [auditData]);
  const docRows = useMemo(() => (healthData?.docOnlyStuiClasses || []).map((row, i) => ({
    id: i + 1, name: `.${row.name}`, referencedAs: docOnlyCellValue(row, 'referencedAs'),
  })), [healthData]);
  const auditShown = applyColumnFilters(auditRows, auditFilters);
  const docShown = applyColumnFilters(docRows, docFilters);

  const refresh = async () => {
    setLoading(true);
    setMenuOpen(false);
    try {
      const [auditRes, healthRes] = await Promise.all([
        fetch(API_BASE_URL + '/api/devfiles/stui-audit'),
        fetch(API_BASE_URL + '/api/devfiles/css-health'),
      ]);
      setAuditData(await auditRes.json());
      setHealthData(await healthRes.json());
    } catch (e) {
      console.error('Usage tab fetch failed:', e);
    }
    setLoading(false);
  };

  // Mount loads the data; nothing is filtered yet, so only the menu action clears filters.
  useEffect(() => { refresh(); }, []);

  const resetAndRefresh = () => {
    setAuditFilters({});
    setDocFilters({});
    refresh();
  };

  return (
    <div className="stui-preview-zone stui-tab-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div className="stui-detail-section-header" style={{ flex: 1, margin: 0 }}>CSS Usage Audit</div>
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {menuOpen && (
            <div className="stui-action-menu-dropdown">
              <button className="stui-action-menu-item" onClick={resetAndRefresh}><RefreshCw size={16} /> Refresh</button>
            </div>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--color-gray-500)' }}>Page-level CSS file usage and inline style counts across DAWN</p>

      {loading && <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-gray-500)', fontSize: 13 }}>Scanning pages…</div>}

      {!loading && auditData && (
        <>
          {/* Grid 1: Page Audit */}
          <div style={{ marginBottom: 32 }}>
            <div className="stui-detail-section-header">
              Page Audit — {auditShown.length}{auditShown.length !== auditRows.length ? ` of ${auditRows.length}` : ''} pages
            </div>
            <FilterableDataGrid
              data={auditShown}
              allData={auditRows}
              columns={AUDIT_COLS}
              filters={auditFilters}
              setFilters={setAuditFilters}
            />
          </div>

          {/* Grid 2: CSS Health Summary */}
          {healthData && (
            <div style={{ marginBottom: 32 }}>
              <div className="stui-detail-section-header">CSS Health Summary</div>
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-no-column-resize stui-w-full">
                    <thead>
                      <tr>
                        <th>STUI.css Classes</th>
                        <th>STUI Used</th>
                        <th>App.css Classes</th>
                        <th>App.css Used</th>
                        <th>Total Inline Styles</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{healthData.stuiTotal}</td>
                        <td>{healthData.stuiUsed} <span className="stui-label-xs-muted">({Math.round(healthData.stuiUsed / healthData.stuiTotal * 100)}%)</span></td>
                        <td>{healthData.appTotal}</td>
                        <td>{healthData.appUsed} <span className="stui-label-xs-muted">({Math.round(healthData.appUsed / healthData.appTotal * 100)}%)</span></td>
                        <td className="stui-text-error">{healthData.totalInlineStyles.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Grid 3: Unused STUI Classes */}
          {healthData?.unusedStuiClasses?.length > 0 && (
            <div className="stui-mb-20">
              <div className="stui-detail-section-header">Unused STUI.css Classes ({healthData.unusedStuiClasses.length})</div>
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-no-column-resize stui-w-full">
                    <thead>
                      <tr><th>Class Name</th></tr>
                    </thead>
                    <tbody>
                      {healthData.unusedStuiClasses.map((cls, i) => (
                        <tr key={i}>
                          <td className="stui-mono">.{cls}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Grid 4: classes no page but this one references. Kept apart from Grid 3 because
              they are not safe deletions — see the Referenced As column. */}
          {healthData?.docOnlyStuiClasses?.length > 0 && (
            <div>
              <div className="stui-detail-section-header">
                STUI.css Classes Used Only on the STUI Page — {docShown.length}
                {docShown.length !== docRows.length ? ` of ${docRows.length}` : ''} classes
              </div>
              <p className="stui-hint">
                Only the STUI page references these classes. Styles the page means the class sits in a
                className here - real usage with a single consumer, mostly style-guide scaffolding.
                Named only means it appears as a string but never provably in a className, which
                includes classes reached through a variable and ones a library defined at runtime.
                Check the Elements tab for the name before removal.
              </p>
              <FilterableDataGrid
                data={docShown}
                allData={docRows}
                columns={DOC_ONLY_COLS}
                filters={docFilters}
                setFilters={setDocFilters}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── Tab: Standard View ────────────────────────────────────── */

const STANDARD_VIEW_SECTIONS = [
  { id: 'composer-select',       label: 'Composer Select'       },
  { id: 'datasheet-extras',      label: 'Datasheet Extras'      },
  { id: 'detail-section-header', label: 'Detail Section Header' },
  { id: 'fk-select',             label: 'FK Select Modal'       },
  { id: 'form-fields',           label: 'Form Fields'           },
  { id: 'list-layout',           label: 'List View Layout'      },
  { id: 'module-header-crud',    label: 'Module Header CRUD'    },
  { id: 'pagination',            label: 'Pagination'            },
  { id: 'related-tables',        label: 'Related Tables'        },
  { id: 'searchselect',          label: 'SearchSelect'          },
];

const DetailSectionHeaderConfigurator = () => {
  const [title, setTitle] = useState('General Information');
  const output = `stui-detail-section-header · label "${title || 'Section Title'}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Label</span>
        <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 220 }} />
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'flex-start', padding: '14px 18px' }}>
        <div className="stui-detail-section-header" style={{ margin: 0, width: '100%' }}>{title || 'Section Title'}</div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const StandardViewTab = ({ usageMap, pageSlugMap, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('detail-section-header');
  const [listSelected, setListSelected] = useState(1);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [ssOpen, setSsOpen] = useState(false);
  const [ssValue, setSsValue] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [relatedTab, setRelatedTab] = useState(0);
  const [relatedCollapsed, setRelatedCollapsed] = useState(false);

  const listItems = [
    { id: 1, primary: 'AI-100', secondary: 'Analog Input' },
    { id: 2, primary: 'DI-201', secondary: 'Digital Input' },
    { id: 3, primary: 'AO-302', secondary: 'Analog Output' },
    { id: 4, primary: 'DO-410', secondary: 'Digital Output' },
  ];
  const ssOptions = ['Panel A', 'Panel B', 'Panel C', 'MCC-001', 'MCC-002'];
  const composerModules = ['Building', 'Drawing', 'Tag', 'Panel', 'Source'];

  return (
    <div className="stui-preview-zone stui-tab-body">
      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 240 }}>
          {STANDARD_VIEW_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {/* ── Detail Section Header ─────────────────────────────── */}
      {activeSection === 'detail-section-header' && (
        <Section title="Detail Section Header" description="Teal bar that labels groups of fields in the detail panel. Spans full row width, sits above each form field group." cssFile={CSS_LOCATIONS.detailSectionHeader.file} cssSelector={CSS_LOCATIONS.detailSectionHeader.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ maxWidth: 520 }}>
            <div className="stui-detail-section-header">General Information</div>
            <div className="stui-detail-section-header">Tag Information</div>
            <div className="stui-detail-section-header">Assignment Details</div>
            <CSSRef file="STUI.css" selector=".stui-detail-section-header" />
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Property', 'Value', 'Notes']}
            rows={[
              ['background-color', 'var(--color-primary)', 'WM Teal — #1a5568'],
              ['color', 'white', ''],
              ['font-family', 'var(--font-secondary)', 'Rajdhani Clear / Roboto Condensed'],
              ['font-size', 'var(--text-base)', '1rem'],
              ['font-weight', '500', ''],
              ['padding', 'var(--spacing-sm) var(--spacing-lg)', '0.5rem 1rem'],
              ['border-radius', 'var(--radius-sm)', ''],
              ['margin', 'var(--spacing-md) 0', 'top + bottom — :first-child resets top to 0'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-detail-section-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DetailSectionHeaderConfigurator />
        </Section>
      )}

      {/* ── List View Layout ──────────────────────────────────── */}
      {activeSection === 'list-layout' && (
        <Section title="List View Layout" description="Two-panel split: resizable list panel on the left, detail panel on the right. Collapses to icon-only strip. Detail grid uses 24-column span system." cssFile={CSS_LOCATIONS.listLayout.file} cssSelector={CSS_LOCATIONS.listLayout.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', height: 260, display: 'flex', overflow: 'hidden' }}>
            <div className={`stui-list-panel${listCollapsed ? ' collapsed' : ''}`} style={{ width: listCollapsed ? 32 : 180, minWidth: listCollapsed ? 32 : 140, transition: 'width 0.2s' }}>
              <div className="stui-list-panel-header">
                <button className="stui-list-panel-collapse-btn" onClick={() => setListCollapsed(c => !c)} title={listCollapsed ? 'Expand' : 'Collapse'}>
                  {listCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
              </div>
              {!listCollapsed && (
                <div className="stui-list-scroll">
                  {listItems.map(item => (
                    <button key={item.id} className={`stui-list-item ${listSelected === item.id ? 'selected' : ''}`} onClick={() => setListSelected(item.id)}>
                      <span className="stui-list-item-primary">{item.primary}</span>
                      <span className="stui-list-item-secondary">{item.secondary}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="stui-detail-panel" style={{ flex: 1 }}>
              <div className="stui-detail-content" style={{ height: '100%' }}>
                <div className="stui-detail-grid" style={{ padding: 8 }}>
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header stui-m-0">General Information</div>
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Tag Number</label>
                    <input className="stui-field-input" value={listItems.find(i => i.id === listSelected)?.primary || ''} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Type</label>
                    <input className="stui-field-input" value={listItems.find(i => i.id === listSelected)?.secondary || ''} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Status</label>
                    <input className="stui-field-input" value="Active" readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-list-view / .stui-list-panel / .stui-list-panel-header / .stui-list-scroll" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Element', 'Notes']}
            rows={[
              ['.stui-list-view', 'flex container', 'wraps both panels; add .collapsed when list panel is collapsed'],
              ['.stui-list-panel', 'left panel', 'resizable; add .collapsed for icon-only strip mode'],
              ['.stui-list-panel-header', 'panel header', 'contains collapse toggle button'],
              ['.stui-list-panel-collapse-btn', 'toggle button', 'ChevronLeft/Right icon; toggles collapsed state'],
              ['.stui-list-scroll', 'scrollable list area', 'flex:1, overflow-y:auto'],
              ['.stui-list-item', '<button>', 'full-width list row; add .selected for active row'],
              ['.stui-list-item-primary', 'primary text', 'bold, truncated on overflow'],
              ['.stui-list-item-secondary', 'secondary text', 'small muted label below primary'],
              ['.stui-detail-panel', 'right panel', 'flex:1; add .resizing during column drag'],
              ['.stui-detail-content', 'scrollable detail area', 'padding 16px'],
              ['.stui-detail-grid', 'CSS grid', '24-column grid; children use .stui-col-N'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-list-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ListLayoutConfigurator />
        </Section>
      )}

      {/* ── Form Fields ───────────────────────────────────────── */}
      {activeSection === 'form-fields' && (
        <Section title="Form Fields" description="Field group container (.stui-field-group) wraps a label + input pair. Label positions: top (default), left, right, bottom. Input states: normal, dirty, fk, derived, readonly, display." cssFile={CSS_LOCATIONS.formFields.file} cssSelector={CSS_LOCATIONS.formFields.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Input states</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 20px', maxWidth: 640 }}>
            <div className="stui-field-group">
              <label>Normal</label>
              <input className="stui-field-input" defaultValue="Sample value" />
            </div>
            <div className="stui-field-group">
              <label>Dirty (unsaved)</label>
              <input className="stui-field-input dirty" defaultValue="Modified value" />
            </div>
            <div className="stui-field-group">
              <label>FK Field</label>
              <div className="stui-field-input fk" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span>Panel A</span>
                <Link2 size={12} className="stui-c-muted" />
              </div>
            </div>
            <div className="stui-field-group">
              <label>Derived (read-only computed)</label>
              <input className="stui-field-input derived" value="AUTO-2024" readOnly />
            </div>
            <div className="stui-field-group">
              <label>Display Only</label>
              <span className="stui-field-display">No edit</span>
            </div>
            <div className="stui-field-group">
              <label>Compact (xs)</label>
              <input className="stui-field-input stui-field-input-xs" defaultValue="Header bar control" />
            </div>
            <div className="stui-field-group">
              <label>Readonly (non-editable column)</label>
              <div className="stui-field-readonly">System managed</div>
            </div>
          </div>
          <div className="stui-subsection-divider">Label positions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
            <div className="stui-field-group">
              <label>Label Top (default)</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-left">
              <label>Label Left</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-right">
              <label>Label Right</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
            <div className="stui-field-group stui-label-bottom">
              <label>Label Bottom</label>
              <input className="stui-field-input" defaultValue="Value" />
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-field-group / .stui-field-input / .stui-field-display / .stui-field-readonly" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Element', 'Notes']}
            rows={[
              ['.stui-field-group', 'wrapper div', 'flex column; label + input pair'],
              ['.stui-field-group.stui-label-left', 'wrapper', 'row layout; label min-width 100px, right-aligned; input flexes to fill the row'],
              ['.stui-field-group.stui-label-right', 'wrapper', 'row-reverse layout'],
              ['.stui-field-group.stui-label-bottom', 'wrapper', 'column-reverse — label below input'],
              ['.stui-field-group.stui-field-inline', 'wrapper', 'row at natural widths — toolbar fields; no label width, input keeps its own'],
              ['.stui-field-input', '<input>/<textarea>/StuiSelect', 'standard text input styling'],
              ['.stui-field-input.dirty', '', 'amber border + background — unsaved change indicator'],
              ['.stui-field-input.fk', '', 'dashed border — indicates FK lookup field'],
              ['.stui-field-input.derived', '', 'italic, gray bg — computed/auto-filled field'],
              ['.stui-field-input.stui-field-input-xs', '<input>/StuiSelect', 'compact 24px control for inline-add rows inside a section header bar'],
              ['.stui-field-display', '<span>', 'gray bg, light border — display-only value; a blank value keeps one line\'s height, and in a field group it takes the input height'],
              ['.stui-field-readonly', 'div', 'gray bg, solid border — system-managed, non-editable'],
              ['.stui-field-prefix', 'div', 'with .stui-field-display .stui-field-readonly in .stui-row-8 — locked lead-in sized to its text, the input beside it takes the rest (email subject prefix)'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-field-group" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FormFieldsConfigurator />
        </Section>
      )}

      {/* ── SearchSelect ──────────────────────────────────────── */}
      {activeSection === 'searchselect' && (
        <Section title="SearchSelect" description="The STUI dropdown. A trigger div plus a list portalled to the body, drawn the same for StuiSelect (<select> markup) and SearchSelect (options as data). Search box above 10 options; full keyboard support; .active is the highlighted row, .selected the current value. States: open, disabled, dirty." cssFile={CSS_LOCATIONS.searchSelect.file} cssSelector={CSS_LOCATIONS.searchSelect.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 220px)', gap: '12px 24px', marginBottom: 12 }}>
            <div>
              <div className="stui-label">Live, short list (no search)</div>
              <StuiSelect className="stui-field-input" value={ssValue} onChange={e => setSsValue(e.target.value)}>
                <option value="">-- Select --</option>
                {ssOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </StuiSelect>
            </div>
            <div>
              <div className="stui-label">Live, long list (search)</div>
              <SearchSelect value={ssValue} options={[...ssOptions, ...Array.from({ length: 20 }, (_, i) => `Panel ${i + 1}`)]} isLookup onChange={v => setSsValue(v || '')} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 220px)', gap: '12px 24px' }}>
            <div>
              <div className="stui-searchselect-trigger" onClick={() => setSsOpen(o => !o)}>
                <span className={`stui-searchselect-value ${ssValue ? '' : 'placeholder'}`}>{ssValue || '-- Select --'}</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              {ssOpen && (
                <div className="stui-searchselect-dropdown" style={{ position: 'relative', zIndex: 1, marginTop: 2 }}>
                  <div className="stui-searchselect-search">
                    <Search size={13} className="stui-searchselect-search-icon" />
                    <input className="stui-searchselect-search-input" placeholder="Search…" />
                  </div>
                  <div className="stui-searchselect-options">
                    {ssOptions.map(opt => (
                      <div key={opt} className={`stui-searchselect-option ${ssValue === opt ? 'selected' : ''}`} onClick={() => { setSsValue(opt); setSsOpen(false); }}>
                        <span className="stui-searchselect-option-check">{ssValue === opt && <Check size={12} />}</span>
                        <span className="stui-searchselect-option-text">{opt}</span>
                      </div>
                    ))}
                    <div className="stui-searchselect-empty" style={{ display: 'none' }}>No matches found</div>
                  </div>
                </div>
              )}
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger" />
            </div>
            <div className="stui-col-8">
              <div className="stui-searchselect-trigger open">
                <span className="stui-searchselect-value">Panel A</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.open" />
              <div className="stui-searchselect-trigger disabled">
                <span className="stui-searchselect-value placeholder">-- Select --</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.disabled" />
              <div className="stui-searchselect-trigger dirty">
                <span className="stui-searchselect-value">Panel B</span>
                <ChevronDown size={14} className="stui-searchselect-chevron" />
              </div>
              <CSSRef file="STUI.css" selector=".stui-searchselect-trigger.dirty" />
            </div>
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-searchselect-trigger', 'clickable trigger div — flex row, styled like input'],
              ['.stui-searchselect-trigger.open', 'primary border + shadow when dropdown open'],
              ['.stui-searchselect-trigger.disabled', 'gray bg, not-allowed cursor'],
              ['.stui-searchselect-trigger.dirty', 'amber border + bg — unsaved change'],
              ['.stui-searchselect-value.placeholder', 'gray text when nothing selected'],
              ['.stui-searchselect-value-text / -sizer', 'the shown text, and an invisible copy of the longest option that holds an auto-width trigger steady'],
              ['.stui-searchselect-chevron', 'hidden — the trigger draws the shared dropdown chevron'],
              ['.stui-searchselect-dropdown', 'fixed-position panel (portaled to body), max-height 280px'],
              ['.stui-searchselect-search', 'search row at top of dropdown, shown above 10 options'],
              ['.stui-searchselect-options', 'scrollable list; fills the height the search row leaves'],
              ['.stui-searchselect-option', 'row item; .active is the highlight, .selected adds primary bg, .disabled dims'],
              ['.stui-searchselect-option-check', '14px check icon placeholder — shows Check when .selected'],
              ['.stui-searchselect-clear', 'clear-selection row (special option style)'],
              ['.stui-searchselect-empty', 'no-results message'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-searchselect-trigger" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <SearchSelectConfigurator />
        </Section>
      )}

      {/* ── Related Tables ────────────────────────────────────── */}
      {activeSection === 'related-tables' && (
        <Section title="Related Tables" description="Collapsible panel below the detail form showing FK-related records. Has a resizable header, tab bar with counts, and a content area for the data grid." cssFile={CSS_LOCATIONS.relatedTables.file} cssSelector={CSS_LOCATIONS.relatedTables.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', maxWidth: 640 }}>
            <div className={`stui-related-section ${relatedCollapsed ? 'collapsed' : ''}`} style={{ height: relatedCollapsed ? 'auto' : 180 }}>
              <div className={`stui-related-header ${!relatedCollapsed ? 'resizable' : ''}`}>
                {!relatedCollapsed && (
                  <div className="stui-resize-grip" style={{ position: 'static', width: 'auto', height: 'auto', display: 'flex', alignItems: 'center' }}>
                    <GripHorizontal size={13} className="stui-c-muted" />
                  </div>
                )}
                <button className="stui-related-collapse-btn" onClick={() => setRelatedCollapsed(c => !c)} title={relatedCollapsed ? 'Expand' : 'Collapse'}>
                  {relatedCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                <div className="stui-related-tabs">
                  {['Drawings', 'Tags', 'Equipment'].map((tab, idx) => (
                    <button key={tab} className={`stui-related-tab ${relatedTab === idx ? 'active' : ''}`} onClick={() => setRelatedTab(idx)}>
                      {tab}
                      <span className="stui-related-tab-count">{[12, 3, 7][idx]}</span>
                    </button>
                  ))}
                </div>
              </div>
              {!relatedCollapsed && (
                <div className="stui-related-content stui-related-body">
                  {relatedTab === 0 && 'Showing 12 drawings associated with this record…'}
                  {relatedTab === 1 && 'Showing 3 tags associated with this record…'}
                  {relatedTab === 2 && 'Showing 7 equipment items associated with this record…'}
                </div>
              )}
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-related-section / .stui-related-header / .stui-related-tabs / .stui-resize-grip" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-related-section', 'outer container; add .collapsed when folded'],
              ['.stui-related-header', 'header bar — contains resize grip, collapse btn, tabs; add .resizable for row-resize cursor'],
              ['.stui-resize-grip', 'drag handle at top of header for vertical resize'],
              ['.stui-related-collapse-btn', 'icon button to toggle collapsed state'],
              ['.stui-related-tabs', 'flex tab bar'],
              ['.stui-related-tab', 'tab button; add .active for selected, .loading while data loads'],
              ['.stui-related-tab-count', 'small pill showing record count — teal when tab is active'],
              ['.stui-related-content', 'flex content area below header; contains the data grid'],
              ['.stui-related-loading', 'centered spinner row shown while data loads'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-related-section" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <RelatedTablesConfigurator />
        </Section>
      )}

      {/* ── Datasheet Extras ──────────────────────────────────── */}
      {activeSection === 'datasheet-extras' && (
        <Section title="Datasheet Extras" description="Nested selectors inside .stui-data-grid for edit mode, delete mode, editable cells, boolean cells, and the column resize handle." cssFile={CSS_LOCATIONS.datasheetExtras.file} cssSelector={CSS_LOCATIONS.datasheetExtras.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Delete mode — selected rows</div>
          <table className="stui-data-grid delete-mode stui-full-mb">
            <thead><tr><th style={{ width: 40 }}></th><th>Tag</th><th>Description</th><th>Panel</th></tr></thead>
            <tbody>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>AI-100</td><td>Flow Transmitter</td><td>Panel A</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>DI-201</td><td>Level Switch</td><td>Panel B</td></tr>
              <tr className="selected-for-delete"><td className="delete-checkbox-col"><input type="checkbox" checked readOnly /></td><td>AO-302</td><td>Control Valve</td><td>Panel A</td></tr>
              <tr><td className="delete-checkbox-col"><input type="checkbox" /></td><td>DO-410</td><td>Motor Starter</td><td>Panel C</td></tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid tbody tr.selected-for-delete" />
          <div className="stui-subsection-divider">Edit mode — editable cells</div>
          <table className="stui-data-grid edit-mode stui-full-mb">
            <thead><tr><th>Tag</th><th>Description</th><th>Panel</th><th>Boolean</th></tr></thead>
            <tbody>
              <tr>
                <td className="editable-cell"><input defaultValue="AI-100" className="stui-editable-input" /></td>
                <td className="editable-cell modified"><input defaultValue="Updated description" className="stui-editable-input" /></td>
                <td className="readonly-cell">Panel A</td>
                <td className="cell-boolean"><input type="checkbox" checked readOnly /></td>
              </tr>
            </tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid td.editable-cell.modified" />
          <div className="stui-subsection-divider">Column resize handle</div>
          <table className="stui-data-grid" style={{ width: 360 }}>
            <thead>
              <tr>
                <th style={{ position: 'relative' }}>Tag Number <div className="stui-column-resize-handle" /></th>
                <th style={{ position: 'relative' }}>Description <div className="stui-column-resize-handle" /></th>
              </tr>
            </thead>
            <tbody><tr><td>AI-100</td><td>Flow Transmitter</td></tr></tbody>
          </table>
          <CSSRef file="STUI.css" selector=".stui-data-grid th .stui-column-resize-handle" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Notes']}
            rows={[
              ['.stui-data-grid.delete-mode tbody tr:hover', 'pointer cursor on all rows in delete mode'],
              ['.stui-data-grid tbody tr.selected-for-delete', 'red bg tint + red text — row marked for deletion'],
              ['.stui-data-grid td.editable-cell', 'zero-padding cell; contains raw input'],
              ['.stui-data-grid td.editable-cell.modified', 'amber bg — cell value differs from saved'],
              ['.stui-data-grid td.cell-boolean', 'centered checkbox display'],
              ['.stui-data-grid td.readonly-cell', 'gray bg — non-editable in edit mode'],
              ['.stui-data-grid th .stui-column-resize-handle', '4px drag strip at right edge of header cell'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-editable-input" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DatasheetExtrasConfigurator />
        </Section>
      )}

      {/* ── Pagination ────────────────────────────────────────── */}
      {activeSection === 'pagination' && (
        <Section title="Pagination" description="Page navigation bar docked at the bottom of the datasheet view. Flex row with prev/next controls on the left and record-range info on the right." cssFile={CSS_LOCATIONS.pagination.file} cssSelector={CSS_LOCATIONS.pagination.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', maxWidth: 520 }}>
            <div className="stui-pagination">
              <div className="stui-pagination-controls">
                <button className="stui-pagination-btn" disabled><ChevronLeft size={14} /></button>
                <button className="stui-pagination-btn active">1</button>
                <button className="stui-pagination-btn">2</button>
                <button className="stui-pagination-btn">3</button>
                <button className="stui-pagination-btn"><ChevronRight size={14} /></button>
              </div>
              <span className="stui-pagination-info">1–50 of 143</span>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-pagination / .stui-pagination-controls / .stui-pagination-btn / .stui-pagination-info" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-pagination', 'flex row container; border-top; space-between'],
              ['.stui-pagination-controls', 'flex row of buttons'],
              ['.stui-pagination-btn', 'page/nav button; add .active for current page (teal fill)'],
              ['.stui-pagination-btn:disabled', '40% opacity, not-allowed cursor'],
              ['.stui-pagination-info', 'small gray text: "1–50 of 200"'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-pagination" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <PaginationConfigurator />
        </Section>
      )}

      {/* ── FK Select Modal ───────────────────────────────────── */}
      {activeSection === 'fk-select' && (
        <Section title="FK Select Modal" description="Full-screen overlay modal for browsing and selecting a foreign key record. Supports stacking up to 5 levels deep." cssFile={CSS_LOCATIONS.fkSelect.file} cssSelector={CSS_LOCATIONS.fkSelect.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">Inline — Not Overlaid</div>
          <div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', maxWidth: 560, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            <div className="stui-fk-select-header">
              <div className="stui-row-8">
                <span className="stui-label-xs-muted">Building</span>
                <span className="stui-label-xs-muted"> → </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-900)' }}>Drawing</span>
              </div>
              <div className="stui-flex-4">
                <button className="stui-crud-btn" title="Open full page"><Link2 size={14} /></button>
                <button className="stui-crud-btn" title="Close"><X size={16} /></button>
              </div>
            </div>
            <div className="stui-fk-select-body" style={{ padding: 16, minHeight: 120 }}>
              <div className="stui-detail-grid">
                <div className="stui-field-group stui-col-8"><label>Drawing No.</label><input className="stui-field-input" value="DWG-001" readOnly /></div>
                <div className="stui-field-group stui-col-8"><label>Revision</label><input className="stui-field-input" value="B" readOnly /></div>
                <div className="stui-field-group stui-col-8"><label>Discipline</label><input className="stui-field-input" value="Electrical" readOnly /></div>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-fk-select-overlay / .stui-fk-select / .stui-fk-select-header / .stui-fk-select-body" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-fk-select-overlay', 'fixed full-screen dimmed backdrop; flex centering'],
              ['.stui-fk-select', 'modal card — 680px wide, max 80vh height; stacked via translate()'],
              ['.stui-fk-select-header', 'flex row: breadcrumb on left, action buttons on right'],
              ['.stui-fk-select-body', 'flex column content area — holds stui-detail-grid + stui-related-section'],
            ]}
          />
          <div className="stui-hint">Stacking</div>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
            Each FK click pushes a new item onto the stack. The modal offsets by 15px per level via <code>transform: translate(Npx, Npx)</code>. Back button pops the stack; clicking the overlay calls back/close.
          </div>
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-fk-select" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FkSelectConfigurator />
        </Section>
      )}

      {/* ── Composer Select ───────────────────────────────────── */}
      {activeSection === 'composer-select' && (
        <Section title="Composer Select" description="Config-page dropdown used in Standard View Composer and Data Entry Composer to pick which table or module to configure. Based on the lvc-custom-dropdown pattern." cssFile={CSS_LOCATIONS.composerSelect.file} cssSelector={CSS_LOCATIONS.composerSelect.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: '16px 0', position: 'relative' }}>
            <div className="stui-composer-select">
              <label className="stui-composer-select-label">Module</label>
              <div className={`stui-composer-select-trigger ${composerOpen ? 'open' : ''}`} onClick={() => setComposerOpen(o => !o)}>
                <span>Building</span>
                <ChevronDown size={14} />
              </div>
              {composerOpen && (
                <div className="stui-composer-select-panel" style={{ position: 'relative', marginTop: 4, top: 'auto', left: 'auto' }}>
                  <input className="stui-composer-select-search" placeholder="Search modules…" autoFocus />
                  <div className="stui-composer-select-options">
                    {composerModules.map((m, i) => (
                      <div key={m} className={`stui-composer-select-option ${i === 0 ? 'selected' : ''}`} onClick={() => setComposerOpen(false)}>
                        <span className="stui-composer-select-option-name">{m}</span>
                        {i < 3 && <span className="stui-composer-select-option-icons" title="Has config">●</span>}
                      </div>
                    ))}
                  </div>
                  <div className="stui-composer-select-legend">
                    <span>● Has config</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-composer-select / .stui-composer-select-trigger / .stui-composer-select-panel / .stui-composer-select-options" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-composer-select', 'flex row wrapper — label + trigger; position:relative for panel anchor'],
              ['.stui-composer-select-label', 'uppercase gray label to the left of the trigger'],
              ['.stui-composer-select-trigger', 'button-style trigger; add .open for primary border'],
              ['.stui-composer-select-panel', 'absolute dropdown panel; appears below trigger'],
              ['.stui-composer-select-search', 'full-width search input at top of panel'],
              ['.stui-composer-select-options', 'scrollable option list; max-height 300px'],
              ['.stui-composer-select-option', 'option row; add .selected for teal text + bg'],
              ['.stui-composer-select-option-name', 'flex:1 truncated option label'],
              ['.stui-composer-select-option-icons', 'small icon cluster at right edge of option'],
              ['.stui-composer-select-legend', 'gray footnote bar at bottom of panel'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-composer-select" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ComposerSelectConfigurator />
        </Section>
      )}

      {/* ── Module Header CRUD ────────────────────────────────── */}
      {activeSection === 'module-header-crud' && (
        <Section title="Module Header CRUD" description="Flex containers for the CRUD and datasheet edit/save buttons inside .stui-module-header-actions. Two variants: list view CRUD and datasheet controls." cssFile={CSS_LOCATIONS.moduleHeaderCrud.file} cssSelector={CSS_LOCATIONS.moduleHeaderCrud.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-subsection-divider">List view CRUD</div>
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn" title="Add Record"><Plus size={18} /></button>
              <button className="stui-crud-btn" title="Edit Record"><Pencil size={18} /></button>
              <button className="stui-crud-btn disabled" title="Save Changes" disabled><Save size={18} /></button>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-module-header-crud" />
          <div className="stui-subsection-divider">Editing state — delete, cancel, save</div>
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-crud">
              <button className="stui-crud-btn stui-delete-btn" title="Delete Record"><Trash2 size={18} /></button>
              <button className="stui-crud-btn" title="Cancel"><X size={18} /></button>
              <button className="stui-crud-btn" title="Save Changes"><Save size={18} /></button>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-crud-btn.stui-delete-btn / .stui-crud-btn" />
          <div className="stui-subsection-divider">Datasheet controls</div>
          <div className="stui-cfg-demo-bar">
            <div className="stui-module-header-datasheet-controls">
              <div className="stui-module-header-crud">
                <button className="stui-crud-btn" title="Enable Editing"><Pencil size={18} /></button>
                <button className="stui-crud-btn disabled" title="Save Changes" disabled><Save size={18} /></button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-module-header-datasheet-controls" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Notes']}
            rows={[
              ['.stui-module-header-crud', 'flex row of CRUD icon buttons; gap 2px — used inside both list and datasheet views'],
              ['.stui-module-header-datasheet-controls', 'wrapper for datasheet edit+save group; gap 4px'],
              ['.stui-crud-btn', 'see Module Header tab — 30×30 icon button'],
              ['.stui-crud-btn.stui-delete-btn', 'red tint on hover — destructive action'],
              ['.stui-crud-btn.disabled', 'same as disabled attr — 0.4 opacity, not-allowed cursor'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-module-header-crud" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModuleHeaderCrudConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: Base & Reset ─────────────────────────────────────── */

const BASE_RESET_SECTIONS = [
  { id: 'body',           label: 'Body'           },
  { id: 'box-sizing',     label: 'Box Sizing'     },
  { id: 'form-controls',  label: 'Form Controls'  },
];

const BaseResetTab = ({ onChipClick }) => {
  const [activeSection, setActiveSection] = useState('body');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {BASE_RESET_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {activeSection === 'box-sizing' && (
        <Section title="Box Sizing" description="Universal rule that makes width and height include padding and border. Without this, padding adds to the declared width — breaking every layout." cssFile={CSS_LOCATIONS.baseReset.file} cssSelector={CSS_LOCATIONS.baseReset.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="stui-col-6">
              <div style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 600 }}>border-box (STUI) ✓</div>
              <div style={{ width: 200, padding: 16, border: '2px solid var(--color-primary)', boxSizing: 'border-box', background: 'var(--color-primary-tint)', fontSize: 11, color: 'var(--color-gray-700)', borderRadius: 'var(--radius-sm)'}}>
                width: 200px<br />padding included in width
              </div>
              <div className="stui-hint">rendered width: 200px</div>
            </div>
            <div className="stui-col-6">
              <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontWeight: 600 }}>content-box (browser default) ✗</div>
              <div style={{ width: 200, padding: 16, border: '2px solid var(--color-gray-300)', boxSizing: 'content-box', background: 'var(--color-gray-50)', fontSize: 11, color: 'var(--color-gray-700)', borderRadius: 'var(--radius-sm)'}}>
                width: 200px<br />padding added on top
              </div>
              <div className="stui-hint">rendered width: 236px (200 + 32 + 4)</div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector="*, *::before, *::after" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['*, *::before, *::after', 'box-sizing', 'border-box', 'Applies to every element and pseudo-element'],
            ]}
          />
          <Subsection label="Configurator" />
          <BoxSizingConfigurator />
        </Section>
      )}

      {activeSection === 'body' && (
        <Section title="Body" description="Sets the application's base font, weight, color, and anti-aliasing. All text inherits from this unless overridden." cssFile={CSS_LOCATIONS.baseBody.file} cssSelector={CSS_LOCATIONS.baseBody.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: '16px 20px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-white)', maxWidth: 480 }}>
            <p style={{ margin: 0, fontFamily: 'var(--font-secondary)', fontWeight: 500, color: 'var(--color-gray-800)', fontSize: 'var(--text-base)', WebkitFontSmoothing: 'antialiased' }}>
              The quick brown fox jumps over the lazy dog — 0O lI1 ao hn OQ GC 8B — Rajdhani Clear 500
            </p>
            <p style={{ margin: '8px 0 0', fontFamily: 'var(--font-secondary)', fontWeight: 500, color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)', WebkitFontSmoothing: 'antialiased' }}>
              Secondary text at var(--text-sm) — color-gray-500
            </p>
          </div>
          <CSSRef file="STUI.css" selector="body" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['body', 'margin',                   '0',                          'Remove browser default margin'],
              ['body', 'font-family',              'var(--font-secondary)',       'Rajdhani Clear / Roboto Condensed'],
              ['body', 'font-weight',              '500',                        ''],
              ['body', '-webkit-font-smoothing',   'antialiased',                'Thinner, crisper text on macOS/retina'],
              ['body', '-moz-osx-font-smoothing',  'grayscale',                  'Firefox equivalent'],
              ['body', 'color',                    'var(--color-gray-800)',       'Base text color — #1f2937'],
            ]}
          />
          <Subsection label="Configurator" />
          <BodyConfigurator />
        </Section>
      )}

      {activeSection === 'form-controls' && (
        <Section title="Form Controls" description="Browsers apply their own system font to inputs, selects, textareas, and buttons — overriding any body font. This rule forces them to inherit from the parent." cssFile={CSS_LOCATIONS.baseFormControls.file} cssSelector={CSS_LOCATIONS.baseFormControls.selector} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', padding: '16px 20px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-white)' }}>
            <input placeholder="Input" style={{ padding: '6px 10px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', fontSize: 13 }} />
            <StuiSelect style={{ padding: '6px 10px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
              <option>Select</option>
            </StuiSelect>
            <button style={{ padding: '6px 14px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', fontSize: 13, cursor: 'pointer' }}>Button</button>
            <textarea placeholder="Textarea" rows={2} style={{ padding: '6px 10px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', fontSize: 13, resize: 'none' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 6, fontStyle: 'italic' }}>
            All four elements render in Rajdhani Clear / Roboto Condensed — same as body text
          </div>
          <CSSRef file="STUI.css" selector="input, select, textarea, button" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Selector', 'Property', 'Value', 'Notes']}
            rows={[
              ['input, select, textarea, button', 'font-family', 'inherit', 'Picks up body font-family'],
              ['input, select, textarea, button', 'font-weight', 'inherit', 'Picks up body font-weight 500'],
            ]}
          />
          <Subsection label="Configurator" />
          <FormControlsConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Tab: CSS Output Test ──────────────────────────────────── */

// Registry: one entry per STUI class that has a configurator.
// Each renderer receives the parsed token array and returns JSX.
const CSS_OUTPUT_RENDERERS = {
  'stui-page-header': (tokens) => {
    const iconName  = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '') || 'FileSpreadsheet';
    const titleM    = (tokens.find(t => /^title "/.test(t))  || '').match(/"([^"]*)"/);
    const descM     = (tokens.find(t => /^desc "/.test(t))   || '').match(/"([^"]*)"/);
    const hasExport = tokens.some(t => t === 'with export action menu');
    const IconMap   = { FileSpreadsheet, FileText, FileDown };
    const IconComp  = IconMap[iconName] || FileSpreadsheet;
    return (
      <div className="stui-page-header">
        <div className="stui-row-md">
          <h1 className="stui-page-title">
            <IconComp size={20} />{titleM ? titleM[1] : 'Report Title'}
          </h1>
          {descM && <span className="stui-text-sm-muted">{descM[1]}</span>}
        </div>
        {hasExport && <DemoExportMenu />}
      </div>
    );
  },
  'stui-detail-section-header': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-detail-section-header stui-m-0">{labelM ? labelM[1] : 'Section Title'}</div>;
  },
  'stui-btn-sort-state': (tokens) => {
    const colM = (tokens.find(t => /^col "/.test(t)) || '').match(/"([^"]*)"/);
    const dir  = tokens.some(t => t === 'dir desc') ? '↓' : '↑';
    return <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 16 }}><span className="stui-btn-sort-state">{dir} {colM ? colM[1] : 'Column'}</span><button className="stui-btn-sort-reset">✕ Clear Sort</button></div>;
  },
  'stui-col': (tokens) => {
    const spanM = tokens.find(t => /^span /.test(t));
    const n     = spanM ? parseInt(spanM.replace('span ', '')) : 12;
    const rem   = 24 - n;
    return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, padding: 16 }}><div style={{ gridColumn: `span ${n}`, background: 'var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '6px 0', textAlign: 'center', color: 'white', fontSize: 12, fontWeight: 600 }}>{n}</div>{rem > 0 && <div style={{ gridColumn: `span ${rem}`, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', padding: '6px 0', textAlign: 'center', fontSize: 12, color: 'var(--color-gray-500)' }}>{rem}</div>}</div>;
  },
  'stui-module-header': (tokens) => {
    const iconM  = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '');
    const titleM = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const countM = tokens.find(t => /^count /.test(t));
    const IconMap = { Baby, FileSpreadsheet, FileText, Database };
    const IconComp = IconMap[iconM] || Baby;
    return <div className="stui-module-header-styleguideonly"><div className="stui-module-header-left"><IconComp size={20} className="stui-page-icon" /><h1>{titleM ? titleM[1] : 'Module'}</h1><span className="stui-module-header-meta">{countM ? countM.replace('count ', '') : '0'} records</span></div><div className="stui-module-header-actions"><button className="stui-crud-btn" disabled><Plus size={16} /></button><button className="stui-action-menu-btn disabled" disabled><GripVertical size={16} /></button></div></div>;
  },
  'stui-boolean-display': (tokens) => {
    const isTrue = tokens.some(t => t === 'value true');
    return <div className="stui-p-16"><div className="stui-boolean-display">{isTrue && <span className="stui-boolean-check">✓</span>}</div></div>;
  },
  'stui-breadcrumb': (tokens) => {
    const segs = tokens.join(' ').match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '')) || ['Projects', 'BU28', 'Current'];
    return <div className="stui-p-16"><div className="stui-breadcrumb" style={{ display: 'flex', alignItems: 'center' }}>{segs.map((s, i) => <React.Fragment key={i}>{i > 0 && <span className="stui-breadcrumb-arrow"> › </span>}<span className={i === segs.length - 1 ? 'stui-breadcrumb-current' : 'stui-breadcrumb-parent'}>{s}</span></React.Fragment>)}</div></div>;
  },
  'stui-badge': (tokens) => {
    const variantM = tokens.find(t => /^variant /.test(t));
    const variant  = variantM ? variantM.replace('variant ', '') : 'accepted';
    const labelM   = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-p-16"><span className={`stui-badge-${variant}`}>{labelM ? labelM[1] : variant}</span></div>;
  },
  'stui-tile': (tokens) => {
    const titleM    = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const clickable = tokens.some(t => t === 'clickable');
    const legend    = tokens.some(t => t === 'with legend');
    return <div className="stui-p-16"><div className={`stui-tile${clickable ? ' clickable' : ''}`} style={{ width: 200 }}><div className="stui-tile-header"><FileText size={16} className="stui-tile-icon" /><span className="stui-tile-title">{titleM ? titleM[1] : 'Tile'}</span></div><div className="stui-tile-content stui-text-placeholder">…chart…</div>{legend && <div className="stui-tile-legend"><span className="stui-legend-item"><span className="stui-legend-dot" style={{ background: 'var(--color-primary)' }} />Series A</span></div>}</div></div>;
  },
  'stui-stat-row': (tokens) => {
    const stats = tokens.map(t => { const m = t.match(/"([^:]+):([^"]+)"/); return m ? { label: m[1], value: m[2] } : null; }).filter(Boolean);
    const items = stats.length ? stats : [{ label: 'Rows', value: '1,284' }, { label: 'Pages', value: '43' }];
    return <div className="stui-p-16"><div className="stui-stat-row"><div className="stui-stat-group">{items.map((s, i) => <div key={i} className="stui-stat-item"><span className="stui-stat-label">{s.label}</span><span className="stui-stat-value">{s.value}</span></div>)}</div></div></div>;
  },
  'stui-label': (tokens) => {
    const textM = (tokens.find(t => /^text "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: 16, display: 'flex', gap: 8, alignItems: 'center' }}><span className="stui-label">{textM ? textM[1] : 'Label'}</span><StuiSelect className="stui-select"><option>Option</option></StuiSelect></div>;
  },
  'stui-checkbox': (tokens) => {
    const labelM  = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const checked = tokens.some(t => t === 'checked');
    return <div className="stui-p-16"><label className="stui-checkbox"><input type="checkbox" defaultChecked={checked} readOnly /> {labelM ? labelM[1] : 'Checkbox'}</label></div>;
  },
  'stui-sublabel': (tokens) => {
    const labelM    = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const sublabelM = (tokens.find(t => /^sublabel "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: 16, display: 'flex', flexDirection: 'column' }}><span className="stui-label">{labelM ? labelM[1] : 'Label'}</span><div className="stui-sublabel">{sublabelM ? sublabelM[1] : 'Helper text'}</div></div>;
  },
  'stui-pill': (tokens) => {
    const optsM    = (tokens.find(t => /^options "/.test(t)) || '').match(/"([^"]*)"/);
    const selM     = tokens.find(t => /^selected /.test(t));
    const opts     = optsM ? optsM[1].split(',') : ['Option A', 'Option B'];
    const selected = selM ? parseInt(selM.replace('selected ', '')) : 0;
    return <div className="stui-p-16"><div className="stui-pill-group">{opts.map((o, i) => <button key={i} className={`stui-pill${i === selected ? ' selected' : ''}`}>{o.trim()}</button>)}</div></div>;
  },
  'stui-toggle': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const on     = tokens.some(t => t === 'on');
    return <div style={{ padding: 16, display: 'flex', gap: 10, alignItems: 'center' }}><label className="stui-toggle"><input type="checkbox" defaultChecked={on} readOnly /><span className="stui-toggle-slider" /></label><span className="stui-label">{labelM ? labelM[1] : 'Toggle'}</span></div>;
  },
  'stui-row-setting': (tokens) => {
    const type      = tokens.find(t => /^type /.test(t))?.replace('type ', '') || 'toggle';
    const labelM    = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const sublabelM = (tokens.find(t => /^sublabel "/.test(t)) || '').match(/"([^"]*)"/);
    return <div style={{ padding: '0 16px' }}><div className="stui-row-setting"><div className="stui-row-setting-label"><div className="stui-label">{labelM ? labelM[1] : 'Setting'}</div>{sublabelM && <div className="stui-sublabel">{sublabelM[1]}</div>}</div>{type === 'toggle' && <label className="stui-toggle"><input type="checkbox" defaultChecked readOnly /><span className="stui-toggle-slider" /></label>}{type === 'select' && <StuiSelect className="stui-select"><option>Option A</option></StuiSelect>}{type === 'buttons' && <div className="stui-row-setting-controls"><button className="stui-btn stui-btn-primary">Apply</button></div>}</div></div>;
  },
  'stui-list-view': () => <div style={{ padding: 16, fontSize: 12, color: 'var(--color-gray-500)' }}>Structural layout — two-panel split (stui-list-panel + stui-detail-panel inside stui-list-view).</div>,
  'stui-field-group': (tokens) => {
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const state  = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'normal';
    const pos    = tokens.find(t => /^position /.test(t))?.replace('position ', '') || 'top';
    const posClass = pos === 'top' ? '' : ` label-${pos}`;
    return <div className="stui-p-16"><div className={`stui-field-group${posClass}`} style={{ width: 240 }}><label>{labelM ? labelM[1] : 'Field'}</label>{state === 'readonly' ? <div className="stui-field-readonly">System managed</div> : state === 'derived' ? <input className="stui-field-input derived" value="AUTO-2024" readOnly /> : <input className={`stui-field-input${state === 'normal' ? '' : ` ${state}`}`} defaultValue="Sample value" />}</div></div>;
  },
  'stui-searchselect-trigger': (tokens) => {
    const valueM = (tokens.find(t => /^value "/.test(t)) || '').match(/"([^"]*)"/);
    const state  = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'normal';
    const cls    = `stui-searchselect-trigger${state !== 'normal' ? ` ${state}` : ''}`;
    return <div className="stui-p-16"><div className={cls} style={{ width: 200 }}><span className={`stui-searchselect-value${!valueM ? ' placeholder' : ''}`}>{valueM ? valueM[1] : '-- Select --'}</span><ChevronDown size={14} className="stui-searchselect-chevron" /></div></div>;
  },
  'stui-related-section': (tokens) => {
    const tabsRaw   = tokens.find(t => /^tabs "/.test(t))?.match(/"([^"]+)"/)?.[1] || 'Tab A,Tab B';
    const collapsed = tokens.some(t => t === 'collapsed');
    const tabs      = tabsRaw.split(',').map(t => t.trim());
    return <div className="stui-p-16"><div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}><div className={`stui-related-section${collapsed ? ' collapsed' : ''}`}><div className="stui-related-header"><div className="stui-related-tabs">{tabs.map((t, i) => <button key={i} className={`stui-related-tab${i === 0 ? ' active' : ''}`}>{t}<span className="stui-related-tab-count">{(i+1)*4}</span></button>)}</div></div>{!collapsed && <div className="stui-related-content stui-related-body">Showing {tabs[0]} records…</div>}</div></div></div>;
  },
  'stui-pagination': (tokens) => {
    const pageM  = tokens.find(t => /^page /.test(t));
    const totalM = tokens.find(t => /^total /.test(t));
    const sizeM  = tokens.find(t => /^pageSize /.test(t));
    const page   = pageM  ? parseInt(pageM.replace('page ', ''))     : 1;
    const total  = totalM ? parseInt(totalM.replace('total ', ''))   : 100;
    const size   = sizeM  ? parseInt(sizeM.replace('pageSize ', '')) : 50;
    const totalPages = Math.ceil(total / size);
    const start  = (page - 1) * size + 1;
    const end    = Math.min(page * size, total);
    const pages  = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1);
    return <div className="stui-p-16"><div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', maxWidth: 360 }}><div className="stui-pagination"><div className="stui-pagination-controls"><button className="stui-pagination-btn" disabled={page <= 1}><ChevronLeft size={14} /></button>{pages.map(p => <button key={p} className={`stui-pagination-btn${p === page ? ' active' : ''}`}>{p}</button>)}<button className="stui-pagination-btn" disabled={page >= totalPages}><ChevronRight size={14} /></button></div><span className="stui-pagination-info">{start}–{end} of {total}</span></div></div></div>;
  },
  'stui-fk-select': (tokens) => {
    const pathM  = (tokens.find(t => /^path "/.test(t)) || '').match(/"([^"]*)"/);
    const segs   = (pathM ? pathM[1] : 'Parent → Child').split('→').map(s => s.trim());
    return <div className="stui-p-16"><div style={{ border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', maxWidth: 380, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}><div className="stui-fk-select-header"><div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>{segs.map((s, i) => <React.Fragment key={i}>{i > 0 && <span style={{ color: 'var(--color-gray-500)', margin: '0 2px' }}>→</span>}<span style={{ color: i === segs.length-1 ? 'var(--color-gray-900)' : 'var(--color-gray-400)', fontWeight: i === segs.length-1 ? 600 : 400 }}>{s}</span></React.Fragment>)}</div><button className="stui-crud-btn" title="Close"><X size={16} /></button></div><div className="stui-fk-select-body" style={{ padding: '12px 16px', minHeight: 60, fontSize: 12, color: 'var(--color-gray-500)', fontStyle: 'italic' }}>…record list…</div></div></div>;
  },
  'stui-composer-select': (tokens) => {
    const moduleM = (tokens.find(t => /^module "/.test(t)) || '').match(/"([^"]*)"/);
    return <div className="stui-p-16"><div className="stui-composer-select"><label className="stui-composer-select-label">Module</label><div className="stui-composer-select-trigger"><span>{moduleM ? moduleM[1] : 'Building'}</span><ChevronDown size={14} /></div></div></div>;
  },
  'stui-module-header-crud': (tokens) => {
    const mode  = tokens.find(t => /^mode /.test(t))?.replace('mode ', '') || 'list';
    const state = tokens.find(t => /^state /.test(t))?.replace('state ', '') || 'view';
    return <div className="stui-p-16"><div style={{ padding: '12px 16px', background: 'var(--color-gray-50)', border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', display: 'inline-flex' }}>{mode === 'list' ? <div className="stui-module-header-crud">{state === 'view' ? <><button className="stui-crud-btn" title="Add"><Plus size={18} /></button><button className="stui-crud-btn" title="Edit"><Pencil size={18} /></button><button className="stui-crud-btn disabled" disabled><Save size={18} /></button></> : <><button className="stui-crud-btn stui-delete-btn"><Trash2 size={18} /></button><button className="stui-crud-btn"><X size={18} /></button><button className="stui-crud-btn"><Save size={18} /></button></>}</div> : <div className="stui-module-header-datasheet-controls"><div className="stui-module-header-crud"><button className="stui-crud-btn"><Pencil size={18} /></button><button className={`stui-crud-btn${state === 'view' ? ' disabled' : ''}`} disabled={state === 'view'}><Save size={18} /></button></div></div>}</div></div>;
  },
  // Prefix-matched renderers — stui-btn-primary, stui-btn-secondary, stui-btn-danger all resolve here
  'stui-btn': (tokens, className) => {
    const iconM    = (tokens.find(t => / icon$/.test(t)) || '').replace(/ icon$/, '');
    const labelM   = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const disabled = tokens.some(t => t === 'disabled');
    const IconMap  = { FileSpreadsheet, FileText, FileDown, Download };
    const IconComp = IconMap[iconM] || null;
    const label    = labelM ? labelM[1] : 'Button';
    const variantM = (className || '').match(/stui-btn-(primary|secondary|danger)/);
    const variant  = variantM ? variantM[1] : 'primary';
    return <div className="stui-p-16"><button className={`stui-btn stui-btn-${variant}`} disabled={disabled}>{IconComp && <IconComp size={14} style={{ marginRight: 4 }} />}{label}</button></div>;
  },
  'stui-select': (tokens) => {
    const phM      = (tokens.find(t => /^placeholder "/.test(t)) || '').match(/"([^"]*)"/);
    const disabled = tokens.some(t => t === 'disabled');
    return <div className="stui-p-16"><StuiSelect className="stui-select" disabled={disabled} defaultValue=""><option value="" disabled>{phM ? phM[1] : '-- Select --'}</option><option>Option A</option><option>Option B</option><option>Option C</option></StuiSelect></div>;
  },
  'stui-data-grid': (tokens) => {
    const colsM = tokens.find(t => /col/.test(t));
    const rowsM = tokens.find(t => /row/.test(t));
    const cols  = colsM ? parseInt(colsM) : 3;
    const rows  = rowsM ? parseInt(rowsM) : 3;
    const headers = ['Column A', 'Column B', 'Column C', 'Column D', 'Column E'].slice(0, Math.min(cols, 5));
    const rowData = Array.from({ length: Math.min(rows, 4) }, (_, i) => headers.map((_, j) => `R${i+1}C${j+1}`));
    return <div className="stui-p-16"><div className="stui-data-grid-container"><div className="stui-data-grid-scroll-area"><table className="stui-data-grid"><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{rowData.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody></table></div></div></div>;
  },
  'stui-section-card': (tokens) => {
    const hasDesc    = tokens.some(t => t === 'with description');
    const hasChip    = tokens.some(t => / chip$/.test(t));
    const subsCount  = (tokens.find(t => /subsection/.test(t)) || '1').match(/(\d+)/)?.[1] || '1';
    return <div className="stui-p-16"><div className="stui-section-card"><div className="stui-section-card-header">{hasChip && <span className="stui-css-chip">STUI.css : L100</span>}<span className="stui-section-card-title">Section Title</span>{hasDesc && <p className="stui-section-card-desc">Short description of this section.</p>}</div><div className="stui-section-card-body">{Array.from({ length: parseInt(subsCount) }, (_, i) => <div key={i} className="stui-detail-section-header" style={{ margin: '0 0 8px' }}>Subsection {i+1}</div>)}</div></div></div>;
  },
  'stui-step-panel': (tokens) => {
    const numM      = tokens.find(t => /^number /.test(t));
    const titleM    = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const descM     = (tokens.find(t => /^desc "/.test(t)) || '').match(/"([^"]*)"/);
    const collapsed = tokens.some(t => t === 'collapsed');
    const hasBtn    = tokens.some(t => t === 'Run Report button');
    const num       = numM ? numM.replace('number ', '') : '1';
    return <div className="stui-p-16"><div className={`stui-step-panel${collapsed ? ' collapsed' : ''}`}><div className="stui-step-panel-header"><span className="stui-step-number">{num}</span><div className="stui-step-info"><span className="stui-step-title">{titleM ? titleM[1] : 'Step Title'}</span>{descM && <span className="stui-step-desc">{descM[1]}</span>}</div></div>{!collapsed && hasBtn && <div style={{ padding: '8px 16px' }}><button className="stui-btn stui-btn-primary">Run Report</button></div>}</div></div>;
  },
  'stui-filter-dropdown': (tokens) => {
    const hasSort   = !tokens.some(t => t === 'no sort');
    const hasSearch = !tokens.some(t => t === 'no search');
    const hasSelAll = !tokens.some(t => t === 'no select-all');
    return <div className="stui-p-16"><div className="stui-filter-dropdown" style={{ width: 200, border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)'}}>{hasSort && <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--color-gray-100)', display: 'flex', gap: 4 }}><button className="stui-btn-sort-state">↑ Asc</button><button className="stui-btn-sort-state">↓ Desc</button></div>}{hasSearch && <div style={{ padding: '6px 10px' }}><input className="stui-field-input" placeholder="Search…" style={{ width: '100%', boxSizing: 'border-box' }} readOnly /></div>}{hasSelAll && <div style={{ padding: '4px 10px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 12 }}><label style={{ display: 'flex', gap: 6, cursor: 'pointer' }}><input type="checkbox" defaultChecked readOnly /> Select All</label></div>}<div style={{ padding: '4px 0' }}>{['Alpha', 'Bravo', 'Charlie'].map(o => <label key={o} style={{ display: 'flex', gap: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}><input type="checkbox" readOnly /> {o}</label>)}</div></div></div>;
  },
  'stui-action-menu': (tokens) => {
    const isReport  = tokens.some(t => t === 'report');
    const items     = isReport
      ? ['Download PDF', 'Export Excel', 'Export CSV']
      : ['Refresh', 'Export Excel', 'Modify View'];
    return <div className="stui-p-16"><div className="stui-action-menu"><button className="stui-action-menu-btn"><GripVertical size={16} /></button><div className="stui-action-menu-dropdown" style={{ display: 'block', position: 'relative', boxShadow: 'none', border: 'var(--border-width) solid var(--color-border)' }}>{items.map(item => <div key={item} className="stui-action-menu-item">{item}</div>)}</div></div></div>;
  },
  // App Shell renderers
  'stui-nav-item': (tokens) => {
    const levelM = tokens.find(t => /^level /.test(t))?.replace('level ', '') || '1';
    const labelM = (tokens.find(t => /^label "/.test(t)) || '').match(/"([^"]*)"/);
    const active = tokens.some(t => t === 'active');
    const future = tokens.some(t => t === 'future');
    const label  = labelM ? labelM[1] : 'Nav Item';
    return (
      <div style={{ padding: 16, background: 'var(--color-bg-white)', width: 220, border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)'}}>
        <div className="stui-nav-item-container">
          <button className={`stui-nav-item stui-nav-item-level-${levelM}${active ? ' active' : ''}`}>
            <div className="stui-nav-item-content">
              <span className="stui-nav-item-spacer" />
              <span className="stui-nav-item-label">{label}</span>
              {future && <span className="stui-nav-future-badge">FUTURE</span>}
            </div>
          </button>
        </div>
      </div>
    );
  },
  'stui-footer': (tokens) => {
    const dbM    = (tokens.find(t => /^db "/.test(t)) || '').match(/"([^"]*)"/);
    const verM   = (tokens.find(t => /^version "/.test(t)) || '').match(/"([^"]*)"/);
    const preprod = tokens.some(t => t === 'preprod');
    return (
      <div className="stui-p-16">
        <div className="stui-demo-header-bar">
          <div className="stui-footer-left">
            <span className={`stui-footer-db-badge${preprod ? ' stui-footer-db-badge-preprod' : ''}`}>
              <Database size={12} /><span>{dbM ? dbM[1] : 'GWA'}</span>
            </span>
            {verM && <span className={`stui-footer-version${preprod ? ' stui-footer-version-preprod' : ''}`}>{verM[1]}</span>}
          </div>
          <div className="stui-footer-right">
            <button className="stui-debug-btn"><Wrench size={11} /></button>
          </div>
        </div>
      </div>
    );
  },
  'stui-debug-panel': (tokens) => {
    const togOn = tokens.some(t => t === 'toggle on');
    return (
      <div className="stui-p-16">
        <div className="stui-debug-panel" style={{ position: 'relative', bottom: 'auto', right: 'auto' }}>
          <div className="stui-debug-head">
            <span className="stui-debug-title">Debug Tools</span>
            <span className="stui-debug-hint">Shift+F12</span>
            <button className="stui-debug-close"><X size={12} /></button>
          </div>
          <div className="stui-debug-section">Hover Inspectors</div>
          <div className="stui-debug-row">
            <span className="stui-debug-label">CSS Variables</span>
            <span className={`stui-debug-tog${togOn ? ' on' : ''}`} />
          </div>
          <div className="stui-debug-row">
            <span className="stui-debug-label">Tab Order</span>
            <span className="stui-debug-tog" />
          </div>
          <div className="stui-debug-section">Theme</div>
          <div className="stui-debug-mode-row">
            <button className="stui-debug-mode-btn on">Standard</button>
            <button className="stui-debug-mode-btn">Dark</button>
            <button className="stui-debug-mode-btn">Dim</button>
            <button className="stui-debug-mode-btn">Sepia</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-activity-panel': (tokens) => {
    const empty  = tokens.some(t => t === 'empty');
    const format = tokens.find(t => /^format /.test(t))?.replace('format ', '') || 'xlsx';
    return (
      <div className="stui-p-16">
        <div className="stui-activity-panel" style={{ position: 'relative', top: 'auto', right: 'auto', width: 280 }}>
          <div className="stui-activity-panel-header">
            <div className="stui-row-8">
              <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
              <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--color-gray-700)' }}>Data Transfer</span>
            </div>
          </div>
          <div className="stui-activity-entries-scroll">
            {empty ? (
              <div className="stui-activity-empty">No active transfers</div>
            ) : (
              <div className="stui-activity-entry">
                <div className="stui-entry-icon-col"><Download size={13} style={{ color: '#16a34a' }} /></div>
                <div className="stui-entry-main-col">
                  <div className="stui-entry-name-row">
                    <span className="stui-entry-name">I/O Assignments</span>
                    <span className="stui-entry-format-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>{format.toUpperCase()}</span>
                  </div>
                  <div className="stui-entry-phase">Generating…</div>
                  <div className="stui-entry-progress-track">
                    <div className="stui-entry-progress-fill" style={{ width: '60%', background: 'var(--color-primary)' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="stui-activity-panel-footer">
            <div className="stui-row-4">
              <span style={{ fontSize: 9, color: 'var(--color-gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>Clear:</span>
              {['1m','1 day'].map(o => <button key={o} className="stui-activity-clear-pill">{o}</button>)}
            </div>
            <button className="stui-activity-history-link"><Clock size={11} /> History</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-modal': (tokens) => {
    const titleM  = (tokens.find(t => /^title "/.test(t)) || '').match(/"([^"]*)"/);
    const hasForm = tokens.some(t => t === 'with form');
    const hasList = tokens.some(t => t === 'with list');
    return (
      <div className="stui-p-16">
        <div className="stui-modal" style={{ position: 'relative', width: 360, maxHeight: 'none' }}>
          <div className="stui-modal-header">
            <h3>{titleM ? titleM[1] : 'Modal Title'}</h3>
            <button className="stui-modal-close">&times;</button>
          </div>
          {hasList && (
            <div className="stui-modal-list">
              {['Alice Johnson', 'Bob Smith', 'Carol White'].map(name => (
                <button key={name} className="stui-modal-user">
                  <div className="stui-modal-user-info">
                    <span className="stui-modal-user-name">{name}</span>
                    <span className="stui-modal-user-group">Developer</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {hasForm && (
            <div className="stui-modal-body">
              <div className="stui-form-row"><label>Description</label><input className="stui-field-input" placeholder="Enter value…" readOnly /></div>
            </div>
          )}
          <div className="stui-modal-footer">
            <button className="stui-btn stui-btn-secondary">Cancel</button>
            <button className="stui-btn stui-btn-primary">Confirm</button>
          </div>
        </div>
      </div>
    );
  },
  'stui-db-switcher-modal': (tokens) => {
    const dbs = [
      { name: 'GWA', desc: 'Greater Wenatchee Area', active: true },
      { name: 'LEAVENWORTH', desc: 'Leavenworth Project', active: false },
    ];
    return (
      <div className="stui-p-16">
        <div className="stui-db-switcher-modal" style={{ width: 340, height: 'auto', position: 'relative' }}>
          <div className="stui-db-switcher-header">
            <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>DAWN</div>
            <button className="stui-modal-close">&times;</button>
          </div>
          <div className="stui-db-switcher-list">
            {dbs.map(db => (
              <button key={db.name} className={`stui-db-switcher-row${db.active ? ' current' : ''}`}>
                <div className="stui-db-switcher-logo"><div className="stui-db-switcher-logo-placeholder"><Database size={14} /></div></div>
                <div className="stui-db-switcher-info">
                  <span className="stui-db-switcher-name">{db.name}</span>
                  <span className="stui-db-switcher-desc">{db.desc}</span>
                </div>
                {db.active && <span className="stui-db-switcher-badge">Current</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  },
};

const CSSOutputTestTab = () => {
  const [input, setInput] = useState('');

  const parsed = useMemo(() => {
    const raw = input.trim();
    if (!raw) return null;
    const parts = raw.split(' · ').map(p => p.trim()).filter(Boolean);
    if (!parts.length) return null;
    return { className: parts[0], tokens: parts.slice(1) };
  }, [input]);

  const renderer = parsed ? (() => {
    if (CSS_OUTPUT_RENDERERS[parsed.className]) return CSS_OUTPUT_RENDERERS[parsed.className];
    // Prefix fallback: stui-col-12 → stui-col, stui-badge-accepted → stui-badge, stui-btn-primary → stui-btn
    const key = Object.keys(CSS_OUTPUT_RENDERERS).find(k => parsed.className.startsWith(k + '-'));
    return key ? CSS_OUTPUT_RENDERERS[key] : null;
  })() : null;

  return (
    <div className="stui-preview-zone" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Header */}
      <div className="stui-css-output-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-gray-700)', fontFamily: 'var(--font-secondary)' }}>CSS Output Test</h2>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--color-gray-500)' }}>Paste a configurator output string — renders the actual component</p>
      </div>

      <div className="stui-css-output-wrap">

        {/* Left: paste + parsed tokens */}
        <div className="stui-css-output-left">
          <div className="stui-detail-section-header stui-m-0">Paste Output</div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={'stui-page-header · FileSpreadsheet icon · title "I/O Assignments" · desc "Tag IO…" · with export action menu'}
            style={{ minHeight: 120, resize: 'vertical', fontSize: 12, fontFamily: 'var(--font-mono)', padding: '8px 10px', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', color: 'var(--color-gray-700)', background: 'var(--color-bg-white)', lineHeight: 1.6 }}
          />
          {input && (
            <button onClick={() => setInput('')} style={{ alignSelf: 'flex-start', fontSize: 11, color: 'var(--color-gray-500)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
          )}

          {parsed && (
            <div className="stui-col-6">
              <div className="stui-detail-section-header stui-m-0">Parsed</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--color-gray-500)', width: 52, flexShrink: 0 }}>class</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-primary)', fontWeight: 700 }}>.{parsed.className}</span>
                </div>
                {parsed.tokens.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 10, color: 'var(--color-gray-500)', width: 52, flexShrink: 0 }}>arg {i + 1}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-gray-600)' }}>{t}</span>
                  </div>
                ))}
                {!renderer && (
                  <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontStyle: 'italic', marginTop: 4 }}>
                    No renderer for this class — generic preview shown
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div className="stui-css-output-right">
          <div className="stui-detail-section-header stui-m-0">Live Preview</div>

          {!parsed && (
            <div style={{ color: 'var(--color-gray-500)', fontSize: 13, fontStyle: 'italic', marginTop: 8 }}>
              Paste a configurator output on the left
            </div>
          )}

          {/* Registered renderer — renders the actual component */}
          {parsed && renderer && (
            <div style={{ border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              {renderer(parsed.tokens, parsed.className)}
            </div>
          )}

          {/* Generic fallback for unregistered classes */}
          {parsed && !renderer && (
            <div className="stui-col-12">
              {(['div', 'button', 'span']).map(tag => (
                <div key={tag} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{`<${tag}>`}</span>
                  <div style={{ padding: '12px 16px', background: 'var(--color-gray-50)', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                    {tag === 'div'    && <div className={parsed.className}>Sample content</div>}
                    {tag === 'button' && <button className={parsed.className}>Button</button>}
                    {tag === 'span'   && <span className={parsed.className}>Span</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

/* ── Tab: CSS Migrate ──────────────────────────────────────── */

const parseStuiClasses = (cssContent) => {
  if (!cssContent) return [];
  const seen = new Set();
  const classes = [];
  const re = /\.([a-zA-Z][a-zA-Z0-9\-_]*)(?=[\s,:{])/g;
  let m;
  while ((m = re.exec(cssContent)) !== null) {
    if (!seen.has(m[1])) { seen.add(m[1]); classes.push(m[1]); }
  }
  return classes;
};

const parseStuiClassProps = (cssContent, className) => {
  if (!cssContent || !className) return [];
  const esc = className.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
  const blockRe = new RegExp(`\\.${esc}\\s*\\{([^}]+)\\}`, 's');
  const match = cssContent.match(blockRe);
  if (!match) return [];
  return match[1].split(';')
    .map(p => p.trim())
    .filter(p => p && p.includes(':'))
    .map(p => { const i = p.indexOf(':'); return { prop: p.substring(0, i).trim(), value: p.substring(i + 1).trim() }; });
};

const MigrationTab = () => {
  const [appIndex, setAppIndex]           = useState(null);
  const [appFilter, setAppFilter]         = useState('');
  const [selectedApp, setSelectedApp]     = useState(null);
  const [appStyles, setAppStyles]         = useState(null);
  const [appUsages, setAppUsages]         = useState(null);
  const [usagesExp, setUsagesExp]         = useState(false);
  const [loadingDet, setLoadingDet]       = useState(false);
  const [stuiContent, setStuiContent]     = useState(null);
  const [stuiFilter, setStuiFilter]       = useState('');
  const [selectedStui, setSelectedStui]   = useState(null);
  const [stuiUsages, setStuiUsages]       = useState(null);
  const [stuiUsagesExp, setStuiUsagesExp] = useState(false);
  const [loadingStuiDet, setLoadingStuiDet] = useState(false);
  const [menuOpen, setMenuOpen]           = useState(false);
  const [loading, setLoading]             = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setMenuOpen(false);
    try {
      const [idxRes, stuiRes] = await Promise.all([
        fetch(API_BASE_URL + '/api/devfiles/app-css-index'),
        fetch(API_BASE_URL + '/api/devfiles/stui-css'),
      ]);
      setAppIndex(await idxRes.json());
      const d = await stuiRes.json();
      setStuiContent(d.content);
    } catch (e) { console.error('Migration tab load failed:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Fetch App.css class details when selection changes
  useEffect(() => {
    if (!selectedApp) { setAppStyles(null); setAppUsages(null); return; }
    setUsagesExp(false);
    const load = async () => {
      setLoadingDet(true);
      try {
        const [sRes, uRes] = await Promise.all([
          fetch(API_BASE_URL + `/api/devfiles/ui-audit/styles?class=.${selectedApp}`),
          fetch(API_BASE_URL + `/api/devfiles/ui-audit/usages?class=${selectedApp}`),
        ]);
        setAppStyles(await sRes.json());
        setAppUsages(await uRes.json());
      } catch (e) { console.error('App class detail fetch failed:', e); }
      setLoadingDet(false);
    };
    load();
  }, [selectedApp]);

  // Fetch STUI class usages when selection changes
  useEffect(() => {
    if (!selectedStui) { setStuiUsages(null); return; }
    setStuiUsagesExp(false);
    const load = async () => {
      setLoadingStuiDet(true);
      try {
        const uRes = await fetch(API_BASE_URL + `/api/devfiles/ui-audit/usages?class=${selectedStui}`);
        setStuiUsages(await uRes.json());
      } catch (e) { console.error('STUI class usages fetch failed:', e); }
      setLoadingStuiDet(false);
    };
    load();
  }, [selectedStui]);

  // Auto-match: when App class selected, highlight stui-{class} if it exists
  useEffect(() => {
    setSelectedStui(null);
    if (!selectedApp || !stuiContent) return;
    const candidate = `stui-${selectedApp}`;
    if (parseStuiClasses(stuiContent).includes(candidate)) setSelectedStui(candidate);
  }, [selectedApp, stuiContent]);

  const filteredApp = (appIndex || []).filter(
    item => !appFilter || item.className.toLowerCase().includes(appFilter.toLowerCase())
  );
  const groupedApp = filteredApp.reduce((acc, item) => {
    (acc[item.section] = acc[item.section] || []).push(item);
    return acc;
  }, {});

  const stuiClasses  = parseStuiClasses(stuiContent);
  const filteredStui = stuiFilter ? stuiClasses.filter(c => c.toLowerCase().includes(stuiFilter.toLowerCase())) : stuiClasses;
  const stuiProps    = parseStuiClassProps(stuiContent, selectedStui);
  const autoMatch    = selectedApp ? `stui-${selectedApp}` : null;
  const hasMatch     = !!(autoMatch && stuiClasses.includes(autoMatch));

  // Migration status computation
  const stillInApp     = !!(selectedApp && appIndex?.some(item => item.className === selectedApp));
  const exactStuiMatch = !!(selectedApp && stuiClasses.includes(selectedApp));
  const stuiEquivName  = exactStuiMatch ? selectedApp : (hasMatch ? autoMatch : null);
  const hasStuiEquiv   = !!stuiEquivName;
  const stuiEquivProps = stuiEquivName ? parseStuiClassProps(stuiContent, stuiEquivName) : [];
  const varsUsed       = [...new Set(stuiEquivProps.flatMap(p => (p.value.match(/var\(--[^)]+\)/g) || [])))];
  const migStatus      = selectedApp
    ? (stillInApp && !hasStuiEquiv)                              ? 'not-started'
    : (stillInApp && hasStuiEquiv)                               ? 'duplicate'
    : (!stillInApp && hasStuiEquiv && varsUsed.length > 0)       ? 'vars-pending'
    : (!stillInApp && hasStuiEquiv && varsUsed.length === 0)     ? 'clean'
    : 'no-equiv'
    : null;

  const panelHeader = 'stui-panel-header';
  const detailBox   = 'stui-panel-detail';
  const propTable   = { width: '100%', fontSize: 11, borderCollapse: 'collapse' };
  const propTd1     = { padding: '2px 8px 2px 0', color: 'var(--color-gray-500)', fontFamily: 'var(--font-mono)', width: '42%', verticalAlign: 'top' };
  const propTd2     = { padding: '2px 0', fontFamily: 'var(--font-mono)', color: 'var(--color-gray-700)', wordBreak: 'break-all' };

  return (
    <div className="stui-preview-zone" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(200vh - 84px)' }}>

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 32px', borderBottom: 'var(--border-width) solid var(--color-border-subtle)', background: 'var(--color-gray-50)', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-gray-700)', fontFamily: 'var(--font-secondary)' }}>CSS Migration Tool</h2>
          {appIndex && <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--color-gray-500)' }}>{appIndex.length} App.css classes · {stuiClasses.length} STUI.css classes</p>}
        </div>
        <div className="stui-action-menu">
          <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions"><GripVertical size={16} /></button>
          {menuOpen && (
            <div className="stui-action-menu-dropdown">
              <button className="stui-action-menu-item" onClick={refresh}><RefreshCw size={16} /> Refresh</button>
            </div>
          )}
        </div>
      </div>

      {loading && <div style={{ padding: '48px 32px', color: 'var(--color-gray-500)', fontSize: 13, textAlign: 'center' }}>Loading CSS index…</div>}

      {/* Migration status banner */}
      {!loading && selectedApp && migStatus && (() => {
        const colors = {
          'clean':       { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', label: '✓ Clean'          },
          'not-started': { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: '✗ Not Migrated'   },
          'duplicate':   { bg: '#fffbeb', border: '#fcd34d', text: '#d97706', label: '⚠ In Both Files'  },
          'vars-pending':{ bg: '#fffbeb', border: '#fcd34d', text: '#d97706', label: '~ Migrated'       },
          'no-equiv':    { bg: 'var(--color-gray-50)', border: 'var(--color-gray-300)', text: 'var(--color-gray-500)', label: '◌ No Equivalent' },
        };
        const c = colors[migStatus];
        return (
          <div style={{ padding: '10px 20px', flexShrink: 0, borderBottom: `2px solid ${c.border}`, background: c.bg, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{c.label}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-gray-600)' }}>.{selectedApp}</span>
            </div>
            <div className="stui-label-xs">
              {migStatus === 'clean'       && `Removed from App.css · STUI equivalent: .${stuiEquivName} · No var() dependencies`}
              {migStatus === 'not-started' && 'Still defined in App.css · No STUI equivalent found'}
              {migStatus === 'duplicate'   && `Defined in both files · Remove from App.css to complete — STUI equivalent: .${stuiEquivName}`}
              {migStatus === 'vars-pending'&& `Removed from App.css · STUI equivalent: .${stuiEquivName} · Uses ${varsUsed.length} CSS var${varsUsed.length !== 1 ? 's' : ''} from App.css :root`}
              {migStatus === 'no-equiv'    && 'Not in App.css · No STUI equivalent defined'}
            </div>
            {migStatus === 'vars-pending' && varsUsed.length > 0 && (
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--color-gray-500)', lineHeight: 1.8 }}>{varsUsed.join('  ·  ')}</div>
            )}
          </div>
        );
      })()}

      {!loading && (
        <div className="stui-migrate-wrap">

          {/* ── Left: App.css ── */}
          <div className="stui-migrate-panel-left">

            {/* Panel header + filter */}
            <div className={panelHeader}>
              <div className="stui-detail-section-header" style={{ margin: '0 0 10px' }}>App.css Browser</div>
              <input placeholder="Filter classes…" value={appFilter} onChange={e => setAppFilter(e.target.value)}
                className="stui-panel-filter" />
            </div>

            {/* ── Detail section — ABOVE the browser ── */}
            {selectedApp && (
              <div className={detailBox}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--color-primary)', marginBottom: 10 }}>.{selectedApp}</div>

                {/* Live preview */}
                <div style={{ background: 'var(--color-gray-50)', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 12, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ fontSize: 10, color: 'var(--color-gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Live Preview</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className={selectedApp}>Div</div>
                    <button className={selectedApp}>Button</button>
                    <span className={selectedApp}>Span</span>
                  </div>
                </div>

                {/* CSS properties */}
                {loadingDet && <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 8 }}>Loading…</div>}
                {!loadingDet && appStyles?.rules?.map((rule, ri) => (
                  <div key={ri} style={{ marginBottom: 10 }}>
                    {rule.state !== 'default' && <div style={{ fontSize: 10, color: 'var(--color-gray-500)', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>{rule.selector}</div>}
                    <table style={propTable}><tbody>
                      {Object.entries(rule.properties).map(([prop, val], pi) => (
                        <tr key={pi} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={propTd1}>{prop}</td>
                          <td style={propTd2}>{val}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div>
                ))}

                {/* Usages */}
                {!loadingDet && appUsages && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--color-gray-100)', paddingTop: 8 }}>
                    <button onClick={() => setUsagesExp(ex => !ex)} style={{ fontSize: 11, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {usagesExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Used by {appUsages.count} file{appUsages.count !== 1 ? 's' : ''}
                    </button>
                    {usagesExp && appUsages.usages?.map((u, ui) => (
                      <div key={ui} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-gray-500)', padding: '2px 12px' }}>{u.fileName}:{u.line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Class browser — below detail ── */}
            <div style={{ flex: 1, overflowY: 'auto', fontSize: 12 }}>
              {Object.keys(groupedApp).map(section => (
                <div key={section}>
                  <div style={{ padding: '4px 16px', background: 'var(--color-primary)', color: 'white', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>{section}</div>
                  {groupedApp[section].map(item => (
                    <div key={`${item.className}-${item.line}`} onClick={() => setSelectedApp(item.className)} style={{
                      padding: '4px 20px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
                      background: selectedApp === item.className ? 'var(--color-primary-light)' : 'transparent',
                      color: selectedApp === item.className ? 'var(--color-primary)' : 'var(--color-gray-700)',
                      borderLeft: selectedApp === item.className ? '3px solid var(--color-primary)' : '3px solid transparent',
                      fontWeight: selectedApp === item.className ? 600 : 400,
                    }}>.{item.className}</div>
                  ))}
                </div>
              ))}
              {appIndex && filteredApp.length === 0 && (
                <div style={{ padding: '24px 16px', color: 'var(--color-gray-500)', fontSize: 12, textAlign: 'center' }}>No classes match &quot;{appFilter}&quot;</div>
              )}
            </div>

          </div>

          {/* ── Right: STUI.css ── */}
          <div className="stui-migrate-panel-right">

            {/* Panel header + filter */}
            <div className={panelHeader}>
              <div className="stui-detail-section-header" style={{ margin: '0 0 10px' }}>STUI.css</div>
              <input placeholder="Filter classes…" value={stuiFilter} onChange={e => setStuiFilter(e.target.value)}
                className="stui-panel-filter" />
            </div>

            {/* Match banner */}
            {selectedApp && hasMatch && (
              <div style={{ padding: '7px 16px', fontSize: 12, flexShrink: 0, borderBottom: 'var(--border-width) solid var(--color-border-subtle)', background: 'var(--color-primary-tint)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>✓ Match</span> — <span className="stui-mono">.{autoMatch}</span> covers this
              </div>
            )}

            {/* ── Detail section — ABOVE the browser ── */}
            {selectedStui && (
              <div className={detailBox}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--color-primary)', marginBottom: 10 }}>.{selectedStui}</div>

                {/* Live preview */}
                <div style={{ background: 'var(--color-gray-50)', border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 12, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ fontSize: 10, color: 'var(--color-gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Live Preview</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className={selectedStui}>Div</div>
                    <button className={selectedStui}>Button</button>
                    <span className={selectedStui}>Span</span>
                  </div>
                </div>

                {/* CSS properties */}
                {stuiProps.length > 0
                  ? <div style={{ marginBottom: 10 }}>
                      <table style={propTable}><tbody>
                        {stuiProps.map((p, pi) => (
                          <tr key={pi} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <td style={propTd1}>{p.prop}</td>
                            <td style={propTd2}>{p.value}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  : <div style={{ fontSize: 11, color: 'var(--color-gray-500)', fontStyle: 'italic', marginBottom: 10 }}>No properties found</div>
                }

                {/* Usages */}
                {loadingStuiDet && <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Loading usages…</div>}
                {!loadingStuiDet && stuiUsages && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--color-gray-100)', paddingTop: 8 }}>
                    <button onClick={() => setStuiUsagesExp(ex => !ex)} style={{ fontSize: 11, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {stuiUsagesExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Used by {stuiUsages.count} file{stuiUsages.count !== 1 ? 's' : ''}
                    </button>
                    {stuiUsagesExp && stuiUsages.usages?.map((u, ui) => (
                      <div key={ui} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-gray-500)', padding: '2px 12px' }}>{u.fileName}:{u.line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Class browser — below detail ── */}
            <div style={{ flex: 1, overflowY: 'auto', fontSize: 12 }}>
              {filteredStui.map(cls => {
                const isMatch = cls === autoMatch;
                const isSel   = cls === selectedStui;
                return (
                  <div key={cls} onClick={() => setSelectedStui(cls)} style={{
                    padding: '5px 16px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
                    background: isSel ? 'var(--color-primary-light)' : isMatch ? 'var(--color-primary-tint)' : 'transparent',
                    color: isSel ? 'var(--color-primary)' : isMatch ? 'var(--color-primary)' : 'var(--color-gray-700)',
                    borderLeft: isSel ? '3px solid var(--color-primary)' : isMatch ? '3px solid var(--color-primary-hover)' : '3px solid transparent',
                    fontWeight: isSel || isMatch ? 600 : 400,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span>.{cls}</span>
                    {isMatch && <span style={{ fontSize: 10, background: 'var(--color-primary)', color: 'white', padding: '1px 5px', borderRadius: 'var(--radius-sm)'}}>MATCH</span>}
                  </div>
                );
              })}
            </div>

          </div>

        </div>
      )}
    </div>
  );
};

/* ── App Shell Tab ──────────────────────────────────────────── */

const NavConfigurator = () => {
  const [level, setLevel] = useState('1');
  const [label, setLabel] = useState('Nav Item');
  const [active, setActive] = useState(false);
  const [future, setFuture] = useState(false);
  const output = [`stui-nav-item`, `level ${level}`, `label "${label || 'Nav Item'}"`, active ? 'active' : 'inactive', future ? 'future' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Level</span>
        {['1', '2', '3'].map(v => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" className="stui-radio-input" name="nav-level" checked={level === v} onChange={() => setLevel(v)} /> {v}
          </label>
        ))}
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Label</span>
        <input value={label} onChange={e => setLabel(e.target.value)} className="stui-cfg-input" style={{ width: 160 }} />
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <span className="stui-cfg-key">State</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active
        </label>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={future} onChange={e => setFuture(e.target.checked)} /> Future badge
        </label>
      </div>
      <div className="stui-example-cell" style={{ justifyContent: 'flex-start' }}>
        <div style={{ background: '#1a5568', padding: '4px 0', borderRadius: 'var(--radius-sm)', width: 220 }}>
          <div className="stui-nav-item-container">
            <button className={`stui-nav-item stui-nav-item-level-${level}${active ? ' active' : ''}`}>
              <div className="stui-nav-item-content">
                <span className="stui-nav-item-spacer" />
                <span className="stui-nav-item-label">{label || 'Nav Item'}</span>
                {future && <span className="stui-nav-future-badge">FUTURE</span>}
              </div>
            </button>
          </div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const FooterConfigurator = () => {
  const [db, setDb] = useState('GWA');
  const [version, setVersion] = useState('v18.001');
  const [preprod, setPreprod] = useState(false);
  const output = [`stui-footer`, `db "${db || 'GWA'}"`, `version "${version || 'v1.000'}"`, preprod ? 'preprod' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">DB</span>
        <input value={db} onChange={e => setDb(e.target.value)} className="stui-cfg-input" style={{ width: 120 }} />
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Version</span>
        <input value={version} onChange={e => setVersion(e.target.value)} className="stui-cfg-input" style={{ width: 120 }} />
      </div>
      <div className="stui-row-10">
        <span className="stui-cfg-key">Env</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={preprod} onChange={e => setPreprod(e.target.checked)} /> Pre-production
        </label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const DebugConfigurator = () => {
  const [togOn, setTogOn] = useState(false);
  const output = [`stui-debug-panel`, togOn ? 'toggle on' : 'toggle off'].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key-lg">CSS Vars tog</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={togOn} onChange={e => setTogOn(e.target.checked)} /> On
        </label>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const ActivityConfigurator = () => {
  const [empty, setEmpty] = useState(false);
  const [format, setFormat] = useState('xlsx');
  const output = [`stui-activity-panel`, empty ? 'empty' : `format ${format}`].join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">State</span>
        <label className="stui-cfg-label">
          <input type="checkbox" checked={empty} onChange={e => setEmpty(e.target.checked)} /> Empty
        </label>
      </div>
      {!empty && (
        <div className="stui-row-10-wrap">
          <span className="stui-cfg-key">Format</span>
          {['xlsx', 'pdf', 'csv'].map(v => (
            <label key={v} className="stui-cfg-label">
              <input type="radio" className="stui-radio-input" name="act-fmt" checked={format === v} onChange={() => setFormat(v)} /> {v}
            </label>
          ))}
        </div>
      )}
      <ConfigOutput text={output} />
    </div>
  );
};

const ModalConfigurator = () => {
  const [title, setTitle] = useState('Modal Title');
  const [body, setBody] = useState('list');
  const output = [`stui-modal`, `title "${title || 'Modal Title'}"`, body === 'list' ? 'with list' : body === 'form' ? 'with form' : ''].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-row-10">
        <span className="stui-cfg-key">Title</span>
        <input value={title} onChange={e => setTitle(e.target.value)} className="stui-cfg-input" style={{ width: 200 }} />
      </div>
      <div className="stui-row-10-wrap">
        <span className="stui-cfg-key">Body</span>
        {[['list', 'User list'], ['form', 'Form'], ['none', 'None']].map(([v, lbl]) => (
          <label key={v} className="stui-cfg-label">
            <input type="radio" className="stui-radio-input" name="modal-body" checked={body === v} onChange={() => setBody(v)} /> {lbl}
          </label>
        ))}
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

const AppShellTab = ({ usageMap, pageSlugMap, navigate }) => {
  const [activeSection, setActiveSection] = useState('nav');
  const sections = [
    { id: 'activity', label: 'Activity Panel' },
    { id: 'dbsw',     label: 'DB Switcher'    },
    { id: 'debug',    label: 'Debug Panel'    },
    { id: 'footer',   label: 'Footer'         },
    { id: 'header',   label: 'Header'         },
    { id: 'nav',      label: 'Navigation'     },
  ];
  return (
    <div className="stui-preview-zone stui-tab-body">
      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {sections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {/* Navigation */}
      {activeSection === 'nav' && (
        <Section title="Navigation" description="Sidebar nav items at three indent levels. The sidebar uses stui-nav-sidebar; individual items use stui-nav-item with stui-nav-item-level-1/2/3." cssFile="STUI.css" cssSelector=".stui-nav-sidebar" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ background: '#1a5568', padding: '8px 0', borderRadius: 'var(--radius-md)', width: 240, marginBottom: 8 }}>
            {[
              { level: 1, label: 'Reports',         active: true  },
              { level: 2, label: 'I/O Assignments',  active: false },
              { level: 2, label: 'Drawing Log',      active: false },
              { level: 1, label: 'Settings',         active: false },
            ].map((item, i) => (
              <div key={i} className="stui-nav-item-container">
                <button className={`stui-nav-item stui-nav-item-level-${item.level}${item.active ? ' active' : ''}`}>
                  <div className="stui-nav-item-content">
                    <span className="stui-nav-item-spacer" />
                    <span className="stui-nav-item-label">{item.label}</span>
                  </div>
                </button>
              </div>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-nav-sidebar / .stui-nav-item / .stui-nav-item-level-1/2/3 / .stui-nav-item.active" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-nav-sidebar',            'Sidebar container — full-height, teal bg, collapsible'],
              ['.stui-nav-sidebar.collapsed',  'Sidebar in collapsed/icon-only state'],
              ['.stui-nav-scroll',             'Scrollable inner area for nav items'],
              ['.stui-nav-item',               'Individual nav button — full width, left-aligned'],
              ['.stui-nav-item-level-1',       'Top-level item — no indent'],
              ['.stui-nav-item-level-2',       'Sub-item — 16px indent'],
              ['.stui-nav-item-level-3',       'Deep sub-item — 32px indent'],
              ['.stui-nav-item.active',        'Currently selected item — white highlight'],
              ['.stui-nav-item-content',       'Flex row inside the item button'],
              ['.stui-nav-item-label',         'Text label within nav item'],
              ['.stui-nav-item-spacer',        'Icon placeholder / indent spacer'],
              ['.stui-nav-future-badge',       '"FUTURE" label badge on not-yet-implemented items'],
              ['.stui-nav-footer',             'Bottom area of sidebar (version, collapse toggle)'],
              ['.stui-nav-footer-btn',         'Collapse / expand toggle button in sidebar footer'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-nav-item" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <NavConfigurator />
        </Section>
      )}

      {/* Header */}
      {activeSection === 'header' && (
        <Section title="Header" description="Top app bar with logo, DB badge, user menu trigger, and activity panel toggle." cssFile="STUI.css" cssSelector=".stui-header" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div className="stui-header" style={{ position: 'relative', marginBottom: 8 }}>
            <div className="stui-header-left">
              <span style={{ fontWeight: 700, fontSize: 15, color: 'white', letterSpacing: 1 }}>DAWN</span>
            </div>
            <div className="stui-header-right">
              <button className="stui-header-db-btn"><Database size={13} /><span>GWA</span></button>
              <button className="stui-header-user-btn"><User size={14} /><span>T. McMurry</span><ChevronDown size={12} /></button>
              <button className="stui-header-activity-btn"><ArrowDownUp size={15} /></button>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-header / .stui-header-left / .stui-header-right / .stui-header-db-btn / .stui-header-user-btn / .stui-header-activity-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-header',               'Top app bar — teal, flex, fixed height'],
              ['.stui-header-left',          'Left slot — logo / app name'],
              ['.stui-header-right',         'Right slot — db, user, activity buttons'],
              ['.stui-header-db-btn',        'Database name trigger button'],
              ['.stui-header-user-btn',      'User identity / menu trigger'],
              ['.stui-header-activity-btn',  'Data transfer activity panel toggle'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <div className="stui-col-8">
            <div className="stui-config-output">stui-header</div>
            <div className="stui-hint">Layout slots only — nothing to configure. Paste <code>stui-header</code> into CSS Output Test to render.</div>
          </div>
        </Section>
      )}

      {/* Activity Panel */}
      {activeSection === 'activity' && (
        <Section title="Activity Panel" description="Slide-in panel showing active data transfers. Floats from the header activity button." cssFile="STUI.css" cssSelector=".stui-activity-panel" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', marginBottom: 8, display: 'inline-block' }}>
            <div className="stui-activity-panel" style={{ position: 'relative', top: 'auto', right: 'auto', width: 280 }}>
              <div className="stui-activity-panel-header">
                <div className="stui-row-8">
                  <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--color-gray-700)' }}>Data Transfer</span>
                </div>
              </div>
              <div className="stui-activity-entries-scroll">
                <div className="stui-activity-entry">
                  <div className="stui-entry-icon-col"><Download size={13} style={{ color: '#16a34a' }} /></div>
                  <div className="stui-entry-main-col">
                    <div className="stui-entry-name-row">
                      <span className="stui-entry-name">I/O Assignments</span>
                      <span className="stui-entry-format-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>XLSX</span>
                    </div>
                    <div className="stui-entry-phase">Generating…</div>
                    <div className="stui-entry-progress-track">
                      <div className="stui-entry-progress-fill" style={{ width: '60%', background: 'var(--color-primary)' }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="stui-activity-panel-footer">
                <div className="stui-row-4">
                  <span style={{ fontSize: 9, color: 'var(--color-gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>Clear:</span>
                  {['1m', '1 day'].map(o => <button key={o} className="stui-activity-clear-pill">{o}</button>)}
                </div>
                <button className="stui-activity-history-link"><Clock size={11} /> History</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-activity-panel / .stui-activity-entry / .stui-activity-icon / .stui-activity-icon.active" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-activity-icon',           'Header activity toggle — pulsing when transfers are active'],
              ['.stui-activity-icon.active',    'Pulse animation state'],
              ['.stui-activity-panel',          'Float panel containing transfer list'],
              ['.stui-activity-panel-header',   'Panel title row'],
              ['.stui-activity-entries-scroll', 'Scrollable list of transfer entries'],
              ['.stui-activity-entry',          'Single transfer row — icon + name + progress'],
              ['.stui-activity-entry.done',     'Completed transfer (muted)'],
              ['.stui-activity-entry.failed',   'Failed transfer (red tint)'],
              ['.stui-entry-icon-col',          'Left icon column'],
              ['.stui-entry-main-col',          'Right content column — name, phase, progress'],
              ['.stui-entry-name-row',          'Name + format badge row'],
              ['.stui-entry-name',              'Report/file name text'],
              ['.stui-entry-format-badge',      'XLSX/PDF/CSV badge chip'],
              ['.stui-entry-phase',             'Current phase label (Generating, Streaming…)'],
              ['.stui-entry-progress-track',    'Gray progress bar track'],
              ['.stui-entry-progress-fill',     'Filled portion of progress bar'],
              ['.stui-activity-empty',          'Empty state message when no transfers'],
              ['.stui-activity-panel-footer',   'Footer row — clear pills + history link'],
              ['.stui-activity-clear-pill',     'Time-window clear button (1m, 1 day)'],
              ['.stui-activity-history-link',   'Link to Activity History page'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-activity-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ActivityConfigurator />
        </Section>
      )}

      {/* DB Switcher */}
      {activeSection === 'dbsw' && (
        <Section title="DB Switcher" description="Database selection modal opened from the DB badge in the header. Lists available databases with the current one marked." cssFile="STUI.css" cssSelector=".stui-db-switcher-modal" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-db-switcher-modal" style={{ width: 340, height: 'auto', position: 'relative' }}>
              <div className="stui-db-switcher-header">
                <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>DAWN</div>
                <button className="stui-modal-close">&times;</button>
              </div>
              <div className="stui-db-switcher-list">
                {[
                  { name: 'GWA',         desc: 'Greater Wenatchee Area', active: true  },
                  { name: 'LEAVENWORTH', desc: 'Leavenworth Project',    active: false },
                ].map(db => (
                  <button key={db.name} className={`stui-db-switcher-row${db.active ? ' current' : ''}`}>
                    <div className="stui-db-switcher-logo"><div className="stui-db-switcher-logo-placeholder"><Database size={14} /></div></div>
                    <div className="stui-db-switcher-info">
                      <span className="stui-db-switcher-name">{db.name}</span>
                      <span className="stui-db-switcher-desc">{db.desc}</span>
                    </div>
                    {db.active && <span className="stui-db-switcher-badge">Current</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-db-switcher-modal / .stui-db-switcher-header / .stui-db-switcher-list / .stui-db-switcher-row / .stui-db-switcher-badge" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-db-switcher-modal',            'DB selection modal card'],
              ['.stui-db-switcher-header',           'Header row — app logo + close button'],
              ['.stui-db-switcher-list',             'List of available databases'],
              ['.stui-db-switcher-row',              'Single database row button'],
              ['.stui-db-switcher-row.current',      'Currently active database — highlighted'],
              ['.stui-db-switcher-logo',             'Logo thumbnail container'],
              ['.stui-db-switcher-logo-placeholder', 'Fallback icon when no logo image'],
              ['.stui-db-switcher-info',             'DB name + description wrapper'],
              ['.stui-db-switcher-name',             'Database short name (GWA, etc.)'],
              ['.stui-db-switcher-desc',             'Database long description'],
              ['.stui-db-switcher-badge',            '"Current" active badge'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-db-switcher-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <div className="stui-col-8">
            <div className="stui-config-output">stui-db-switcher-modal</div>
            <p className="stui-hint">No token variants — paste <code>stui-db-switcher-modal</code> into CSS Output Test to render.</p>
          </div>
        </Section>
      )}

      {/* Footer */}
      {activeSection === 'footer' && (
        <Section title="Footer" description="Bottom app bar — always visible. Shows active database, app version, and debug panel trigger." cssFile="STUI.css" cssSelector=".stui-footer" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 4 }}>
            <div className="stui-demo-header-bar">
              <div className="stui-footer-left">
                <span className="stui-footer-db-badge"><Database size={12} /><span>GWA</span></span>
                <span className="stui-footer-version">v18.001</span>
              </div>
              <div className="stui-footer-right">
                <button className="stui-debug-btn"><Wrench size={11} /></button>
              </div>
            </div>
          </div>
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-demo-header-bar">
              <div className="stui-footer-left">
                <span className="stui-footer-db-badge stui-footer-db-badge-preprod"><Database size={12} /><span>PREPROD</span></span>
                <span className="stui-footer-version stui-footer-version-preprod">v18.001</span>
              </div>
              <div className="stui-footer-right">
                <button className="stui-debug-btn"><Wrench size={11} /></button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-footer / .stui-footer-left / .stui-footer-right / .stui-footer-db-badge / .stui-footer-version / .stui-debug-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-footer',                  'Footer bar — teal, full width, fixed bottom'],
              ['.stui-footer-left',             'Left slot — db badge + version string'],
              ['.stui-footer-right',            'Right slot — debug trigger'],
              ['.stui-footer-db-badge',         'Active database chip — icon + name'],
              ['.stui-footer-db-badge-preprod', 'Pre-production variant — amber tone'],
              ['.stui-footer-version',          'App version string next to db badge'],
              ['.stui-footer-version-preprod',  'Pre-production version variant'],
              ['.stui-debug-btn',               'Wrench icon button — opens debug panel'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-footer" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <FooterConfigurator />
        </Section>
      )}

      {/* Debug Panel */}
      {activeSection === 'debug' && (
        <Section title="Debug Panel" description="Developer overlay panel opened by the bug icon in the footer. Contains hover inspectors, theme switcher, and diagnostic tools." cssFile="STUI.css" cssSelector=".stui-debug-panel" onChipClick={() => {}} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ padding: 8, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8, display: 'inline-block' }}>
            <div className="stui-debug-panel" style={{ position: 'relative', bottom: 'auto', right: 'auto' }}>
              <div className="stui-debug-head">
                <span className="stui-debug-title">Debug Tools</span>
                <span className="stui-debug-hint">Shift+F12</span>
                <button className="stui-debug-close"><X size={12} /></button>
              </div>
              <div className="stui-debug-section">Hover Inspectors</div>
              <div className="stui-debug-row">
                <span className="stui-debug-label">CSS Variables</span>
                <span className="stui-debug-tog" />
              </div>
              <div className="stui-debug-row">
                <span className="stui-debug-label">Tab Order</span>
                <span className="stui-debug-tog on" />
              </div>
              <div className="stui-debug-section">Theme</div>
              <div className="stui-debug-mode-row">
                <button className="stui-debug-mode-btn on">Standard</button>
                <button className="stui-debug-mode-btn">Dark</button>
                <button className="stui-debug-mode-btn">Dim</button>
                <button className="stui-debug-mode-btn">Sepia</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-debug-panel / .stui-debug-head / .stui-debug-row / .stui-debug-tog / .stui-debug-mode-btn" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-debug-panel',       'Floating debug card — positioned above footer'],
              ['.stui-debug-head',        'Panel header row — title, shortcut hint, close button'],
              ['.stui-debug-title',       'Panel title text'],
              ['.stui-debug-hint',        'Keyboard shortcut hint (Shift+F12)'],
              ['.stui-debug-close',       'Close button'],
              ['.stui-debug-section',     'Section divider label within the panel'],
              ['.stui-debug-row',         'Toggle row — label + toggle switch'],
              ['.stui-debug-label',       'Toggle label text'],
              ['.stui-debug-tog',         'Toggle switch (off state)'],
              ['.stui-debug-tog.on',      'Toggle switch (on state)'],
              ['.stui-debug-mode-row',    'Theme button row'],
              ['.stui-debug-mode-btn',    'Theme selector button'],
              ['.stui-debug-mode-btn.on', 'Currently active theme button'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-debug-panel" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <DebugConfigurator />
        </Section>
      )}
    </div>
  );
};

/* ── Tab: Popups ───────────────────────────────────────────── */

const POPUP_SECTIONS = [
  { id: 'anatomy',   label: 'Anatomy'               },
  { id: 'controls',  label: 'Controls'              },
  { id: 'dividers',  label: 'Dividers'              },
  { id: 'email',     label: 'Email'                 },
  { id: 'sizes',     label: 'Sizes & Variants'      },
  { id: 'title-bar', label: 'Title Bar'             },
  { id: 'titles',    label: 'Titles'                },
];

const PopupDemoHeader = ({ title, children }) => (
  <div className="stui-modal-header">
    <h3>{title}</h3>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {children}
      <button className="stui-modal-close"><X size={20} /></button>
    </div>
  </div>
);

/* —— Modal Size Configurator —— */
const MODAL_SIZES = [
  ['',              'stui-modal (default)', '400px'],
  ['stui-modal-md', 'stui-modal-md',        '600px'],
  ['stui-modal-lg', 'stui-modal-lg',        '720px, 90vh'],
  ['stui-modal-xl', 'stui-modal-xl',        '960px, 90vh'],
  ['stui-modal-xxl', 'stui-modal-xxl',      '1400px (95vw), 90vh: wide grids'],
];
const ModalSizeConfigurator = () => {
  const [size, setSize]   = useState('stui-modal-md');
  const [title, setTitle] = useState('Add Task');
  const cur = MODAL_SIZES.find(s => s[0] === size);
  const output = `stui-modal${size ? " " + size : ""} · ${cur[2]} · title "${title || "Modal Title"}"`;
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Title', cfgTextInput(title, setTitle, 200))}
        {cfgRow('Size', <div className="stui-flex-10">{cfgRadios('modal-size', MODAL_SIZES.map(s => s[1]), cur[1],
          lbl => setSize(MODAL_SIZES.find(s => s[1] === lbl)[0]))}</div>)}
      </div>
      <div style={{ padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)'}}>
        <div className={`stui-modal ${size}`} style={{ position: 'relative', maxHeight: 'none', maxWidth: '100%' }}>
          <PopupDemoHeader title={title || 'Modal Title'} />
          <div className="stui-modal-body stui-text-placeholder">…body content…</div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* —— Modal Controls Configurator —— */
const ModalControlsConfigurator = () => {
  const [primary, setPrimary]     = useState('Add Step');
  const [secondary, setSecondary] = useState('Cancel');
  const [busyLabel, setBusyLabel] = useState('Adding…');
  const [variant, setVariant]     = useState('primary');
  const [disabled, setDisabled]   = useState(false);
  const [busy, setBusy]           = useState(false);
  const [showSecondary, setShowSecondary] = useState(true);
  const label = busy ? busyLabel : primary;
  const off   = disabled || busy;
  const output = ['stui-modal-footer',
    showSecondary ? `stui-btn-secondary "${secondary}"` : null,
    `stui-btn-${variant} "${label}"`,
    off ? 'disabled' : null].filter(Boolean).join(' · ');
  return (
    <div className="stui-col-12">
      <div className="stui-col-8">
        {cfgRow('Primary', cfgTextInput(primary, setPrimary, 180))}
        {cfgRow('Secondary', cfgTextInput(secondary, setSecondary, 180))}
        {cfgRow('Busy label', cfgTextInput(busyLabel, setBusyLabel, 180))}
        {cfgRow('Variant', <div className="stui-flex-10">{cfgRadios('modal-ctl', ['primary', 'danger'], variant, setVariant)}</div>)}
        {cfgRow('State', cfgCheck(disabled, setDisabled, 'Disabled'))}
        {cfgRow('', cfgCheck(busy, setBusy, 'Busy (label swaps, disabled)'))}
        {cfgRow('', cfgCheck(showSecondary, setShowSecondary, 'Show secondary'))}
      </div>
      <div style={{ padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)'}}>
        <div className="stui-modal" style={{ position: 'relative', width: 440, maxWidth: '100%', maxHeight: 'none' }}>
          <div className="stui-modal-footer">
            {showSecondary && <button className="stui-btn stui-btn-secondary">{secondary || 'Cancel'}</button>}
            <button className={`stui-btn stui-btn-${variant}`} disabled={off}>{label || 'Confirm'}</button>
          </div>
        </div>
      </div>
      <ConfigOutput text={output} />
    </div>
  );
};

/* ── Tab: Data Entry Module ──────────────────── */

const DataEntryTab = ({ usageMap, pageSlugMap, navigate, onChipClick }) => (
  <div className="stui-preview-zone stui-tab-body">

    <Section
      title="Module States"
      description="The three page-level states a module renders instead of its content. All three replace the whole module body, so they are siblings of the content, not overlays. Loading fills a flex parent; error and empty are padded blocks. The colour lives in the class, so an error is not red by itself — compose .stui-text-error onto it."
      cssFile={CSS_LOCATIONS.moduleStates.file}
      cssSelector={CSS_LOCATIONS.moduleStates.selector}
      onChipClick={onChipClick}
      noBodyScroll
    >
      <Subsection label="Preview" />
      <Example label=".stui-module-loading">
        <div className="stui-module-loading">Loading...</div>
      </Example>
      <Example label=".stui-module-error">
        <div className="stui-module-error">Failed to load module</div>
      </Example>
      <Example label=".stui-module-error + .stui-text-error">
        <div className="stui-module-error stui-text-error">Failed to load module</div>
      </Example>
      <Example label=".stui-module-empty">
        <div className="stui-module-empty">
          <p>Estimating</p>
          <p>No views configured. Use the Data Entry Composer to set up this module.</p>
        </div>
      </Example>
      <CSSRef file="STUI.css" selector=".stui-module-loading / .stui-module-error / .stui-module-empty" />

      <Subsection label="Reference" />
      <RefTable
        headers={['Class', 'Role']}
        rows={[
          ['.stui-module-loading', 'Waiting on the fetch — flex-centred, flex 1 so it fills a flex parent, padding 2rem, gray-500, text-sm'],
          ['.stui-module-error', 'The fetch failed — padding 2rem, centred, gray-500. Carries no colour: pair it with .stui-text-error'],
          ['.stui-module-empty', 'Loaded cleanly with nothing configured to show — padding 3rem, centred, gray-400. Its first paragraph is the heading, styled by the class, so the markup needs no extra class'],
        ]}
      />

      <Subsection label="Used By" />
      <UsedBy cssClass="stui-module-loading" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
      <UsedBy cssClass="stui-module-error" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
      <UsedBy cssClass="stui-module-empty" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
    </Section>

  </div>
);

const PopupsTab = ({ usageMap, pageSlugMap, navigate, onChipClick }) => {
  const [activeSection, setActiveSection] = useState('anatomy');
  return (
    <div className="stui-preview-zone stui-tab-body">

      <div className="stui-mb-20">
        <StuiSelect className="stui-select" value={activeSection} onChange={e => setActiveSection(e.target.value)} style={{ minWidth: 220 }}>
          {POPUP_SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </StuiSelect>
      </div>

      {/* ── Anatomy ───────────────────────────────────────────── */}
      {activeSection === 'anatomy' && (
        <Section title="Anatomy" description="THE standard popup. Every popup in the app is: stui-modal-overlay (full-screen dim) → stui-modal (card) → stui-modal-header (blue title bar) → stui-modal-body → stui-modal-footer. No other popup skeleton is compliant — legacy modal-overlay / modal-content markup gets migrated to this." cssFile="STUI.css" cssSelector=".stui-modal-overlay" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ position: 'relative', padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-modal stui-modal-md" style={{ position: 'relative', maxHeight: 'none' }}>
              <PopupDemoHeader title="New Record" />
              <div className="stui-modal-body">
                <div className="stui-detail-grid">
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">General Information</div>
                  </div>
                  <div className="stui-field-group stui-col-16">
                    <label>Name</label>
                    <input type="text" className="stui-field-input" defaultValue="AI-100" />
                  </div>
                  <div className="stui-field-group stui-col-8">
                    <label>Type</label>
                    <StuiSelect className="stui-field-input" defaultValue="Analog Input">
                      <option>Analog Input</option>
                      <option>Digital Input</option>
                    </StuiSelect>
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>Description</label>
                    <textarea className="stui-field-input resizable" rows={3} defaultValue="Chilled water supply temperature" />
                  </div>
                </div>
              </div>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary">Add Record</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal-overlay / .stui-modal / .stui-modal-header / .stui-modal-body / .stui-modal-footer / .stui-modal-close" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Class', 'Role']}
            rows={[
              ['.stui-modal-overlay',   'Full-screen dim overlay behind the popup — z-index 1000'],
              ['.stui-modal',           'The card — centered, white, rounded, shadow, flex column'],
              ['.stui-modal-header',    'Blue title bar — see Title Bar section'],
              ['.stui-modal-body',      'Content area — scrolls when content exceeds max-height'],
              ['.stui-modal-footer',    'Action button row — see Controls section'],
              ['.stui-modal-close',     'White X close button in the title bar'],
              ['.stui-modal-list',      'Scrollable selection list (impersonation-style popups)'],
              ['.stui-detail-grid',     '24-column field grid inside body — same as detail panels'],
              ['.stui-field-group',     'Label + input stack — same as Standard View form fields'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-modal" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModalConfigurator />
        </Section>
      )}

      {/* ── Title Bar ─────────────────────────────────────────── */}
      {activeSection === 'title-bar' && (
        <Section title="Title Bar" description="Every popup gets the blue title bar: var(--color-primary) background, white 600-weight title, white close X pinned top-right. Optional action icons (external link, back) sit LEFT of the X. White headers with gray underlines are legacy — not compliant." cssFile="STUI.css" cssSelector=".stui-modal-header" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <PopupDemoHeader title="Standard Title Bar" />
            </div>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <PopupDemoHeader title="With Action Icon">
                <button className="stui-modal-close" title="Open full page"><ExternalLink size={16} /></button>
              </PopupDemoHeader>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal-header / .stui-modal-header h3 / .stui-modal-title / .stui-modal-close" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Rule', 'Spec']}
            rows={[
              ['Background',    'var(--color-primary) — no border-bottom, top corners rounded'],
              ['Title',         '<h3> (or .stui-modal-title) — white, text-lg, weight 600, no margin'],
              ['Close X',       '.stui-modal-close — always present, always rightmost, rgba-white with white hover'],
              ['Action icons',  'Optional — 16px lucide icons left of the X, same .stui-modal-close styling'],
              ['Height',        'min-height 48px, spacing-md/lg padding'],
              ['Variants',      '.stui-fk-select-header and .stui-db-switcher-header share this exact look'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-modal-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {/* ── Titles ────────────────────────────────────────────── */}
      {activeSection === 'titles' && (
        <Section title="Titles" description="Title text conventions by popup purpose. Titles are nouns, not sentences — no trailing punctuation, em-dashes separate context segments." cssFile="STUI.css" cssSelector={null} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            {['New Task', 'Step Details — 12 — Owner Dropdown', 'Complete Step — Renumbering', 'Building → Drawing → Tag'].map(t => (
              <div key={t} className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
                <PopupDemoHeader title={t} />
              </div>
            ))}
          </div>
          <Subsection label="Reference" />
          <RefTable
            headers={['Popup purpose', 'Title format', 'Example']}
            rows={[
              ['Create',        '"New <thing>" — singular noun',                    'New Task · New Step · New User'],
              ['Detail / view', '"<thing> Details — <key> — <name>"',               'Step Details — 12 — Owner Dropdown'],
              ['Wizard page',   '"<action> — <page name>" — title changes per page', 'Complete Step — Details · Complete Step — Renumbering'],
              ['Stacked (FK)',  'Breadcrumb of the stack, arrows between levels',    'Building → Drawing → Tag'],
              ['Confirm',       'Question-free action phrase',                       'Delete 3 Records · Discard Changes'],
            ]}
          />
        </Section>
      )}

      {/* ── Controls ──────────────────────────────────────────── */}
      {activeSection === 'controls' && (
        <Section title="Controls" description="Footer buttons use .stui-btn from Elements → Buttons. Primary action is ALWAYS rightmost. Secondary (Cancel/Close/Back) sits left of it. Read-only popups have no footer at all — the title bar X is the only way out." cssFile="STUI.css" cssSelector=".stui-modal-footer" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary">Add Step</button>
              </div>
            </div>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Back</button>
                <button className="stui-btn stui-btn-primary">Complete & Renumber</button>
              </div>
            </div>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-danger">Delete</button>
              </div>
            </div>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary" disabled>Add Step</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal-footer / .stui-btn / .stui-btn-primary / .stui-btn-secondary / .stui-btn-danger" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Rule', 'Spec']}
            rows={[
              ['Placement',      'Footer only — right-aligned, spacing-sm gap, top border divider'],
              ['Primary action', 'Rightmost, .stui-btn-primary — Add / Apply / Save / Next / Confirm'],
              ['Secondary',      'Left of primary, .stui-btn-secondary — Cancel / Close / Back'],
              ['Destructive',    '.stui-btn-danger in the primary position — Delete / Remove'],
              ['Disabled state', 'Primary disabled until required fields are valid'],
              ['Busy state',     'Label swaps while saving — "Add Step" → "Adding..." — button disabled'],
              ['Read-only',      'No footer. Title bar X closes. Never a lone "Close" button row'],
              ['Wizards',        'Back (secondary) + Next/finish (primary). Page 1 gets Next only'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-modal-footer" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModalControlsConfigurator />
        </Section>
      )}

      {/* ── Dividers ──────────────────────────────────────────── */}
      {activeSection === 'dividers' && (
        <Section title="Dividers" description="Popups divide content the same way detail panels do: teal stui-detail-section-header bars group fields inside the body. The footer's top border is the only horizontal rule — no ad-hoc <hr> or border hacks." cssFile="STUI.css" cssSelector={null} onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ position: 'relative', padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-modal stui-modal-md" style={{ position: 'relative', maxHeight: 'none' }}>
              <PopupDemoHeader title="New Step" />
              <div className="stui-modal-body">
                <div className="stui-detail-grid">
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">General Information</div>
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>Name</label>
                    <input type="text" className="stui-field-input" defaultValue="" placeholder="Step name" />
                  </div>
                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">Step Details</div>
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>Description</label>
                    <textarea className="stui-field-input resizable" rows={2} placeholder="What does this step involve?" />
                  </div>
                </div>
              </div>
              <div className="stui-inline-notice" style={{ margin: '0 var(--spacing-lg) var(--spacing-lg)' }}>
                Contained note in the body — for a consequence the reader should not skim past.
              </div>
              <div className="stui-inline-notice stui-inline-notice-warning" style={{ margin: '0 var(--spacing-lg) var(--spacing-lg)' }}>
                <AlertTriangle size={13} />
                <span>Warning tone — targets <strong>PRODUCTION</strong>. Run a dry run first and read the result.</span>
              </div>
              <div className="stui-inline-notice stui-inline-notice-success" style={{ margin: '0 var(--spacing-lg) var(--spacing-lg)' }}>
                <CheckCircle size={13} />
                <div><div className="stui-fw-semibold">Success tone — sync complete</div><div className="stui-text-xs stui-text-muted">12s • 40 objects</div></div>
              </div>
              <div className="stui-inline-notice stui-inline-notice-error" style={{ margin: '0 var(--spacing-lg) var(--spacing-lg)' }}>
                <XCircle size={13} />
                <div><div className="stui-fw-semibold">Error tone — sync failed</div><div className="stui-text-xs stui-text-muted">Login failed for the target database</div></div>
              </div>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary" disabled>Add Step</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-detail-section-header / .stui-modal-footer / .stui-modal-hint / .stui-inline-notice / .stui-modal-scroll" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Divider', 'Use']}
            rows={[
              ['.stui-detail-section-header', 'Teal bar grouping body fields — same element as Standard View detail panels'],
              ['Footer top border',           'Built into .stui-modal-footer — separates content from actions'],
              ['.stui-modal-hint',            'Muted explanatory line above scrolling content (wizard page 2 pattern)'],
              ['.stui-inline-notice',         'Contained note in the body — gray-50 panel, rounded, for a consequence the reader should not skim past. Not .stui-alert-*, which floats'],
              ['.stui-inline-notice.stui-inline-notice-warning', 'Warning tone — yellow panel, amber border and text, icon pinned to the first line. For something the reader must not miss, e.g. that an action writes to Production'],
              ['.stui-inline-notice.stui-inline-notice-success / -error', 'Success and error tones — the same box in green or red, for the outcome of a run. Compose .stui-text-xs .stui-text-muted onto a detail line under the bold label'],
              ['.stui-modal-footer-split',    'Footer modifier — space-between, so a summary or toggle sits left and the buttons right'],
              ['.stui-modal-scroll',          'Scroll region that grows to fill the body (flex: 1, overflow-y: auto, no border or padding). Needs a flex-column body: pair it with .stui-modal-body-grid'],
              ['Header',                      'No border — the blue bar IS the divider'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-detail-section-header" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-modal-hint" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-inline-notice" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-modal-scroll" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
        </Section>
      )}

      {/* ── Email ─────────────────────────────────────────────── */}
      {activeSection === 'email' && (
        <Section title="Email" description="StuiEmailModal (components/common/StuiEmailModal.js) is THE email popup. The caller passes an audience, an initial subject and context; the user edits the subject and writes a rich-text body. Audiences are resolved to addresses by POST /api/email/send, never by the caller. Open it from a 16px Mail action icon in a title bar." cssFile="STUI.css" cssSelector=".stui-modal-overlay" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            <div className="stui-modal" style={{ position: 'relative', width: 440, maxHeight: 'none' }}>
              <PopupDemoHeader title="Launcher — Help title bar">
                <button className="stui-modal-close" title="Email support about this page"><Mail size={16} /></button>
                <button className="stui-modal-close" title="Edit this page's help"><Pencil size={16} /></button>
              </PopupDemoHeader>
            </div>
            <div className="stui-modal stui-modal-lg" style={{ position: 'relative', maxHeight: 'none', maxWidth: '100%' }}>
              <PopupDemoHeader title="Email Support" />
              <div className="stui-modal-body">
                <div className="stui-field-group stui-mb-md">
                  <label>To</label>
                  <div className="stui-field-display stui-field-readonly">DAWN Support</div>
                </div>
                <div className="stui-field-group stui-mb-md">
                  <label>Subject</label>
                  <input type="text" className="stui-field-input" defaultValue="DAWN Help: Panels (GWA)" />
                </div>
                <StuiRichText value="" onChange={() => {}} placeholder="What's the question, or what went wrong?" minHeight={140} />
                <p className="stui-label-note">Page, Database, Route, Help Key and your name and email are included with the message.</p>
              </div>
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary">Cancel</button>
                <button className="stui-btn stui-btn-primary">Send</button>
              </div>
            </div>
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal-lg / .stui-field-display / .stui-field-readonly / .stui-field-input / .stui-rte-editor / .stui-label-note" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Prop', 'Purpose']}
            rows={[
              ['audience',      "Server-resolved recipient group — 'support' goes to the DAWN support group; 'users' (SysAdmin) goes to recipientIds"],
              ['audienceLabel', 'Text shown on the read-only To line'],
              ['subjectQualifier', "Optional database name for the locked prefix — 'GWA' gives DAWN Help (GWA):"],
              ['subject',       'Initial subject AFTER the prefix — the user can edit it (prefix + subject max 200)'],
              ['context',       '{ Label: value } pairs appended to the email as small print — page, database, route'],
              ['title',         "Title bar text — defaults to 'Send Email'"],
              ['placeholder',   'Body placeholder text'],
              ['onClose / onSent', 'Close handler (also called after a send) and optional post-send callback'],
              ['recipientIds',  "audience 'users' only — AppSec_Users ids; the server looks up addresses and drops inactive users"],
              ['query',         'The search that produced recipientIds — recorded in the audit row'],
              ['templates',     'Canned messages (config/emailTemplates.js) — adds a Template picker and its fields'],
              ['defaultTemplate', 'Template key selected on open (else the first); with templates one is always selected'],
              ['recipients',    '[{ name, email }] — adds an eye on the To line that lists the addresses, read-only'],
              ['subjectTools',  '({ template, setSubject, setBodyHtml }) => node at the end of the subject row — e.g. the rev log picker'],
              ['allowPreview / reviewThreshold', 'Preview button (server-built email in a sandboxed frame); above the threshold Send becomes Review & Send'],
            ]}
          />
          <RefTable
            headers={['Rule', 'Spec']}
            rows={[
              ['Size',          '.stui-modal-lg — the rich-text body needs the room'],
              ['Stacking',      'Portals to document.body, so it opens above the popup that launched it'],
              ['Send enabled',  'Only with a subject and a non-empty body — and, with a template, every field picked and every [Slot] gone'],
              ['Template fields', "datetime (date input + 15-minute time select) / select; picking replaces the field's [Slot] in subject and body live; times written with the sender's zone"],
              ['Plain by default', 'With none of the broadcast props (the Help popup), there is no template, preview or recipient list'],
              ['Delivery',      "No-reply sender, Reply-To = the signed-in user, body sanitized server-side. 'users' is a broadcast: sender on To, recipients BCC, 450 per message"],
              ['Subject format','Prefix is the server\'s, per audience, shown locked (.stui-field-prefix): "DAWN Help (GWA): Panels", "DAWN Notice (GF): Service Outage - Unavailable"'],
            ]}
          />
        </Section>
      )}

      {/* ── Sizes & Variants ──────────────────────────────────── */}
      {activeSection === 'sizes' && (
        <Section title="Sizes & Variants" description="Four fixed widths plus the full-width FK stacking variant. Pick by content, not by taste — forms get -md, forms with big textareas or tables get -lg, wide multi-column grids get -xl, simple pickers/confirms stay default." cssFile="STUI.css" cssSelector=".stui-modal" onChipClick={onChipClick} noBodyScroll>
          <Subsection label="Preview" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', marginBottom: 8 }}>
            {[
              { cls: '', w: 400, label: 'stui-modal — 400px' },
              { cls: 'stui-modal-md', w: 600, label: 'stui-modal-md — 600px' },
              { cls: 'stui-modal-lg', w: 720, label: 'stui-modal-lg — 720px' },
              { cls: 'stui-modal-xl', w: 960, label: 'stui-modal-xl — 960px' },
            ].map(s => (
              <div key={s.label} className={`stui-modal ${s.cls}`} style={{ position: 'relative', maxHeight: 'none', maxWidth: '100%' }}>
                <PopupDemoHeader title={s.label} />
              </div>
            ))}
          </div>
          <CSSRef file="STUI.css" selector=".stui-modal / .stui-modal-md / .stui-modal-lg / .stui-modal-xl / .stui-fk-select" />
          <Subsection label="Reference" />
          <RefTable
            headers={['Size', 'Width', 'Use for']}
            rows={[
              ['.stui-modal (default)', '400px',              'Confirms, pickers, single-field prompts'],
              ['.stui-modal-md',        '600px',              'Create/edit forms — Add Task, Add Step'],
              ['.stui-modal-lg',        '720px, 90vh',        'Forms with large textareas, wizards, embedded tables'],
              ['.stui-fk-select',       '90% / max 1200px',   'FK record browsing — stacks up to 5 deep with 15px offsets'],
              ['All sizes',             'max-width 90vw',     'Never overflow the viewport'],
            ]}
          />
          <Subsection label="Used By" />
          <UsedBy cssClass="stui-fk-select" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-modal-md" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <UsedBy cssClass="stui-modal-lg" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
          <Subsection label="Configurator" />
          <ModalSizeConfigurator />
        </Section>
      )}

    </div>
  );
};

/* ── Authorization Tab ─────────────────────────────────────── */

const LOGO_URL = 'https://pmdprodstor.blob.core.windows.net/org-assets/brand/wm-logo-white.png';

const INVITE_EMAIL_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Rajdhani:wght@400;500;600&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#f0f4f5;font-family:'Rajdhani',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4f5;padding:40px 20px;">
  <tr><td align="center">
    <table width="520" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,0.10);">
      <tr>
        <td bgcolor="#1a5568" style="background:#1a5568;padding:20px 28px;">
          <table cellpadding="0" cellspacing="0" border="0"><tr>
            <td style="vertical-align:middle;padding-right:14px;">
              <img src="${LOGO_URL}" alt="Wunderlich-Malec" height="36" style="display:block;border:0;">
            </td>
            <td style="border-left:1px solid #5f8895;padding-left:14px;vertical-align:middle;">
              <div style="color:#ffffff;font-family:'Orbitron',Arial,sans-serif;font-weight:700;font-size:18px;line-height:1.1;letter-spacing:2px;">DAWN</div>
              <div style="color:#afc3ca;font-family:'Rajdhani',Arial,sans-serif;font-size:10px;letter-spacing:0.12em;line-height:1.4;margin-top:4px;font-weight:normal;">DOCUMENTATION &middot; ASSETS &middot; WIRING &middot; NETWORKS</div>
            </td>
          </tr></table>
        </td>
      </tr>
      <tr>
        <td style="padding:36px 36px 28px;">
          <p style="font-size:17px;color:#1f2937;margin:0 0 16px;font-weight:600;font-family:'Rajdhani',Arial,sans-serif;">Hi Josh Neville,</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">
            You&rsquo;ve been invited to <strong>DAWN</strong>, the Wunderlich-Malec engineering platform
            for Documentation, Assets, Wiring &amp; Networking.
          </p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">Your account has been set up under <strong>Wunderlich-Malec</strong>.</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 28px;font-family:'Rajdhani',Arial,sans-serif;">
            Click the button below to accept your invitation &mdash; you&rsquo;ll sign in with your
            Microsoft account (<strong>josh.neville@wmeng.com</strong>).
          </p>
          <table cellpadding="0" cellspacing="0" style="margin:0 auto 28px;"><tr>
            <td style="background:#1a5568;border-radius:6px;">
              <a href="#" style="display:block;padding:13px 44px;font-size:15px;font-weight:600;color:#ffffff;text-decoration:none;letter-spacing:1px;font-family:'Rajdhani',Arial,sans-serif;">ACCEPT INVITATION</a>
            </td>
          </tr></table>
          <p style="font-size:12px;color:#9ca3af;text-align:center;margin:0 0 24px;font-family:'Rajdhani',Arial,sans-serif;">
            This invitation expires on <strong>Wednesday, May 20, 2026 at 12:00 PM UTC</strong>.
          </p>
          <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 18px;">
          <p style="font-size:11px;color:#9ca3af;margin:0;line-height:1.8;font-family:'Rajdhani',Arial,sans-serif;">
            If the button above doesn&rsquo;t work, copy and paste this link into your browser:<br>
            <a href="#" style="color:#1a5568;word-break:break-all;font-size:11px;">https://ppdawn.wmeng.com/accept-invitation?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.sample</a>
          </p>
        </td>
      </tr>
      <tr>
        <td style="background:#f0f4f5;padding:16px 36px;border-top:1px solid #e5e7eb;">
          <p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;font-family:'Rajdhani',Arial,sans-serif;">
            Wunderlich-Malec Engineering &nbsp;&middot;&nbsp; This is an automated message &mdash; do not reply
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

const TEST_EMAIL_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Rajdhani:wght@400;500;600&display=swap" rel="stylesheet">
</head>
<body style="margin:0;padding:0;background:#f0f4f5;font-family:'Rajdhani',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f4f5;padding:40px 20px;">
  <tr><td align="center">
    <table width="520" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:10px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,0.10);">
      <tr>
        <td bgcolor="#1a5568" style="background:#1a5568;padding:20px 28px;">
          <table cellpadding="0" cellspacing="0" border="0"><tr>
            <td style="vertical-align:middle;padding-right:14px;">
              <img src="${LOGO_URL}" alt="Wunderlich-Malec" height="36" style="display:block;border:0;">
            </td>
            <td style="border-left:1px solid #5f8895;padding-left:14px;vertical-align:middle;">
              <div style="color:#ffffff;font-family:'Orbitron',Arial,sans-serif;font-weight:700;font-size:18px;line-height:1.1;letter-spacing:2px;">DAWN</div>
              <div style="color:#afc3ca;font-family:'Rajdhani',Arial,sans-serif;font-size:10px;letter-spacing:0.12em;line-height:1.4;margin-top:4px;font-weight:normal;">DOCUMENTATION &middot; ASSETS &middot; WIRING &middot; NETWORKS</div>
            </td>
          </tr></table>
        </td>
      </tr>
      <tr>
        <td style="padding:36px 36px 28px;">
          <p style="font-size:17px;color:#1f2937;margin:0 0 16px;font-weight:600;font-family:'Rajdhani',Arial,sans-serif;">Hi Josh Neville,</p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 14px;font-family:'Rajdhani',Arial,sans-serif;">
            This is a test email from <strong>DAWN</strong>, the Wunderlich-Malec engineering platform
            for Documentation, Assets, Wiring &amp; Networking.
          </p>
          <p style="font-size:15px;color:#4b5563;line-height:1.7;margin:0 0 28px;font-family:'Rajdhani',Arial,sans-serif;">
            If you&rsquo;re reading this, email delivery to <strong>josh.neville@wmeng.com</strong> is working correctly.
            No action is required &mdash; you can disregard this message.
          </p>
          <table cellpadding="0" cellspacing="0" style="margin:0 auto 28px;width:100%;"><tr>
            <td style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:14px 24px;text-align:center;">
              <p style="font-size:14px;font-weight:600;color:#166534;margin:0;font-family:'Rajdhani',Arial,sans-serif;letter-spacing:0.5px;">EMAIL DELIVERY CONFIRMED</p>
            </td>
          </tr></table>
          <p style="font-size:12px;color:#9ca3af;text-align:center;margin:0 0 24px;font-family:'Rajdhani',Arial,sans-serif;">
            Sent on <strong>Tuesday, May 13, 2026 at 3:00 PM UTC</strong>
          </p>
          <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 18px;">
          <p style="font-size:11px;color:#9ca3af;margin:0;line-height:1.8;font-family:'Rajdhani',Arial,sans-serif;">
            This message was sent as a delivery test by a DAWN administrator.
          </p>
        </td>
      </tr>
      <tr>
        <td style="background:#f0f4f5;padding:16px 36px;border-top:1px solid #e5e7eb;">
          <p style="font-size:11px;color:#9ca3af;margin:0;text-align:center;font-family:'Rajdhani',Arial,sans-serif;">
            Wunderlich-Malec Engineering &nbsp;&middot;&nbsp; This is an automated message &mdash; do not reply
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;

const AUTH_GATE_STATES = [
  { id: 'video',          label: 'Video'          },
  { id: 'connecting',     label: 'Connecting'     },
  { id: 'authenticating', label: 'Authenticating' },
  { id: 'verifying',      label: 'Verifying'      },
  { id: 'selecting',      label: 'Step: Select'   },
  { id: 'loading',        label: 'Loading'        },
  { id: 'db-picker',      label: 'DB Picker'      },
  { id: 'deadend',        label: 'Dead End'       },
  { id: 'debug',          label: 'Debug'          },
];

const GATE_STEPS = [
  { key: 'connecting',     label: 'Connecting to Microsoft' },
  { key: 'authenticating', label: 'Signing you in'          },
  { key: 'verifying',      label: 'Checking your account'   },
  { key: 'selecting',      label: 'Select a database'       },
  { key: 'loading',        label: 'Loading your workspace'  },
];

const GATE_STEP_MAP = { connecting: 0, authenticating: 1, verifying: 2, selecting: 3, loading: 4 };

// One of each debug-entry modifier, for the Debug preview state — .step and .error are
// real log line types; .empty only ever appears alone (the "No events yet" placeholder),
// shown here alongside the others so all three colors are visible at once.
const PREVIEW_DEBUG_LOG = [
  { time: '[00:412]', msg: 'AUTH: authenticating',                           type: 'step'  },
  { time: '[02:118]', msg: 'API: databases/available → ERROR: Network timeout', type: 'error' },
  { time: '[--:---]', msg: 'No events yet',                                  type: 'empty' },
];

const AUTH_GATE_REF_ROWS = [
  ['.stui-auth-gate-backdrop',          'Full-screen fixed overlay behind the card — teal background, sits above everything else.'],
  ['.stui-auth-gate-exiting',           'Added to the backdrop to fade the gate out once sign-in completes.'],
  ['.stui-auth-gate-card',              'The white card itself — fixed size, column flex layout.'],
  ['.stui-auth-gate-video',             'Full-bleed splash video filling the card before auth starts.'],
  ['.stui-auth-gate-header',            'Teal header bar holding the logo, divider and title.'],
  ['.stui-auth-gate-logo',              'Wunderlich-Malec logo mark in the header.'],
  ['.stui-auth-gate-header-divider',    'Vertical hairline separating the logo from the title block.'],
  ['.stui-auth-gate-title',             '"DAWN" wordmark.'],
  ['.stui-auth-gate-subtitle',          'Small uppercase tagline under the title.'],
  ['.stui-auth-gate-body',              'Flex container for whatever the card is showing below the header (steps, picker, or dead end) — gives the debug panel a positioned ancestor to overlay.'],
  ['.stui-auth-gate-steps',             'Column list of the sign-in progress steps.'],
  ['.stui-auth-gate-step .done/.active/.pending', 'One step row; modifier colors the step label for its state.'],
  ['.stui-auth-gate-step-icon',         'Fixed-size icon slot for a step’s check / spinner / circle.'],
  ['.stui-auth-gate-step-circle',       'Empty ring shown for a pending step.'],
  ['.stui-auth-gate-step-label',        'Step’s text.'],
  ['.stui-auth-gate-db-header',         '"Select a database" heading above the picker list.'],
  ['.stui-auth-gate-db-list',           'Scrollable column of database choice buttons.'],
  ['.stui-auth-gate-db-item',           'One clickable database row.'],
  ['.stui-auth-gate-db-icon',           'Database’s icon image.'],
  ['.stui-auth-gate-db-icon-placeholder', 'Teal initial-letter tile shown when a database has no icon.'],
  ['.stui-auth-gate-db-info',           'Column wrapper for a database’s name + description.'],
  ['.stui-auth-gate-db-name',           'Database display name.'],
  ['.stui-auth-gate-db-desc',           'Database description, truncated to one line.'],
  ['.stui-auth-gate-deadend',           'Centered terminal-state container shown when the gate cannot proceed (no databases, a failed fetch, or an unverified account).'],
  ['.stui-auth-gate-deadend-title',     'Dead-end heading, e.g. "No database access".'],
  ['.stui-auth-gate-deadend-text',      'Dead-end explanation copy.'],
  ['.stui-auth-gate-deadend-action',    'The dead end’s only way out — a bordered button, e.g. Sign out.'],
  ['.stui-auth-gate-debug-panel',       'Floating log overlay inset from the body edges.'],
  ['.stui-auth-gate-debug-entry .step/.error/.empty', 'One log line; modifier colors it by event type.'],
  ['.stui-auth-gate-debug-icon', 'Places the debug toggle top-right on the header. The toggle itself is .stui-debug-btn (with .active while the trace is open), the same button the footer uses.'],
];

const AuthorizationTab = ({ usageMap, pageSlugMap, navigate, onChipClick }) => {
  const [gateIdx, setGateIdx]     = useState(1);
  const [emailView, setEmailView] = useState('invite');
  const [databases, setDatabases] = useState([]);

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data || []))
      .catch(() => {});
  }, []);

  const gateState      = AUTH_GATE_STATES[gateIdx];
  const currentStepIdx = GATE_STEP_MAP[gateState.id] ?? -1;

  const renderGateBody = () => {
    if (gateState.id === 'video') {
      return (
        <div style={{ flex: 1, background: '#0a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.35)', fontFamily: 'var(--font-secondary)', fontSize: 13 }}>
          [ DAWN video splash ]
        </div>
      );
    }
    if (gateState.id === 'db-picker') {
      return (
        <>
          <div className="stui-auth-gate-db-header">Select a database</div>
          <div className="stui-auth-gate-db-list">
            {databases.map(db => {
              const name = db.displayName || db.name;
              return (
                <button key={db.id} className="stui-auth-gate-db-item">
                  {db.iconUrl
                    ? <img src={db.iconUrl} alt="" className="stui-auth-gate-db-icon" />
                    : <div className="stui-auth-gate-db-icon-placeholder">{name.charAt(0)}</div>
                  }
                  <div className="stui-auth-gate-db-info">
                    <div className="stui-auth-gate-db-name">{name}</div>
                    {db.description && <div className="stui-auth-gate-db-desc">{db.description}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      );
    }
    if (gateState.id === 'deadend') {
      return (
        <div className="stui-auth-gate-deadend">
          <div className="stui-auth-gate-deadend-title">No database access</div>
          <div className="stui-auth-gate-deadend-text">
            You have not been granted access to any databases. Please contact your administrator.
          </div>
          <button className="stui-auth-gate-deadend-action" tabIndex={-1} style={{ cursor: 'default' }}>
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
      );
    }
    return (
      <div className="stui-auth-gate-steps">
        {GATE_STEPS.map((step, idx) => {
          const isDone   = idx < currentStepIdx;
          const isActive = !isDone && idx === currentStepIdx;
          const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
          return (
            <div key={step.key} className={`stui-auth-gate-step ${state}`}>
              <div className="stui-auth-gate-step-icon">
                {isDone   ? <CheckCircle size={20} color="#1a5568" /> :
                 isActive ? <Loader2 size={20} className="stui-spinner" /> :
                            <div className="stui-auth-gate-step-circle" />}
              </div>
              <span className="stui-auth-gate-step-label">{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1 }}>

      {/* ── Email Format ──────────────────────────── */}
      <div className="stui-detail-section-header">Email Format</div>
      <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button className={`stui-btn ${emailView === 'invite' ? 'stui-btn-primary' : 'stui-btn-secondary'}`} onClick={() => setEmailView('invite')}>Invitation</button>
            <button className={`stui-btn ${emailView === 'test'   ? 'stui-btn-primary' : 'stui-btn-secondary'}`} onClick={() => setEmailView('test')}>Test Email</button>
          </div>
          <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--color-gray-600)' }}>
            <strong>Subject:</strong>&nbsp;{emailView === 'invite' ? "You've been invited to DAWN" : 'DAWN Email Test'}
          </div>
          <div style={{ border: 'var(--border-width) solid var(--color-border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: 500 }}>
            <iframe
              key={emailView}
              srcDoc={emailView === 'invite' ? INVITE_EMAIL_HTML : TEST_EMAIL_HTML}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={emailView === 'invite' ? 'Invitation Email Preview' : 'Test Email Preview'}
            />
          </div>
      </div>

      {/* ── Authorization Gate ────────────────────── */}
      <Section
        title="Authorization Gate"
        description="The sign-in / database-selection card shown before the app loads, and the screens built on it: database picker, access denied, invitation, dev-only guard. Includes the dead-end (terminal-state) and debug-trace pieces, both reachable in the switcher below."
        cssFile={CSS_LOCATIONS.authGate.file}
        cssSelector={CSS_LOCATIONS.authGate.selector}
        onChipClick={onChipClick}
        noBodyScroll
      >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <button className="stui-btn stui-btn-secondary" onClick={() => setGateIdx(i => Math.max(0, i - 1))} disabled={gateIdx === 0}><ChevronLeft size={14} /></button>
            {AUTH_GATE_STATES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setGateIdx(i)}
                style={{
                  padding: '4px 11px', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 12,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: i === gateIdx ? 'var(--color-primary)' : 'var(--color-gray-100)',
                  color: i === gateIdx ? 'var(--color-on-primary)' : 'var(--color-gray-600)',
                  fontWeight: i === gateIdx ? 600 : 400,
                }}
              >{s.label}</button>
            ))}
            <button className="stui-btn stui-btn-secondary" onClick={() => setGateIdx(i => Math.min(AUTH_GATE_STATES.length - 1, i + 1))} disabled={gateIdx === AUTH_GATE_STATES.length - 1}><ChevronRight size={14} /></button>
          </div>
          <div style={{ background: '#1a5568', borderRadius: 'var(--radius-md)', padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="stui-auth-gate-card" style={{ position: 'relative' }}>
              {gateState.id === 'video' ? renderGateBody() : (
                <>
                  <div className="stui-auth-gate-header">
                    <img src={wmLogo} alt="Wunderlich-Malec" className="stui-auth-gate-logo" />
                    <div className="stui-auth-gate-header-divider" />
                    <div>
                      <div className="stui-auth-gate-title">DAWN</div>
                      <div className="stui-auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
                    </div>
                  </div>
                  <button
                    className={`stui-debug-btn stui-auth-gate-debug-icon${gateState.id === 'debug' ? ' active' : ''}`}
                    tabIndex={-1}
                    title={gateState.id === 'debug' ? 'Debug trace open' : 'Show debug trace'}
                  >
                    <Bug size={13} />
                  </button>
                  <div className="stui-auth-gate-body">
                    {renderGateBody()}
                    {gateState.id === 'debug' && (
                      <div className="stui-auth-gate-debug-panel">
                        {PREVIEW_DEBUG_LOG.map((entry, i) => (
                          <div key={i} className={`stui-auth-gate-debug-entry${entry.type !== 'info' ? ' ' + entry.type : ''}`}>
                            {entry.time} {entry.msg}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <Subsection label="Reference" />
          <RefTable headers={['Class', 'Role']} rows={AUTH_GATE_REF_ROWS} />

          <Subsection label="Used By" />
          <UsedBy cssClass="stui-auth-gate-card" usageMap={usageMap} pageSlugMap={pageSlugMap} navigate={navigate} />
      </Section>

      {/* ── Database Picker ───────────────────────── */}
      <div className="stui-detail-section-header">Database Picker</div>
      <div style={{ marginBottom: 32 }}>
          <div className="db-switcher-modal">
            <div className="db-switcher-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flex: 1, minWidth: 0 }}>
                <img src={wmLogo} alt="Wunderlich-Malec" style={{ height: '32px', flexShrink: 0 }} />
                <div style={{ borderLeft: '1px solid color-mix(in srgb, var(--color-bar-text) 30%, transparent)', paddingLeft: '0.875rem' }}>
                  <div style={{ color: 'var(--color-bar-text)', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.1 }}>DAWN</div>
                  <div style={{ color: 'color-mix(in srgb, var(--color-bar-text) 65%, transparent)', fontSize: '0.7rem', letterSpacing: '0.04em', lineHeight: 1.2, marginTop: '2px' }}>
                    Documentation, Assets, Wiring &amp; Networking
                  </div>
                </div>
              </div>
              <button className="impersonate-close" style={{ background: 'none', border: 'none', cursor: 'default', padding: 4 }} tabIndex={-1}>&times;</button>
            </div>
            <div className="db-switcher-list">
              {databases.map((db, i) => {
                const name = db.displayName || db.name;
                return (
                  <button key={db.id} className={`db-switcher-row${i === 0 ? ' current' : ''}`}>
                    <div className="db-switcher-logo">
                      {db.iconUrl
                        ? <img src={db.iconUrl} alt="" />
                        : <div className="db-switcher-logo-placeholder"><Database size={16} /></div>
                      }
                    </div>
                    <div className="db-switcher-info">
                      <span className="db-switcher-name">{name}</span>
                      {db.description && <span className="db-switcher-desc">{db.description}</span>}
                    </div>
                    {i === 0 && <span className="db-switcher-badge">Current</span>}
                  </button>
                );
              })}
            </div>
          </div>
      </div>

    </div>
  );
};

/* ── Page shell ────────────────────────────────────────────── */

const TABS = [
  { id: 'base-reset',    label: 'Base'              },
  { id: 'elements',      label: 'Elements'          },
  { id: 'popups',        label: 'Popups'            },
  { id: 'notifications', label: 'Notifications'     },
  { id: 'authorization', label: 'Authorization'     },
  { id: 'dynamic-module', label: 'Dynamic Module'   },
  { id: 'standard-view', label: 'Standard View'     },
  { id: 'data-entry',    label: 'Data Entry Module' },
  { id: 'reports',       label: 'Reports'           },
  { id: 'app-shell',    label: 'App Shell'         },
  { id: 'usage',         label: 'Usage'             },
  { id: 'migration',     label: 'CSS Migrate'       },
  { id: 'css-test',      label: 'CSS Output Test'   },
];

const STUIPage = () => {
  const navigate = useNavigate();
  // ?tab=<id> opens a specific tab (the retired /style-guide route redirects here).
  const [activeTab, setActiveTab] = useState(() => {
    const want = new URLSearchParams(window.location.search).get('tab');
    return TABS.some(t => t.id === want) ? want : 'elements';
  });
  const [usageMap, setUsageMap] = useState(null);
  const [pageSlugMap, setPageSlugMap] = useState(null);
  const [elementTexts, setElementTexts] = useState(null);
  const [cssData, setCssData] = useState(null);   // { index, lines } - one object, so the
                                                 // context value stays reference-stable
  const [cssView, setCssView] = useState(null);

  // A chip with no resolved line has nothing to open; its label already shows the selector.
  const handleChipClick = useCallback((file, selector, line) => {
    if (line) setCssView({ file, selector, line });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [mapRes, textsRes, cssRes] = await Promise.all([
          fetch(API_BASE_URL + '/api/devfiles/stui-usage-map'),
          fetch(API_BASE_URL + '/api/devfiles/element-texts'),
          fetch(API_BASE_URL + '/api/devfiles/stui-css'),
        ]);
        // usage and pages share one page-name key, so every Used By button finds its route
        const { usage, pages } = await mapRes.json();
        setUsageMap(usage);
        setPageSlugMap(pages);
        setElementTexts(await textsRes.json());
        const cssDoc = await cssRes.json();
        setCssData(buildCssIndex(cssDoc.content || ''));
      } catch (e) {
        console.error('STUI data load failed:', e);
      }
    };
    loadData();
  }, []);

  const tabProps = { usageMap, pageSlugMap, elementTexts, navigate, onChipClick: handleChipClick };

  return (
    <CssIndexContext.Provider value={cssData}>
    <div className="stui-module-container">
      <div className="stui-module-header-styleguideonly">
        <div className="stui-module-header-left">
          <Baby size={20} className="stui-page-icon" />
          <h1>Standard Template User Interface</h1>
          <span className="stui-module-header-meta">STUI.css — Reference: I/O Assignments Report</span>
        </div>
        <div className="stui-module-header-actions">
          <button className="stui-crud-btn" disabled title="Add"><Plus size={16} /></button>
          <button className="stui-crud-btn" disabled title="Edit"><Pencil size={16} /></button>
          <button className="stui-crud-btn" disabled title="Save"><Save size={16} /></button>
          <button className="stui-action-menu-btn disabled" disabled title="Actions"><GripVertical size={16} /></button>
        </div>
      </div>

      <div className="stui-ssg-tab-bar">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`stui-ssg-tab-btn${activeTab === tab.id ? ' active' : ''}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'elements'       && <ElementsTab {...tabProps} />}
      {activeTab === 'popups'         && <PopupsTab {...tabProps} />}
      {activeTab === 'dynamic-module' && <DynamicModuleTab />}
      {activeTab === 'notifications'  && <NotificationsTab />}
      {activeTab === 'standard-view'  && <StandardViewTab {...tabProps} />}
      {activeTab === 'authorization'  && <AuthorizationTab {...tabProps} />}
      {activeTab === 'data-entry'     && <DataEntryTab {...tabProps} />}
      {activeTab === 'reports'        && <ReportsTab {...tabProps} />}
      {activeTab === 'app-shell'      && <AppShellTab {...tabProps} />}
      {activeTab === 'base-reset'     && <BaseResetTab onChipClick={handleChipClick} />}
      {activeTab === 'usage'          && <UsageTab />}
      {activeTab === 'migration'      && <MigrationTab />}
      {activeTab === 'css-test'       && <CSSOutputTestTab />}

      {cssView && cssData && (
        <CssSourceModal {...cssView} lines={cssData.lines} onClose={() => setCssView(null)} />
      )}
    </div>
    </CssIndexContext.Provider>
  );
};

export default STUIPage;
